import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { readBlobError, triggerBlobDownload } from '../utils/exportHelpers';
import { PageHeader, StatCards, Card, TabBar, Table, StatusPill, Field, inputStyle, btnPrimary, btnSm, Empty, Loading, fmtKES , useBreakpoint } from '../components/UI';
import * as api from '../api/client';
import axiosClient from '../api/client';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getBrandingSlug } from '../utils/formatters';
import HowToGuide, { ADMIN_TAB_GUIDES, HowToJumpButton } from '../components/HowToGuide';
import { KpiCard, AreaChart, BarChartV, DonutRing, RankBars, ActivityFeed, DashCard, Gauge } from '../components/DashboardWidgets';
import { printComplimentaryAuthorization, printConferenceInvoice, printTransportReceipt } from '../utils/print';

// Admin imports all the other role pages
import WaiterPage         from './WaiterPage';
import CashierPage        from './CashierPage';
import KitchenPage        from './KitchenPage';
import ReceptionPage      from './ReceptionPage';
import HousekeepingPage   from './HousekeepingPage';
import PayrollPage        from './PayrollPage';
import ReservationsPage   from './ReservationsPage';
import AccountantPage     from './AccountantPage';
import StorekeeperPage    from './StorekeeperPage';
import ChefPage           from './ChefPage';
import GiftShopPage       from './GiftShopPage';
import AuditTrailPage     from './AuditTrailPage';
import MarketingPage      from './MarketingPage';
import SeoPage            from './SeoPage';
import SwimmingPage       from './SwimmingPage';
import GuestFolioPage     from './GuestFolioPage';
import ReportingPage      from './ReportingPage';

const tabs = [
  { id:'overview',   label:'Overview',       icon:'📊' },
  { id:'users',      label:'Staff Accounts', icon:'👥' },
  { id:'rooms',      label:'Rooms',          icon:'🏨' },
  { id:'food',       label:'Food Menu',      icon:'🍽️' },
  { id:'bar',        label:'Bar Menu',       icon:'🍹' },
  { id:'reports',    label:'Reports',        icon:'💰' },
  { id:'gate_log',   label:'Gate Log',       icon:'🔐' },
  { id:'branding',   label:'Branding',       icon:'🎨' },
  { id:'seo',        label:'SEO Intelligence',icon:'🎯' },
  { id:'reviews',    label:'Guest Reviews',  icon:'⭐' },
  { id:'landing',    label:'Landing Page',   icon:'🌐' },
  { id:'complimentary', label:'Complimentaries', icon:'🎁' },
  { id:'donations',  label:'Donations',      icon:'🌿' },
  { id:'currencies', label:'Currencies',     icon:'💱' },
  { id:'leave',      label:'Leave Requests', icon:'📅' },
  { id:'session',    label:'Session Settings',icon:'🔐' },
  { id:'devtools',   label:'Dev Tools',      icon:'🛠️' },
  { id:'---',        label:'',               icon:''   },   // separator
  { id:'reception',  label:'Reception',      icon:'🏨' },
  { id:'cashier',    label:'Cashier',        icon:'💳' },
  { id:'kitchen',    label:'Kitchen',        icon:'👨‍🍳' },
  { id:'waiter',     label:'Waiter View',    icon:'🍽️' },
];

export default function AdminPage({ notify }) {
  const bp = useBreakpoint();
  const [tab, setTab] = useState('overview');

  const NAV_GROUPS = [
    {
      label: 'Command Centre',
      color: '#c45e3e',
      items: [
        { id:'overview',      label:'Overview',           icon:'📊' },
        { id:'reporting',     label:'Reports & Analytics',icon:'📈' },
        { id:'reports',       label:'Financial Reports',  icon:'💰' },
        { id:'accounting',    label:'Accounting',         icon:'📒' },
        { id:'leave',         label:'Leave Requests',     icon:'📅' },
        { id:'attendance',    label:'Attendance',         icon:'🕐' },
        { id:'payroll',       label:'Payroll',            icon:'💼' },
        { id:'audit_trail',   label:'Audit Trail',        icon:'🔍' },
        { id:'marketing',     label:'Marketing',          icon:'📣' },
      ]
    },
    {
      label: 'Hotel Operations',
      color: '#3b82f6',
      items: [
        { id:'bulk_import',   label:'Bulk Import',        icon:'📥' },
        { id:'rooms',         label:'Rooms',              icon:'🏨' },
        { id:'reception',     label:'Reception',          icon:'🛎️' },
        { id:'reservations',      label:'Reservations',       icon:'📅' },
        { id:'conference_bookings',label:'Conference Bookings',icon:'🏛️' },
        { id:'transport',         label:'Transport',          icon:'🚐' },
        { id:'folios',            label:'Guest Folios',       icon:'📁' },
        { id:'gate_log',          label:'Gate & Security',    icon:'🔐' },
        { id:'housekeeping',      label:'Housekeeping',       icon:'🧹' },
        { id:'pool_kids',         label:'Pool & Kids',        icon:'🏊' },
      ]
    },
    {
      label: 'Food & Beverage',
      color: '#e67e22',
      items: [
        { id:'cashier',       label:'Cashier',            icon:'💳' },
        { id:'waiter',        label:'Waiter View',        icon:'🍽️' },
        { id:'kitchen',       label:'Kitchen',            icon:'👨‍🍳' },
        { id:'kitchen_prep',  label:'Kitchen Prep Report',icon:'⏱️' },
        { id:'food',          label:'Food Menu',          icon:'🍽️' },
        { id:'bar',           label:'Bar Menu',           icon:'🍹' },
        { id:'food_costing',  label:'Food Costing',       icon:'📊' },
        { id:'gift_shop',     label:'Gift Shop',          icon:'🛍️' },
      ]
    },
    {
      label: 'Finance & Stock',
      color: '#8b5cf6',
      items: [
        { id:'storeroom',     label:'Storeroom',          icon:'🏪' },
        { id:'currencies',    label:'Currencies',         icon:'💱' },
        { id:'complimentary', label:'Complimentaries',    icon:'🎁' },
        { id:'donations',     label:'Donations',          icon:'🌿' },
      ]
    },
    {
      label: 'System & Config',
      color: '#2d6a4f',
      items: [
        { id:'users',            label:'Staff Accounts',     icon:'👥' },
        { id:'employee_profiles',label:'Employee Profiles',  icon:'🪪' },
        { id:'guest_history',    label:'Guest History',      icon:'📁' },
        { id:'seo',              label:'SEO Intelligence',   icon:'🎯' },
        { id:'branding',         label:'Branding',           icon:'🎨' },
        { id:'landing',          label:'Landing Page',       icon:'🌐' },
        { id:'reviews',          label:'Guest Reviews',      icon:'⭐' },
        { id:'backup',           label:'Data Backup',        icon:'💾' },
        { id:'session',          label:'Session Settings',   icon:'🔐' },
        { id:'devtools',         label:'Dev Tools',          icon:'🛠️' },
      ]
    },
  ];

  const allNavItems = NAV_GROUPS.flatMap(g => g.items);
  const currentGroup = NAV_GROUPS.find(g => g.items.some(i => i.id === tab));
  const currentItem = allNavItems.find(i => i.id === tab);

  const adminBannerStyle = (color) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '7px 12px', marginBottom: 16,
    background: 'var(--bgHover)', border: '1px solid var(--border)',
    borderLeft: `3px solid ${color}`,
    borderRadius: 5, fontSize: 12, color: 'var(--textMuted)',
  });

  return (
    <div>
      <PageHeader title="Admin Panel" sub="Full system access — all departments" />

      {/* ── Grouped nav tabs — replaces internal sidebar ── */}
      <div style={{ marginBottom: 20 }}>
        {NAV_GROUPS.map((group, gi) => (
          <div key={group.label} style={{ marginBottom: 4 }}>
            {/* Group label */}
            <div style={{
              fontSize: 10, fontWeight: 600, letterSpacing: '.07em',
              textTransform: 'uppercase', color: 'var(--textFaint)',
              padding: '8px 2px 5px', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ width: 3, height: 10, borderRadius: 2, background: group.color, display: 'inline-block', flexShrink: 0 }}/>
              {group.label}
            </div>
            {/* Tab row */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {group.items.map(item => {
                const isActive = tab === item.id;
                return (
                  <button key={item.id} onClick={() => setTab(item.id)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '5px 11px', borderRadius: 5,
                    border: `1px solid ${isActive ? group.color + '60' : 'var(--border)'}`,
                    background: isActive ? group.color + '12' : 'var(--bgCard)',
                    color: isActive ? group.color : 'var(--textMuted)',
                    fontSize: 12.5, fontWeight: isActive ? 600 : 400,
                    cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'all .12s', whiteSpace: 'nowrap',
                  }}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'var(--bgHover)'; e.currentTarget.style.color = 'var(--textSecond)'; e.currentTarget.style.borderColor = 'var(--borderMid)'; }}}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'var(--bgCard)'; e.currentTarget.style.color = 'var(--textMuted)'; e.currentTarget.style.borderColor = 'var(--border)'; }}}
                  >
                    <span style={{ fontSize: 13 }}>{item.icon}</span>
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ── Content area — full width ── */}
      <div style={{ minWidth: 0 }}>
        {/* Breadcrumb chip */}
        {currentItem && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 16, fontSize: 11.5, color: 'var(--textMuted)' }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: currentGroup?.color || 'var(--borderMid)', display: 'inline-block', flexShrink: 0 }}/>
            <span style={{ color: currentGroup?.color || 'var(--textMuted)', fontWeight: 600 }}>{currentGroup?.label}</span>
            <span style={{ color: 'var(--borderMid)' }}>/</span>
            <span style={{ color: 'var(--textPrimary)', fontWeight: 600 }}>{currentItem.icon} {currentItem.label}</span>
            <HowToJumpButton style={{ marginLeft: 'auto' }} />
          </div>
        )}

          {tab==='overview'   && <AdminOverview notify={notify} onNavigate={setTab}/>}
          {tab==='reporting'  && <ReportingPage  notify={notify}/>}
          {tab==='users'      && <AdminUsers    notify={notify}/>}
          {tab==='employee_profiles' && <EmployeeProfiles notify={notify}/>}
          {tab==='guest_history'     && <GuestHistory     notify={notify}/>}
          {tab==='bulk_import' && <BulkImport notify={notify}/>}
          {tab==='rooms'      && <AdminRooms    notify={notify}/>}
          {tab==='food'       && <AdminMenu     notify={notify} type="food" title="Food Menu" color="#e67e22"/>}
          {tab==='bar'        && <AdminMenu     notify={notify} type="bar"  title="Bar Menu"  color="#8B2635"/>}
          {tab==='reports'    && <AdminReports/>}
          {tab==='gate_log'   && <AdminGateLog  notify={notify}/>}
          {tab==='branding'   && <BrandingSettings notify={notify}/>}
          {tab==='backup'     && <DataBackup       notify={notify}/>}
          {tab==='reviews'    && <GuestReviews />}
          {tab==='landing'       && <LandingEditor          notify={notify}/>}
          {tab==='seo'           && <SeoPage               notify={notify}/>}
          {tab==='complimentary' && <AdminComplimentaries   notify={notify}/>}
          {tab==='donations'     && <AdminDonations         notify={notify}/>}
          {tab==='currencies'    && <AdminCurrencies        notify={notify}/>}
          {tab==='leave'         && <AdminLeaveRequests     notify={notify}/>}
          {tab==='session'       && <AdminSessionSettings   notify={notify}/>}
          {tab==='devtools'      && process.env.NODE_ENV !== 'production' && <AdminDevTools notify={notify}/>}
          {tab==='accounting'    && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Accounting module</div><AccountantPage notify={notify}/></div>}
          {tab==='payroll'       && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Payroll module</div><PayrollPage notify={notify}/></div>}
          {tab==='audit_trail'   && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Audit Trail</div><AuditTrailPage notify={notify}/></div>}
          {tab==='marketing'     && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Marketing module</div><MarketingPage notify={notify}/></div>}
          {tab==='reservations'        && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Reservations</div><ReservationsPage notify={notify}/></div>}
          {tab==='conference_bookings'  && <AdminConferenceBookings notify={notify}/>}
          {tab==='transport'            && <AdminTransport notify={notify}/>}
          {tab==='attendance'           && <AdminAttendance notify={notify}/>}
          {tab==='folios'        && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Guest Folios</div><GuestFolioPage notify={notify}/></div>}
          {tab==='housekeeping'  && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Housekeeping</div><HousekeepingPage notify={notify}/></div>}
          {tab==='pool_kids'     && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Pool & Kids</div><SwimmingPage notify={notify}/></div>}
          {tab==='food_costing'  && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Food Costing</div><ChefPage notify={notify}/></div>}
          {tab==='gift_shop'     && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Gift Shop</div><GiftShopPage notify={notify} userRole="admin"/></div>}
          {tab==='storeroom'     && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Storeroom</div><StorekeeperPage notify={notify}/></div>}
          {tab==='reception'   && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Reception module</div><ReceptionPage notify={notify}/></div>}
          {tab==='cashier'     && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Cashier module</div><CashierPage notify={notify}/></div>}
          {tab==='kitchen'     && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Kitchen module</div><KitchenPage notify={notify}/></div>}
          {tab==='kitchen_prep' && <KitchenPrepReport notify={notify}/>}
          {tab==='waiter'      && <div><div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', marginBottom:16, background:'var(--bgHover)', border:'1px solid var(--border)', borderLeft:'3px solid var(--primary)', borderRadius:5, fontSize:12, color:'var(--textMuted)' }}>👁️ Admin viewing Waiter module</div><WaiterPage notify={notify}/></div>}

          {/* How-to for the admin section currently open */}
          <HowToGuide key={'admin_' + tab} guideId={ADMIN_TAB_GUIDES[tab]} basics={tab === 'overview'} />
      </div>
    </div>
  );
}



// ── Mini Donut Chart (pure SVG) ───────────────────────────────────────────────
function DonutChart({ value, max, color, size = 64, stroke = 8 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const dash = pct * circ;
  const cx = size / 2, cy = size / 2;
  return (
    <svg width={size} height={size} style={{ transform:'rotate(-90deg)' }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeLinecap="round"
        style={{ transition:'stroke-dasharray 0.6s ease' }}
      />
    </svg>
  );
}


// ── Main Dashboard Overview ───────────────────────────────────────────────────
function AdminOverview({ notify, onNavigate }) {
  const [reports, setReports] = useState(null);
  const [payments, setPayments] = useState([]);
  const [bills, setBills]     = useState([]);
  const [guests, setGuests]   = useState([]);
  const [orders, setOrders]   = useState([]);
  const [loadErr, setLoadErr] = useState(null);

  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const [r, p, b, g, o] = await Promise.all([
        api.getReports(), api.getPayments(), api.getBills(), api.getGuests(), api.getOrders(),
      ]);
      setReports(r.data);
      setPayments(p.data.results || p.data || []);
      setBills(b.data.results || b.data || []);
      setGuests(g.data.results || g.data || []);
      setOrders(o.data.results || o.data || []);
    } catch(e) {
      const msg = e.response?.data?.detail || e.response?.data?.error || e.message || 'Network error';
      setLoadErr(msg);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loadErr) return (
    <div style={{ textAlign:'center', padding:'60px 20px' }}>
      <div style={{ fontSize:48, marginBottom:12 }}>⚠️</div>
      <div style={{ fontSize:16, fontWeight:700, color:'var(--textPrimary)', marginBottom:6 }}>Could not load dashboard</div>
      <div style={{ fontSize:13, color:'var(--textMuted)', marginBottom:4, fontFamily:'monospace', background:'var(--bgInput)', padding:'8px 16px', borderRadius:6, display:'inline-block', maxWidth:500 }}>{loadErr}</div>
      <div style={{ marginTop:20 }}>
        <button onClick={load} style={{ background:'var(--primary)', border:'none', borderRadius:8, padding:'10px 28px', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 }}>↻ Retry</button>
      </div>
    </div>
  );
  if (!reports) return <Loading/>;

  // ── Derived stats ──────────────────────────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const todayPay   = payments.filter(p => (p.created_at||'').slice(0,10) === today);
  const todayRev   = todayPay.reduce((s,p) => s + parseFloat(p.amount||0), 0);
  const monthPay   = payments.filter(p => (p.created_at||'').slice(0,7) === today.slice(0,7));
  const monthRev   = monthPay.reduce((s,p) => s + parseFloat(p.amount||0), 0);
  const pendingBills = (reports.pending_bills||0) + (reports.payment_requested||0);
  const checkedIn  = guests.filter(g => g.status === 'checked-in').length;
  const totalOrders = reports.total_orders || orders.length;
  const waiterStats = reports.waiter_stats || [];
  const occupancyPct = guests.length > 0 ? Math.round(checkedIn / guests.length * 100) : 0;

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // Monthly bar chart
  const monthlyRevMap = {};
  payments.forEach(p => {
    const m = (p.created_at||'').slice(0,7);
    if (m) monthlyRevMap[m] = (monthlyRevMap[m]||0) + parseFloat(p.amount||0);
  });
  const now2 = new Date();
  const barData = Array.from({length:12}, (_,i) => {
    const d = new Date(now2.getFullYear(), now2.getMonth()-11+i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    return { label: monthNames[d.getMonth()], value: monthlyRevMap[key]||0 };
  });

  // Daily trend (14 days)
  const dailyRevMap = {};
  payments.forEach(p => {
    const d = (p.created_at||'').slice(0,10);
    if (d) dailyRevMap[d] = (dailyRevMap[d]||0) + parseFloat(p.amount||0);
  });
  const areaData = Array.from({length:30}, (_,i) => {
    const d = new Date(); d.setDate(d.getDate()-29+i);
    const key = d.toISOString().slice(0,10);
    return { label: key.slice(5), value: dailyRevMap[key]||0 };
  });

  // Payment modes
  const modeTotals = {};
  payments.forEach(p => { modeTotals[p.payment_mode] = (modeTotals[p.payment_mode]||0) + parseFloat(p.amount||0); });
  const modeColors = { cash:'#22c55e', mpesa:'#00b300', card:'#8b5cf6', pdq:'#8b5cf6', bank:'#3b82f6', stripe:'#635bff', 'room-charge':'#f59e0b', complimentary:'#c8a96e' };
  const modeSegments = Object.entries(modeTotals).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([mode,amt])=>({ label: mode.replace('-',' '), value: amt, color: modeColors[mode]||'#888' }));
  const totalRevAll = Object.values(modeTotals).reduce((s,v)=>s+v,0);

  // Waiter performance
  const waiterItems = waiterStats.slice(0,6).map(w=>({ label: w.name||w.waiter_name||'—', value: parseFloat(w.revenue||w.total||0), color:'#c45e3e' }));

  // Recent activity feed
  const recentFeed = [...payments].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).slice(0,10).map(p=>({
    icon: p.payment_mode==='mpesa'?'📱':p.payment_mode==='cash'?'💵':p.payment_mode==='card'||p.payment_mode==='pdq'?'💳':'💰',
    text: `${p.payment_type||'Payment'} — ${p.guest_name||p.waiter_name||'Guest'}`,
    sub: fmtKES(p.amount),
    color: modeColors[p.payment_mode]||'#c8a96e',
    time: new Date(p.created_at).toLocaleTimeString('en-KE',{hour:'2-digit',minute:'2-digit'}),
  }));

  // Spark data for KPI cards
  const last7 = Array.from({length:7},(_,i)=>{ const d=new Date();d.setDate(d.getDate()-6+i);return dailyRevMap[d.toISOString().slice(0,10)]||0; });

  return (
    <div style={{ display:'grid', gap:14 }}>

      {/* ── Top bar ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, color:'var(--textPrimary)', letterSpacing:-0.5, fontFamily:'var(--font-display)' }}>
            {new Date().toLocaleDateString('en-GB',{weekday:'long'})}
            <span style={{ color:'var(--primary)', marginLeft:6 }}>Overview</span>
          </div>
          <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:2, letterSpacing:0.5 }}>
            {new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'})}
          </div>
        </div>
        <button onClick={load} style={{ background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 16px', color:'var(--textMuted)', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontFamily:'inherit' }}>
          ↻ Refresh
        </button>
      </div>

      {/* ── KPI Strip — Reference design style ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:6 }}>
        {[
          { label:"Today's Revenue",  value:`KES ${Number(todayRev||0).toLocaleString('en-KE',{maximumFractionDigits:0})}`, icon:'💰', sub:`${todayPay.length} transactions`, bg:'linear-gradient(135deg,#1e3a5f 0%,#254d80 100%)', trend:null, spark:last7 },
          { label:'Month Revenue',    value:`KES ${Number(monthRev||0).toLocaleString('en-KE',{maximumFractionDigits:0})}`, icon:'📈', sub:`${monthPay.length} payments`, bg:'linear-gradient(135deg,#0f766e 0%,#0d9488 100%)', trend:null },
          { label:'Occupancy',        value:`${occupancyPct}%`,  icon:'🏨', sub:`${checkedIn} guests in-house`, bg:'linear-gradient(135deg,#4338ca 0%,#6366f1 100%)', trend:null },
          { label:'Pending Bills',    value:pendingBills,        icon:'⏳', sub:`${totalOrders} total orders`,  bg:'linear-gradient(135deg,#92400e 0%,#b45309 100%)', trend:null },
          { label:'Revenue (YTD)',    value:`KES ${Number(reports.total_revenue||0).toLocaleString('en-KE',{maximumFractionDigits:0})}`, icon:'💳', sub:'Fiscal year', bg:'linear-gradient(135deg,#6d28d9 0%,#7c3aed 100%)', trend:null },
          { label:'Complimentaries',  value:reports.complimentary_count||0, icon:'🎁', sub:'Void & comp', bg:'linear-gradient(135deg,#0369a1 0%,#0284c7 100%)', trend:null },
        ].map((s,i) => (
          <div key={i} style={{ background:s.bg, borderRadius:14, padding:'20px 22px 16px', position:'relative', overflow:'hidden', color:'#fff', boxShadow:'0 3px 14px rgba(0,0,0,0.14)', cursor:'default', transition:'transform .18s, box-shadow .18s' }}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='0 10px 32px rgba(0,0,0,0.22)';}}
            onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='0 3px 14px rgba(0,0,0,0.14)';}}>
            <div style={{position:'absolute',top:-28,right:-28,width:100,height:100,borderRadius:'50%',background:'rgba(255,255,255,0.07)',pointerEvents:'none'}}/>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
              <div style={{width:38,height:38,borderRadius:10,background:'rgba(255,255,255,0.18)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:17}}>{s.icon}</div>
              <div style={{fontSize:10,color:'rgba(255,255,255,0.75)',fontWeight:600,textTransform:'uppercase',letterSpacing:1}}>{s.label}</div>
            </div>
            <div style={{fontSize:s.value?.toString().length>8?20:26,fontWeight:800,letterSpacing:-0.5,lineHeight:1,marginBottom:4,wordBreak:'break-all'}}>{s.value}</div>
            {s.spark && s.spark.length > 1 && (() => {
              const max = Math.max(...s.spark, 1);
              const pts = s.spark.map((v,j)=>[j*8+4, 18-(v/max)*16]);
              const line = pts.map(([x,y])=>`${x},${y}`).join(' ');
              const area = `M${pts[0][0]},18 L${pts.map(([x,y])=>`${x},${y}`).join(' L ')} L${pts[pts.length-1][0]},18 Z`;
              return (
                <svg viewBox={`0 ${s.spark.length * 8} 20`} style={{width:'100%',height:18,display:'block',marginBottom:4}} preserveAspectRatio="none">
                  <path d={area} fill="rgba(255,255,255,0.2)"/>
                  <polyline points={line} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
              );
            })()}
            <div style={{fontSize:10,color:'rgba(255,255,255,0.6)',marginTop:4}}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Row 2: 30-day area chart + occupancy gauge + payment modes ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 160px 220px', gap:12, alignItems:'stretch' }}>
        <DashCard>
          <div style={{ marginBottom:12 }}><div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.8, marginBottom:10 }}>Revenue Trend — Last 30 Days</div>
            <AreaChart data={areaData} color="var(--primary)" height={160} formatY={v => 'K'+Math.round(v/1000)} />
          </div>
        </DashCard>
        <DashCard style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:4 }}>Occupancy</div>
          <Gauge value={occupancyPct} max={100} color="#3b82f6" size={100} />
          <div style={{ width:'100%', fontSize:11 }}>
            {[['In-House','#22c55e',checkedIn],['Available','var(--textFaint)',Math.max(0,guests.length-checkedIn)]].map(([l,c,v])=>(
              <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderTop:'1px solid var(--border)' }}>
                <span style={{ color:'var(--textMuted)' }}>{l}</span>
                <span style={{ fontWeight:700, color:c }}>{v}</span>
              </div>
            ))}
          </div>
        </DashCard>
        <DashCard>
          <div style={{ marginBottom:12 }}><div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.8, marginBottom:10 }}>Payment Modes</div>
            {modeSegments.length > 0
              ? <DonutRing segments={modeSegments} size={110} stroke={12} label={`${modeSegments.length}`} sublabel="modes" />
              : <div style={{ color:'var(--textFaint)', fontSize:12, textAlign:'center', padding:'20px 0' }}>No data</div>}
          </div>
        </DashCard>
      </div>

      {/* ── Row 3: Monthly bars + Waiter performance + Bills status ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 220px 180px', gap:12, alignItems:'stretch' }}>
        <DashCard>
          <div style={{ marginBottom:12 }}><div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.8, marginBottom:10 }}>Monthly Revenue — 12 Months</div>
            <BarChartV data={barData} color="var(--primary)" height={140} formatY={v=>'K'+Math.round(v/1000)} />
          </div>
        </DashCard>
        <DashCard>
          <div style={{ marginBottom:12 }}><div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.8, marginBottom:10 }}>Waiter Revenue Leaders</div>
            {waiterItems.length > 0
              ? <RankBars items={waiterItems} color="#c45e3e" formatVal={v=>'K'+Math.round(v/1000)} />
              : <div style={{ color:'var(--textFaint)', fontSize:12, padding:'14px 0', textAlign:'center' }}>No waiter data</div>}
          </div>
        </DashCard>
        <DashCard>
          <div style={{ marginBottom:12 }}><div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.8, marginBottom:10 }}>Bills Status</div>
            <div style={{ display:'flex', gap:0, borderRadius:8, overflow:'hidden', border:'1px solid var(--border)', marginBottom:12 }}>
              {[{label:'Pending',value:reports.pending_bills||0,color:'#f59e0b'},{label:'Paid Today',value:todayPay.length,color:'#22c55e'}].map((item,i)=>(
                <div key={i} style={{ flex:1, padding:'11px 8px', textAlign:'center', borderRight:i===0?'1px solid var(--border)':'none', background:'var(--bgCard)' }}>
                  <div style={{ fontSize:18, fontWeight:800, color:item.color, letterSpacing:-0.5 }}>{item.value}</div>
                  <div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginTop:2 }}>{item.label}</div>
                </div>
              ))}
            </div>
            {[
              { label:'Payment Requested', value:reports.payment_requested||0, color:'#3b82f6' },
              { label:'Total Orders',      value:totalOrders,                  color:'#c45e3e' },
              { label:'Total Collected',   value:fmtKES(totalRevAll),          color:'#8b5cf6' },
            ].map((item,i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                <span style={{ fontSize:11, color:'var(--textMuted)' }}>{item.label}</span>
                <span style={{ fontSize:13, fontWeight:800, color:item.color }}>{item.value}</span>
              </div>
            ))}
          </div>
        </DashCard>
      </div>

      {/* ── Row 4: Recent activity feed + top payment mode breakdown ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 260px', gap:12 }}>
        <DashCard>
          <div style={{ marginBottom:12 }}><div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.8, marginBottom:10 }}>Recent Transactions</div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr>{['#','Type','Guest / Waiter','Mode','Amount','Time'].map(h=>(
                    <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:9, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1, borderBottom:'2px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {[...payments].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).slice(0,12).map((p,i)=>(
                    <tr key={p.id||i} style={{ borderBottom:'1px solid var(--border)' }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bgHover)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{ padding:'8px 10px', color:'var(--textFaint)', fontFamily:'monospace', fontSize:11 }}>#{p.id}</td>
                      <td style={{ padding:'8px 10px', color:'var(--textSecond)' }}>{p.payment_type||'—'}</td>
                      <td style={{ padding:'8px 10px', color:'var(--textPrimary)', fontWeight:600 }}>{p.guest_name||p.waiter_name||'—'}</td>
                      <td style={{ padding:'8px 10px' }}>
                        <span style={{ fontSize:10, fontWeight:700, background:(modeColors[p.payment_mode]||'#888')+'18', color:modeColors[p.payment_mode]||'#888', padding:'2px 7px', borderRadius:20, textTransform:'capitalize' }}>
                          {p.payment_mode||'—'}
                        </span>
                      </td>
                      <td style={{ padding:'8px 10px', fontWeight:700, color:'#22c55e' }}>{fmtKES(p.amount)}</td>
                      <td style={{ padding:'8px 10px', color:'var(--textFaint)', fontSize:10 }}>{new Date(p.created_at).toLocaleString('en-KE',{dateStyle:'short',timeStyle:'short'})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DashCard>
        <DashCard>
          <div style={{ marginBottom:12 }}><div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.8, marginBottom:10 }}>Activity Feed</div>
            <ActivityFeed items={recentFeed} maxItems={12} />
          </div>
        </DashCard>
      </div>

      {/* ── Module Launcher ── */}
      <ModuleLauncher onNavigate={onNavigate} />

    </div>
  );
}


function ModuleLauncher({ onNavigate }) {
  const groups = [
    {
      label: 'Hotel Operations',
      color: '#3b82f6',
      modules: [
        { id:'reception',    icon:'🛎️', label:'Reception',      desc:'Check-in, rooms & conference' },
        { id:'reservations', icon:'📅', label:'Reservations',   desc:'Advance bookings' },
        { id:'folios',       icon:'📁', label:'Guest Folios',   desc:'Charges & billing history' },
        { id:'housekeeping', icon:'🧹', label:'Housekeeping',   desc:'Room status & tasks' },
        { id:'gate_log',     icon:'🔐', label:'Gate & Security',desc:'Visitor log & access' },
        { id:'pool_kids',    icon:'🏊', label:'Pool & Kids',    desc:'Amenities & bookings' },
      ]
    },
    {
      label: 'Food & Beverage',
      color: '#e67e22',
      modules: [
        { id:'waiter',       icon:'🍽️', label:'Waiter',         desc:'Orders & table service' },
        { id:'kitchen',      icon:'👨‍🍳', label:'Kitchen',        desc:'Live order board' },
        { id:'cashier',      icon:'💳', label:'Cashier',        desc:'Payments & billing' },
        { id:'food_costing', icon:'📊', label:'Food Costing',   desc:'Recipe cost & margins' },
        { id:'gift_shop',    icon:'🛍️', label:'Gift Shop',      desc:'Retail POS & stock' },
        { id:'storeroom',    icon:'🏪', label:'Storeroom',      desc:'Stock & procurement' },
      ]
    },
    {
      label: 'Finance & Management',
      color: '#8b5cf6',
      modules: [
        { id:'accounting',   icon:'📒', label:'Accounting',     desc:'Financial reports' },
        { id:'reporting',    icon:'📈', label:'Reports Hub',     desc:'Cross-sector analytics & insights' },
        { id:'payroll',      icon:'💼', label:'Payroll',        desc:'Staff pay & deductions' },
        { id:'marketing',    icon:'📣', label:'Marketing',      desc:'Campaigns & reviews' },
        { id:'seo',          icon:'🎯', label:'SEO Intelligence',desc:'Keywords & search rankings' },
        { id:'audit_trail',  icon:'🔍', label:'Audit Trail',    desc:'System activity log' },
        { id:'leave',        icon:'📅', label:'Leave Requests', desc:'HR leave management' },
        { id:'reports',      icon:'💰', label:'Reports',        desc:'Revenue & analytics' },
        { id:'employee_profiles', icon:'🪪', label:'Employee Profiles', desc:'Staff dossiers & attendance' },
        { id:'guest_history',     icon:'📁', label:'Guest History',     desc:'All guest records & history' },
      ]
    },
  ];

  return (
    <div style={{ marginTop:6, display:'grid', gap:20 }}>
      <div style={{ fontSize:10, fontWeight:800, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:2, paddingBottom:8, borderBottom:'1px solid var(--border)' }}>
        Quick Access — All Modules
      </div>
      {groups.map(group => (
        <div key={group.label}>
          {/* Group heading */}
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
            <div style={{ width:3, height:14, borderRadius:2, background:group.color }}/>
            <div style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:1.8, color:'var(--textMuted)' }}>{group.label}</div>
          </div>
          {/* Module cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(168px, 1fr))', gap:8 }}>
            {group.modules.map(m => (
              <button key={m.id} onClick={() => onNavigate(m.id)} style={{
                background:'var(--bgCard)', border:'1px solid var(--border)',
                borderRadius:10, padding:'14px 16px', cursor:'pointer',
                textAlign:'left', fontFamily:'inherit',
                transition:'all .15s', position:'relative', overflow:'hidden',
                display:'flex', flexDirection:'column', gap:5,
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor=group.color+'88'; e.currentTarget.style.boxShadow=`0 4px 20px ${group.color}20`; e.currentTarget.style.transform='translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='translateY(0)'; }}
              >
                {/* Left accent bar */}
                <div style={{ position:'absolute', left:0, top:0, bottom:0, width:3, background:group.color, borderRadius:'10px 0 0 10px' }}/>
                {/* Ghost icon */}
                <div style={{ position:'absolute', top:-8, right:-4, fontSize:38, opacity:0.06, pointerEvents:'none' }}>{m.icon}</div>
                <div style={{ paddingLeft:8 }}>
                  <div style={{ fontSize:18, marginBottom:5 }}>{m.icon}</div>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--textPrimary)', lineHeight:1.2 }}>{m.label}</div>
                  <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:3, lineHeight:1.4 }}>{m.desc}</div>
                </div>
                <div style={{ position:'absolute', bottom:8, right:10, fontSize:10, color:group.color, fontWeight:800, opacity:0.7 }}>Open →</div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminUsers({ notify }) {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:'', username:'', password:'', role:'', kitchen_station:'' });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showTerminated, setShowTerminated] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const roleColors = { admin:'#c8a96e', waiter:'#3498db', cashier:'#2ecc71', kitchen:'#e67e22', chef:'#e74c3c', reception:'#9b59b6', gate:'#e74c3c', housekeeping:'#1abc9c', accountant:'#f39c12', gift_shop:'#e91e8c', restaurant_manager:'#00b300', storekeeper:'#1a9ba0', hr:'#8e44ad', marketing:'#e84393', swimming_attendant:'#0ea5e9', kids_amenities:'#f472b6' };

  const load = useCallback(async () => { const r = await api.getUsers(); setUsers(r.data.results||r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const sf = (k, v) => { setForm(p => ({...p, [k]: v})); setFormErrors(p => ({...p, [k]: ''})); };

  const add = async () => {
    const errs = {};
    if (!form.name.trim())     errs.name     = 'Full name is required';
    if (!form.username.trim()) errs.username  = 'Username is required';
    else if (/\s/.test(form.username)) errs.username = 'No spaces allowed in username';
    if (!form.password)        errs.password  = 'Password is required';
    else if (form.password.length < 8) errs.password = 'Password must be at least 4 characters';
    if (!form.role)            errs.role      = 'A role must be selected';
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    setFormErrors({});
    setSaving(true);
    try {
      await api.createUser(form);
      await load();
      setForm({name:'',username:'',password:'',role:'',kitchen_station:''});
      setShowForm(false);
      notify('Staff account created!');
    } catch(e) {
      const data = e.response?.data;
      if (data && typeof data === 'object') {
        const mapped = {};
        Object.entries(data).forEach(([k,v]) => { mapped[k] = Array.isArray(v) ? v[0] : String(v); });
        setFormErrors(mapped);
        notify('Please fix the highlighted fields', 'error');
      } else {
        notify(data || 'Error creating account', 'error');
      }
    } finally { setSaving(false); }
  };
  const toggle = async (u) => { await api.updateUser(u.id,{is_active:!u.is_active}); await load(); notify(`${u.name} ${u.is_active?'deactivated':'activated'}`); };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    try {
      await api.softDeleteUser(deleteModal.id, { reason: deleteReason || 'Removed by admin' });
      await load();
      setDeleteModal(null);
      setDeleteReason('');
      notify(`${deleteModal.name} account removed. History preserved.`);
    } catch(e) {
      notify(e.response?.data?.error || 'Failed to remove account', 'error');
    }
  };

  const activeUsers     = users.filter(u => !u.is_terminated);
  const terminatedUsers = users.filter(u => u.is_terminated);
  const displayed       = showTerminated ? terminatedUsers : activeUsers;

  return (
    <Card title="Staff Accounts">
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <button style={{ ...btnPrimary, width:'auto', padding:'8px 18px' }} onClick={()=>setShowForm(!showForm)}>
          {showForm?'✕ Cancel':'+ Add Staff Account'}
        </button>
        <button onClick={()=>setShowTerminated(!showTerminated)} style={{
          padding:'7px 16px', borderRadius:5, fontSize:12, fontWeight:600, cursor:'pointer',
          border:`1px solid ${showTerminated?'#e74c3c66':'var(--border)'}`,
          background:showTerminated?'rgba(231,76,60,0.08)':'var(--bgInput)',
          color:showTerminated?'#e74c3c':'var(--textMuted)', fontFamily:'inherit',
        }}>
          {showTerminated ? '← Active Staff' : `🗂 Terminated (${terminatedUsers.length})`}
        </button>
      </div>

      {showForm && (
        <div style={{ padding:16, background:'var(--bgInput)', border:'1px solid var(--borderMid)', borderRadius:8, marginBottom:16 }}>
          <div style={{ fontWeight:700, color:'#c8a96e', marginBottom:14, fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
            <span>👤</span> New Staff Account
          </div>
          {/* General API error */}
          {(formErrors._general || formErrors.non_field_errors) && (
            <div style={{ marginBottom:12, padding:'9px 13px', borderRadius:7, background:'rgba(239,68,68,0.07)', border:'1.5px solid rgba(239,68,68,0.25)', fontSize:12, color:'#ef4444', display:'flex', gap:8 }}>
              <span>⚠️</span><span>{formErrors._general || formErrors.non_field_errors}</span>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10 }}>
            <Field label="Full Name" required error={formErrors.name}>
              <input style={{ ...inputStyle, ...(formErrors.name ? {borderColor:'#ef4444',background:'rgba(239,68,68,0.04)'} : {}) }}
                value={form.name} onChange={e=>sf('name',e.target.value)} placeholder="John Doe" autoFocus />
            </Field>
            <Field label="Username" required error={formErrors.username}>
              <input style={{ ...inputStyle, ...(formErrors.username ? {borderColor:'#ef4444',background:'rgba(239,68,68,0.04)'} : {}) }}
                value={form.username} onChange={e=>sf('username',e.target.value.toLowerCase())} placeholder="john" />
            </Field>
            <Field label="Password" required error={formErrors.password}>
              <input style={{ ...inputStyle, ...(formErrors.password ? {borderColor:'#ef4444',background:'rgba(239,68,68,0.04)'} : {}) }}
                value={form.password} onChange={e=>sf('password',e.target.value)} placeholder="Password" type="password" />
            </Field>
            <Field label="Role">
              <select style={{...inputStyle, color: form.role ? 'inherit' : 'var(--textMuted)', ...(formErrors.role ? {borderColor:'#ef4444',background:'rgba(239,68,68,0.04)'} : {})}} value={form.role} onChange={e=>sf('role',e.target.value)}>
                <option value="" disabled>— Select a role —</option>
                {['admin','waiter','cashier','kitchen','chef','reception','gate','housekeeping','accountant','gift_shop','restaurant_manager','storekeeper','hr','marketing','swimming_attendant','kids_amenities'].map(r=><option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1).replace(/_/g,' ')}</option>)}
              </select>
              {formErrors.role && <span style={{fontSize:11,color:'#ef4444',marginTop:2}}>{formErrors.role}</span>}
            </Field>
            {['kitchen','chef'].includes(form.role) && (
              <Field label="Kitchen Station">
                <input style={inputStyle} value={form.kitchen_station} onChange={e=>sf('kitchen_station',e.target.value)} placeholder="e.g. Main Kitchen" />
              </Field>
            )}
            <div style={{ alignSelf:'flex-end', display:'flex', gap:8, alignItems:'center' }}>
              <button style={{ ...btnPrimary, marginTop:0, opacity:saving?0.7:1 }} onClick={add} disabled={saving}>
                {saving ? '⟳ Creating…' : '✓ Create Account'}
              </button>
              {Object.values(formErrors).filter(Boolean).length > 0 && (
                <span style={{ fontSize:11, color:'#ef4444', fontWeight:600 }}>
                  ⚠ {Object.values(formErrors).filter(v=>v&&v!==formErrors._general).length} error{Object.values(formErrors).filter(v=>v&&v!==formErrors._general).length>1?'s':''}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {showTerminated ? (
        <div>
          <div style={{ padding:'10px 14px', background:'rgba(231,76,60,0.06)', border:'1px solid rgba(231,76,60,0.2)', borderRadius:6, marginBottom:14, fontSize:12, color:'#e74c3c' }}>
            🗂 These accounts have been terminated. All data, orders, payments and audit history are preserved.
          </div>
          <Table headers={['Name','Username','Role','Terminated','Reason']} rows={terminatedUsers.map(u=>[
            <span style={{ fontWeight:600, color:'var(--textMuted)', textDecoration:'line-through' }}>{u.name}</span>,
            <code style={{ fontSize:12, color:'var(--textMuted)' }}>@{u.username}</code>,
            <span style={{ padding:'2px 8px', borderRadius:3, fontSize:11, fontWeight:700, background:'#33333355', color:'#888', textTransform:'capitalize' }}>{u.role}</span>,
            <span style={{ fontSize:11, color:'var(--textFaint)' }}>{u.terminated_at ? new Date(u.terminated_at).toLocaleDateString('en-KE') : '—'}</span>,
            <span style={{ fontSize:11, color:'var(--textMuted)', fontStyle:'italic' }}>{u.termination_reason || '—'}</span>,
          ])}/>
        </div>
      ) : (
        <Table headers={['Name','Username','Role','Station','Status','Actions']} rows={activeUsers.map(u=>[
          <span style={{ fontWeight:600, color:'var(--textPrimary)' }}>{u.name}</span>,
          <code style={{ fontSize:12, color:'var(--textMuted)' }}>@{u.username}</code>,
          <span style={{ padding:'2px 8px', borderRadius:3, fontSize:11, fontWeight:700, background:roleColors[u.role]||'#555', color:'#111', textTransform:'capitalize' }}>{u.role}</span>,
          <span style={{ fontSize:11, color:'var(--accent)' }}>{u.kitchen_station || '—'}</span>,
          <span style={{ fontSize:12, color:u.is_active?'#2ecc71':'#e74c3c', fontWeight:700 }}>{u.is_active?'● Active':'● Inactive'}</span>,
          <div style={{ display:'flex', gap:6 }}>
            <button style={{ ...btnSm, background:u.is_active?'#333':'#1a5c1a' }} onClick={()=>toggle(u)}>{u.is_active?'Deactivate':'Activate'}</button>
            {u.role !== 'admin' && (
              <button style={{ ...btnSm, background:'#5c1a1a' }} onClick={()=>{ setDeleteModal(u); setDeleteReason(''); }}>Remove</button>
            )}
          </div>
        ])}/>
      )}

      {/* Delete confirmation modal */}
      {deleteModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'2px solid rgba(231,76,60,0.4)', borderRadius:12, padding:28, maxWidth:460, width:'100%' }}>
            <div style={{ fontSize:16, fontWeight:700, color:'#e74c3c', marginBottom:8 }}>🗑 Remove Staff Account</div>
            <div style={{ fontSize:13, color:'var(--textSecond)', marginBottom:16, lineHeight:1.6 }}>
              <strong>{deleteModal.name}</strong> ({deleteModal.role}) will be deactivated and their system access revoked.<br/>
              <span style={{ color:'#2ecc71', fontSize:12 }}>✓ All history, orders, payments and audit records are permanently preserved.</span>
            </div>
            <Field label="Reason for removal (optional)">
              <textarea style={{ ...inputStyle, minHeight:70, resize:'vertical' }}
                value={deleteReason} onChange={e=>setDeleteReason(e.target.value)}
                placeholder="e.g. Resigned, Contract ended, Misconduct…" />
            </Field>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:16 }}>
              <button onClick={()=>{ setDeleteModal(null); setDeleteReason(''); }} style={{ padding:'10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bgInput)', color:'var(--textMuted)', cursor:'pointer', fontWeight:600, fontFamily:'inherit' }}>Cancel</button>
              <button onClick={confirmDelete} style={{ padding:'10px', borderRadius:6, border:'none', background:'#c0392b', color:'#fff', cursor:'pointer', fontWeight:700, fontFamily:'inherit' }}>Confirm Remove</button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function AdminRooms({ notify }) {
  const [rooms, setRooms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ number:'', type:'Standard', price:'', floor:1 });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [exportHk, setExportHk] = useState('');
  const [exportStatus, setExportStatus] = useState('');
  const [exportFloor, setExportFloor] = useState('');
  const [exportType, setExportType] = useState('');
  const [exporting, setExporting] = useState('');
  const { user } = useAuth();
  const token = localStorage.getItem('gm_authtoken');

  const load = useCallback(async () => { const r = await api.getRooms(); setRooms(r.data.results||r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const rf = (k, v) => { setForm(p => ({...p,[k]:v})); setFormErrors(p => ({...p,[k]:''})); };

  const add = async () => {
    const errs = {};
    if (!form.number.trim()) errs.number = 'Room number is required';
    if (!form.price)          errs.price  = 'Price is required';
    else if (isNaN(Number(form.price)) || Number(form.price) <= 0) errs.price = 'Enter a valid positive price';
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    setFormErrors({});
    setSaving(true);
    try {
      await api.createRoom({...form, price:Number(form.price), floor:Number(form.floor)});
      await load();
      setForm({number:'',type:'Standard',price:'',floor:1});
      setShowForm(false);
      notify('Room added!');
    } catch(e) {
      const data = e.response?.data;
      if (data && typeof data === 'object') {
        const mapped = {};
        Object.entries(data).forEach(([k,v]) => { mapped[k] = Array.isArray(v) ? v[0] : String(v); });
        setFormErrors(mapped);
        notify('Please fix the highlighted fields', 'error');
      } else { notify(data || 'Error adding room', 'error'); }
    } finally { setSaving(false); }
  };
  const del = async (r) => { if(r.status==='occupied'){notify('Cannot delete occupied room','error');return;} await api.deleteRoom(r.id); await load(); notify('Room removed'); };

  const buildParams = () => {
    const p = new URLSearchParams();
    if (exportHk)     p.set('hk_status', exportHk);
    if (exportStatus) p.set('status', exportStatus);
    if (exportFloor)  p.set('floor', exportFloor);
    if (exportType)   p.set('room_type', exportType);
    const s = p.toString();
    return s ? `?${s}` : '';
  };

  const doExport = async (fmt) => {
    setExporting(fmt);
    notify(`Preparing Room Status ${fmt === 'excel' ? 'Excel' : 'PDF'} export…`);
    try {
      const url = fmt === 'excel' ? api.exportRoomStatusExcel(buildParams()) : api.exportRoomStatusPdf(buildParams());
      const res = await axiosClient.get(url, { responseType: 'blob', baseURL: '' });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_RoomStatus_${new Date().toISOString().slice(0,10)}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click(); URL.revokeObjectURL(a.href);
      notify(`Room Status ${fmt === 'excel' ? 'Excel' : 'PDF'} downloaded ✓`);
    } catch(e) { notify(`Export failed: ${await readBlobError(e)}`, 'error'); }
    finally { setExporting(''); }
  };

  // Count by hk_status
  const hkCounts = { clean:0, dirty:0, inspecting:0, 'out-of-order':0 };
  rooms.forEach(r => { if (hkCounts[r.hk_status] !== undefined) hkCounts[r.hk_status]++; });

  const hkColors = { clean:'#22C55E', dirty:'#E67E22', inspecting:'#3498DB', 'out-of-order':'#A855F7' };
  const hkLabels = { clean:'Clean', dirty:'Dirty', inspecting:'Inspecting', 'out-of-order':'Out of Order' };

  return (
    <>
    {/* ── Room Status Export (Admin Only) ─────────── */}
    <Card title="🏨 Room Status Export — Admin Only" style={{ marginBottom:16, border:'1px solid #c8a96e44' }}>
      <div style={{ display:'flex', gap:16, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        {Object.entries(hkCounts).map(([key, count]) => (
          <div key={key} style={{ textAlign:'center', padding:'10px 18px', background:'var(--bgCard)', border:`1px solid ${hkColors[key]}44`, borderRadius:6, minWidth:90 }}>
            <div style={{ fontSize:22, fontWeight:900, color:hkColors[key] }}>{count}</div>
            <div style={{ fontSize:10, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1 }}>{hkLabels[key]}</div>
          </div>
        ))}
        <div style={{ textAlign:'center', padding:'10px 18px', background:'#0a1a0a', border:'1px solid #2ecc7133', borderRadius:6, minWidth:90 }}>
          <div style={{ fontSize:22, fontWeight:900, color:'#2ecc71' }}>{rooms.length}</div>
          <div style={{ fontSize:10, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1 }}>Total Rooms</div>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10, marginBottom:14 }}>
        <Field label="HK Status Filter">
          <select style={inputStyle} value={exportHk} onChange={e=>setExportHk(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="clean">Clean ✓</option>
            <option value="dirty">Dirty ⚠</option>
            <option value="inspecting">Inspecting ↺</option>
            <option value="out-of-order">Out of Order</option>
          </select>
        </Field>
        <Field label="Occupancy Status">
          <select style={inputStyle} value={exportStatus} onChange={e=>setExportStatus(e.target.value)}>
            <option value="">All</option>
            <option value="available">Available</option>
            <option value="occupied">Occupied</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </Field>
        <Field label="Floor">
          <select style={inputStyle} value={exportFloor} onChange={e=>setExportFloor(e.target.value)}>
            <option value="">All Floors</option>
            {[...new Set(rooms.map(r=>r.floor))].sort().map(f=><option key={f} value={f}>Floor {f}</option>)}
          </select>
        </Field>
        <Field label="Room Type">
          <select style={inputStyle} value={exportType} onChange={e=>setExportType(e.target.value)}>
            <option value="">All Types</option>
            {['Standard','Deluxe','Suite','Presidential'].map(t=><option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>

      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
        <button
          style={{ ...btnPrimary, width:'auto', padding:'9px 20px', background:'var(--bgSuccess)', border:'1px solid #2ecc71', color:'#2ecc71', marginTop:0 }}
          onClick={() => doExport('excel')}
          disabled={!!exporting}>
          {exporting==='excel' ? '⏳ Exporting…' : '📊 Export Excel'}
        </button>
        <button
          style={{ ...btnPrimary, width:'auto', padding:'9px 20px', background:'var(--bgHover)', border:'1px solid #e74c3c', color:'#e74c3c', marginTop:0 }}
          onClick={() => doExport('pdf')}
          disabled={!!exporting}>
          {exporting==='pdf' ? '⏳ Exporting…' : '📄 Export PDF'}
        </button>
        <div style={{ fontSize:11, color:'var(--textFaint)', alignSelf:'center', fontStyle:'italic' }}>
          🔒 Export is restricted to Admin role only
        </div>
      </div>
    </Card>

    <Card title="Room Management">
      <button style={{ ...btnPrimary, width:'auto', padding:'8px 18px', marginBottom:16 }} onClick={()=>{ setShowForm(!showForm); setFormErrors({}); }}>{showForm?'✕ Cancel':'+ Add Room'}</button>
      {showForm&&(
        <div style={{ padding:14, background:'var(--bgCard)', border:'1px solid var(--borderMid)', borderRadius:8, marginBottom:16 }}>
          <div style={{ fontWeight:700, color:'#c8a96e', marginBottom:12, fontSize:13 }}>🏨 New Room</div>
          {(formErrors._general||formErrors.non_field_errors) && (
            <div style={{ marginBottom:12, padding:'9px 13px', borderRadius:7, background:'rgba(239,68,68,0.07)', border:'1.5px solid rgba(239,68,68,0.25)', fontSize:12, color:'#ef4444', display:'flex', gap:8 }}>
              <span>⚠️</span><span>{formErrors._general||formErrors.non_field_errors}</span>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10 }}>
            <Field label="Room Number" required error={formErrors.number}>
              <input style={{ ...inputStyle, ...(formErrors.number?{borderColor:'#ef4444',background:'rgba(239,68,68,0.04)'}:{}) }}
                value={form.number} onChange={e=>rf('number',e.target.value)} placeholder="105" autoFocus />
            </Field>
            <Field label="Type">
              <select style={inputStyle} value={form.type} onChange={e=>rf('type',e.target.value)}>
                {['Standard','Deluxe','Suite','Presidential'].map(t=><option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Price/Night (KES)" required error={formErrors.price}>
              <input style={{ ...inputStyle, ...(formErrors.price?{borderColor:'#ef4444',background:'rgba(239,68,68,0.04)'}:{}) }}
                type="number" value={form.price} onChange={e=>rf('price',e.target.value)} placeholder="5500" />
            </Field>
            <Field label="Floor">
              <input style={inputStyle} type="number" value={form.floor} onChange={e=>rf('floor',e.target.value)} placeholder="1" />
            </Field>
            <div style={{ alignSelf:'flex-end', display:'flex', gap:8, alignItems:'center' }}>
              <button style={{ ...btnPrimary, marginTop:0, opacity:saving?0.7:1 }} onClick={add} disabled={saving}>
                {saving ? '⟳ Adding…' : '✓ Add Room'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:10 }}>
        {rooms.map(r=>(
          <div key={r.id} style={{ background:'var(--bgInput)', border:`2px solid ${r.status==='available'?'#2ecc71':'#e67e22'}`, borderRadius:4, padding:14, textAlign:'center' }}>
            <div style={{ fontSize:18, fontWeight:700, color:'var(--textPrimary)', marginBottom:4 }}>Room {r.number}</div>
            <div style={{ fontSize:11, color:'var(--textFaint)', marginBottom:4 }}>{r.type} · Fl.{r.floor}</div>
            <div style={{ color:r.status==='available'?'#2ecc71':'#e67e22', fontSize:12, marginBottom:4 }}>{r.status==='available'?'● Available':'● Occupied'}</div>
            <div style={{ fontSize:12, color:'var(--accent)' }}>{fmtKES(r.price)}/night</div>
            {r.status==='available'&&<button style={{ ...btnSm, background:'#5c1a1a', marginTop:8, width:'100%' }} onClick={()=>del(r)}>Remove</button>}
          </div>
        ))}
      </div>
    </Card>
    </>
  );
}

function AdminMenu({ notify, type, title, color }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:'', price:'', category:'' });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => { const r = await api.getMenu(type); setItems(r.data.results||r.data); }, [type]);
  useEffect(() => { load(); }, [load]);

  const mf = (k, v) => { setForm(p => ({...p,[k]:v})); setFormErrors(p => ({...p,[k]:''})); };

  const add = async () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Item name is required';
    if (!form.price)        errs.price = 'Price is required';
    else if (isNaN(Number(form.price)) || Number(form.price) < 0) errs.price = 'Enter a valid price';
    if (!form.category.trim()) errs.category = 'Category is required';
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    setFormErrors({});
    setSaving(true);
    try {
      await api.createMenuItem({...form, price:Number(form.price), menu_type:type});
      await load();
      setForm({name:'',price:'',category:''});
      setShowForm(false);
      notify(`${form.name} added!`);
    } catch(e) {
      const data = e.response?.data;
      if (data && typeof data === 'object') {
        const mapped = {};
        Object.entries(data).forEach(([k,v]) => { mapped[k] = Array.isArray(v) ? v[0] : String(v); });
        setFormErrors(mapped);
        notify('Please fix the highlighted fields', 'error');
      } else { notify(data || 'Error adding item', 'error'); }
    } finally { setSaving(false); }
  };
  const toggle = async (item) => { await api.updateMenuItem(item.id,{is_active:!item.is_active}); await load(); };
  const del    = async (id)   => { await api.deleteMenuItem(id); await load(); notify('Item removed'); };
  const cats   = [...new Set(items.map(i=>i.category))];
  const grouped = {};
  items.forEach(i=>{ if(!grouped[i.category]) grouped[i.category]=[]; grouped[i.category].push(i); });
  return (
    <Card title={title}>
      <button style={{ ...btnPrimary, width:'auto', padding:'8px 18px', marginBottom:16, background:color }} onClick={()=>{ setShowForm(!showForm); setFormErrors({}); }}>{showForm?'✕ Cancel':'+ Add Item'}</button>
      {showForm&&(
        <div style={{ padding:14, background:'var(--bgInput)', border:'1px solid var(--borderMid)', borderRadius:8, marginBottom:16 }}>
          <div style={{ fontWeight:700, color, marginBottom:12, fontSize:13 }}>＋ New Menu Item</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10 }}>
            <Field label="Item Name" required error={formErrors.name}>
              <input style={{ ...inputStyle, ...(formErrors.name?{borderColor:'#ef4444',background:'rgba(239,68,68,0.04)'}:{}) }}
                value={form.name} onChange={e=>mf('name',e.target.value)} placeholder="e.g. Beef Stew" autoFocus />
            </Field>
            <Field label="Price (KES)" required error={formErrors.price}>
              <input style={{ ...inputStyle, ...(formErrors.price?{borderColor:'#ef4444',background:'rgba(239,68,68,0.04)'}:{}) }}
                type="number" value={form.price} onChange={e=>mf('price',e.target.value)} placeholder="750" />
            </Field>
            <Field label="Category" required error={formErrors.category}>
              <div style={{ display:'flex', gap:6 }}>
                <select style={{ ...inputStyle, flex:1, ...(formErrors.category?{borderColor:'#ef4444'}:{}) }}
                  value={form.category} onChange={e=>mf('category',e.target.value)}>
                  <option value="">— select —</option>{cats.map(c=><option key={c}>{c}</option>)}
                </select>
                <input style={{ ...inputStyle, flex:1, ...(formErrors.category?{borderColor:'#ef4444'}:{}) }}
                  value={form.category} onChange={e=>mf('category',e.target.value)} placeholder="or type new"/>
              </div>
            </Field>
            <div style={{ alignSelf:'flex-end' }}>
              <button style={{ ...btnPrimary, marginTop:0, background:color, opacity:saving?0.7:1 }} onClick={add} disabled={saving}>
                {saving ? '⟳ Adding…' : '✓ Add'}
              </button>
            </div>
          </div>
        </div>
      )}
      {Object.entries(grouped).map(([cat,catItems])=>(
        <div key={cat} style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, letterSpacing:2, color, fontWeight:700, textTransform:'uppercase', marginBottom:8, borderBottom:'1px solid #222', paddingBottom:4 }}>{cat}</div>
          <Table headers={['Item','Price','Status','Actions']} rows={catItems.map(i=>[
            <span style={{ color:'var(--textSecond)', fontWeight:600 }}>{i.name}</span>,
            <span style={{ color:'var(--accent)', fontWeight:700 }}>{fmtKES(i.price)}</span>,
            <span style={{ fontSize:12, color:i.is_active?'#2ecc71':'#e74c3c' }}>{i.is_active?'● Active':'● Hidden'}</span>,
            <div style={{ display:'flex', gap:6 }}>
              <button style={{ ...btnSm, background:i.is_active?'#333':'#1a5c1a' }} onClick={()=>toggle(i)}>{i.is_active?'Hide':'Show'}</button>
              <button style={{ ...btnSm, background:'#5c1a1a' }} onClick={()=>del(i.id)}>Delete</button>
            </div>
          ])}/>
        </div>
      ))}
    </Card>
  );
}

// ─── Gift Shop Sales Export Panel ─────────────────────────────────────────────
function AdminGiftShopExport() {
  const [gsDateFrom, setGsDateFrom] = useState('');
  const [gsDateTo,   setGsDateTo]   = useState('');
  const [gsExporting, setGsExp]     = useState('');

  const doGiftExport = async (type) => {
    const token = localStorage.getItem('gm_authtoken');
    setGsExp(type);
    try {
      let params = '?status=paid';
      if (gsDateFrom) params += `&date_from=${gsDateFrom}`;
      if (gsDateTo)   params += `&date_to=${gsDateTo}`;
      const url = type === 'pdf'
        ? api.exportGiftShopPdf(params)
        : api.exportGiftShopExcel(params);
      const res = await axiosClient.get(url, { responseType: 'blob', baseURL: '', timeout: 120000 });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 100) throw new Error('Empty file — no records matched your filters');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_GiftShop_${gsDateFrom||'all'}_${gsDateTo||'all'}.${type === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
    } catch(e) {
      const msg = e.name === 'TimeoutError' ? 'Export timed out — try a shorter date range' : await readBlobError(e);
      alert(`Gift Shop export failed: ${msg}`);
    } finally { setGsExp(''); }
  };

  return (
    <Card title="🛍️ Gift Shop Sales Export" style={{ marginTop:0 }}>
      <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom:14, lineHeight:1.7 }}>
        Export all Gift Shop sales transactions with item breakdown, payment modes, and summary by product.
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:12, marginBottom:16 }}>
        <div>
          <div style={{ fontSize:11, color:'var(--textMuted)', marginBottom:5, fontWeight:600 }}>DATE FROM</div>
          <input type="date" value={gsDateFrom} onChange={e => setGsDateFrom(e.target.value)}
            style={{ width:'100%', padding:'8px 10px', borderRadius:6, border:'1px solid var(--border)',
                     background:'var(--bgInput)', color:'var(--textPrimary)', fontSize:13, fontFamily:'inherit' }} />
        </div>
        <div>
          <div style={{ fontSize:11, color:'var(--textMuted)', marginBottom:5, fontWeight:600 }}>DATE TO</div>
          <input type="date" value={gsDateTo} onChange={e => setGsDateTo(e.target.value)}
            style={{ width:'100%', padding:'8px 10px', borderRadius:6, border:'1px solid var(--border)',
                     background:'var(--bgInput)', color:'var(--textPrimary)', fontSize:13, fontFamily:'inherit' }} />
        </div>
      </div>
      <div style={{ display:'flex', gap:10 }}>
        <button onClick={() => doGiftExport('excel')} disabled={!!gsExporting}
          className="btn btn-default btn-sm"
          style={{ flex:1, justifyContent:'center', opacity: gsExporting ? .6 : 1 }}>
          {gsExporting === 'excel' ? '⏳ Exporting…' : '📗 Excel Report'}
        </button>
        <button onClick={() => doGiftExport('pdf')} disabled={!!gsExporting}
          className="btn btn-primary btn-sm"
          style={{ flex:1, justifyContent:'center', opacity: gsExporting ? .6 : 1 }}>
          {gsExporting === 'pdf' ? '⏳ Exporting…' : '📕 PDF Report'}
        </button>
      </div>
      <div style={{ marginTop:10, fontSize:11, color:'var(--textFaint)', lineHeight:1.6 }}>
        Excel includes full transaction list + top items summary sheet. PDF shows KPI cards, sales table (first 500 rows) and top sellers. Use Excel for full data on large date ranges.
      </div>
    </Card>
  );
}

function AdminReports() {
  const [reports, setReports]   = useState(null);
  const [err, setErr]           = useState(null);
  const [period, setPeriod]     = useState('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [exporting, setExp]     = useState('');

  const load = useCallback(() => {
    setErr(null);
    setReports(null);
    let params;
    if (period === 'custom') {
      if (!dateFrom || !dateTo) { setErr('Select both a start and end date for custom range.'); return; }
      params = `?period=custom&date_from=${dateFrom}&date_to=${dateTo}`;
    } else {
      params = `?period=${period}`;
    }
    api.getAccountantReports(params)
      .then(r => {
        const d = r.data;
        setReports({
          total_revenue:      d.revenue?.total        || 0,
          room_revenue:       d.revenue?.room         || 0,
          food_revenue:       d.revenue?.food         || 0,
          bar_revenue:        d.revenue?.bar          || 0,
          pool_revenue:       d.revenue?.pool         || 0,
          conference_revenue: d.revenue?.conference   || 0,
          gift_shop_revenue:  d.revenue?.gift_shop    || 0,
          catering_levy:      d.revenue?.catering_levy|| 0,
          donations_revenue:  d.revenue?.donations    || 0,
          fnb_total:          d.revenue?.fnb_total    || 0,
          vat_collected:      d.revenue?.vat_collected|| 0,
          net_revenue:        d.revenue?.net_ex_vat   || 0,
          total_expenses:     d.expenses?.total       || 0,
          expense_by_category:d.expenses?.by_category || [],
          net_profit:         d.expenses?.net_profit  || 0,
          occupancy_pct:      d.rooms?.occupancy_rate || 0,
          adr:                d.rooms?.adr            || 0,
          revpar:             d.rooms?.revpar         || 0,
          waiter_stats:       d.waiter_stats          || [],
          daily_trend:        d.daily_trend           || [],
          period:             d.period,
        });
      })
      .catch(e => setErr(e.response?.data?.detail || e.message || 'Failed to load reports'));
  }, [period, dateFrom, dateTo]);

  useEffect(() => { if (period !== 'custom') load(); }, [period]); // eslint-disable-line

  const doExport = async (type) => {
    const token = localStorage.getItem('gm_authtoken');
    setExp(type);
    try {
      let params;
      if (period === 'custom' && dateFrom && dateTo) {
        params = `?period=custom&date_from=${dateFrom}&date_to=${dateTo}`;
      } else {
        params = `?period=${period}`;
      }
      const url = type === 'pdf' ? api.exportPdf(params) : api.exportExcel(params);
      const res = await axiosClient.get(url, { responseType: 'blob', baseURL: '', timeout: 120000 });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 100) throw new Error('Empty file returned — check server logs');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const dateStr = period === 'custom' ? `${dateFrom}_to_${dateTo}` : period;
      a.download = `${getBrandingSlug()}_Report_${dateStr}.${type === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
    } catch(e) {
      const msg = e.name === 'TimeoutError' ? 'Export timed out — try a shorter date range' : await readBlobError(e);
      alert(`Export failed: ${msg}`);
    } finally {
      setExp('');
    }
  };

  const PERIODS = [
    {id:'today', label:'Today'},
    {id:'week',  label:'This Week'},
    {id:'month', label:'This Month'},
    {id:'year',  label:'This Year'},
    {id:'custom',label:'Custom Range'},
  ];

  const periodLabel = reports?.period
    ? `${reports.period.start} → ${reports.period.end}`
    : '';

  return (
    <div>
      <PageHeader
        title="Financial Reports"
        sub="Revenue analysis, department breakdown & exportable statements"
        action={
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            {/* Period selector */}
            <select value={period} onChange={e => setPeriod(e.target.value)}
              style={{ padding:'6px 10px', borderRadius:7, border:'1.5px solid var(--borderMid)',
                background:'var(--bgCard)', color:'var(--textPrimary)', fontSize:12.5,
                fontFamily:'inherit', cursor:'pointer', minWidth:130 }}>
              {PERIODS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            {/* Excel export */}
            <button onClick={() => doExport('excel')} disabled={!!exporting}
              className="btn btn-default btn-sm" style={{ gap:5, opacity: exporting ? .6 : 1 }}>
              {exporting === 'excel' ? '⏳ Exporting…' : '⬇ Excel'}
            </button>
            {/* PDF export */}
            <button onClick={() => doExport('pdf')} disabled={!!exporting}
              className="btn btn-primary btn-sm" style={{ gap:5, opacity: exporting ? .6 : 1 }}>
              {exporting === 'pdf' ? '⏳ Exporting…' : '⬇ PDF'}
            </button>
          </div>
        }
      />

      {/* Custom date range picker */}
      {period === 'custom' && (
        <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap',
          padding:'14px 16px', background:'var(--bgCard)', border:'1px solid var(--borderMid)',
          borderRadius:8, marginBottom:16 }}>
          <Field label="From Date">
            <input type="date" style={inputStyle} value={dateFrom}
              onChange={e => setDateFrom(e.target.value)} />
          </Field>
          <Field label="To Date">
            <input type="date" style={inputStyle} value={dateTo}
              onChange={e => setDateTo(e.target.value)} />
          </Field>
          <div style={{ paddingBottom:1 }}>
            <button onClick={load}
              style={{ ...btnPrimary, marginTop:0, minWidth:120, padding:'8px 20px' }}
              disabled={!dateFrom || !dateTo}>
              Generate Report
            </button>
          </div>
          {dateFrom && dateTo && (
            <div style={{ fontSize:11, color:'var(--textMuted)', alignSelf:'center',
              padding:'6px 12px', background:'var(--bgHover)', borderRadius:5 }}>
              📅 {dateFrom} → {dateTo} ({Math.ceil((new Date(dateTo)-new Date(dateFrom))/86400000)+1} days)
            </div>
          )}
        </div>
      )}

      {/* Period label */}
      {reports && periodLabel && (
        <div style={{ fontSize:11, color:'var(--textMuted)', marginBottom:12, padding:'5px 10px',
          background:'var(--bgHover)', borderRadius:5, display:'inline-block' }}>
          📊 Showing data: <strong style={{ color:'var(--primary)' }}>{periodLabel}</strong>
        </div>
      )}

      {err && !reports && (
        <div style={{ textAlign:'center', padding:'48px 24px' }}>
          <div style={{ fontSize:28, marginBottom:12, opacity:.3 }}>⚠</div>
          <div style={{ color:'var(--textMuted)', marginBottom:16 }}>{err}</div>
          {period !== 'custom' && <button onClick={load} className="btn btn-primary">Retry</button>}
        </div>
      )}
      {!reports && !err && <Loading/>}

      {reports && (() => {
        const grand = reports.total_revenue || 0;
        const depts = [
          ['Rooms',          reports.room_revenue   || 0, '#2563eb'],
          ['Restaurant',     reports.food_revenue   || 0, '#C9A84C'],
          ['Bar',            reports.bar_revenue    || 0, '#7c3aed'],
          ['Pool',           reports.pool_revenue   || 0, '#0891b2'],
          ['Gift Shop',      reports.gift_shop_revenue || 0, '#059669'],
          ['Conference',     reports.conference_revenue || 0, '#ea580c'],
          ['Donations',      reports.donations_revenue || 0, '#16a34a'],
        ].filter(([,amt]) => amt > 0);

        return (
          <>
            {/* KPI summary row */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:20 }}>
              {[
                { label:'Total Revenue',   value: fmtKES(grand),                                              icon:'💰' },
                { label:'Room Revenue',    value: fmtKES(reports.room_revenue),                               icon:'🛏️' },
                { label:'F&B Revenue',     value: fmtKES(reports.fnb_total || (reports.food_revenue + reports.bar_revenue)), icon:'🍽️' },
                { label:'Occupancy',       value: `${Number(reports.occupancy_pct||0).toFixed(1)}%`,          icon:'🏨' },
                { label:'Total Expenses',  value: fmtKES(reports.total_expenses),                             icon:'📉' },
                { label:'Net Profit',      value: fmtKES(reports.net_profit),                                 icon:'📊' },
              ].map((s,i) => (
                <div key={i} className="stat-tile">
                  <div className="stat-label">{s.label}<span style={{fontSize:18}}>{s.icon}</span></div>
                  <div className="stat-value" style={{fontSize:19}}>{s.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }} className="r-grid-2">
              {/* Revenue by Department */}
              <Card title="Revenue by Department">
                <div style={{ padding:'4px 0' }}>
                  {depts.map(([lbl, amt, col]) => {
                    const pct = grand > 0 ? (amt / grand) * 100 : 0;
                    return (
                      <div key={lbl} style={{ marginBottom:14 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:13 }}>
                          <span style={{ color:'var(--textSecond)', fontWeight:500 }}>{lbl}</span>
                          <span style={{ fontWeight:700, color:'var(--textPrimary)' }}>
                            {fmtKES(amt)} <span style={{ color:'var(--textFaint)', fontWeight:400, fontSize:11 }}>({pct.toFixed(1)}%)</span>
                          </span>
                        </div>
                        <div style={{ height:6, background:'var(--border)', borderRadius:99, overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${pct}%`, background:col, borderRadius:99, transition:'width .6s ease' }}/>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ borderTop:'1px solid var(--border)', paddingTop:12, display:'flex', justifyContent:'space-between', marginTop:10 }}>
                    <span style={{ fontSize:14, fontWeight:700, color:'var(--textPrimary)' }}>Grand Total</span>
                    <span style={{ fontSize:16, fontWeight:800, color:'#C9A84C' }}>{fmtKES(grand)}</span>
                  </div>
                </div>
              </Card>

              {/* Expense breakdown */}
              <Card title="Expense Breakdown">
                <div style={{ padding:'4px 0' }}>
                  {(reports.expense_by_category||[]).length === 0 ? (
                    <Empty text="No expenses recorded"/>
                  ) : (
                    (reports.expense_by_category||[]).map((cat,i) => {
                      const totalExp = reports.total_expenses || 1;
                      const pct = totalExp > 0 ? (cat.amount / totalExp) * 100 : 0;
                      const colors = ['#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#8b5cf6','#ec4899'];
                      const col = colors[i % colors.length];
                      return (
                        <div key={cat.category||i} style={{ marginBottom:14 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:13 }}>
                            <span style={{ color:'var(--textSecond)', fontWeight:500 }}>{cat.label||cat.category}</span>
                            <span style={{ fontWeight:700, color:'var(--textPrimary)' }}>
                              {fmtKES(cat.amount)} <span style={{ color:'var(--textFaint)', fontWeight:400, fontSize:11 }}>({pct.toFixed(1)}%)</span>
                            </span>
                          </div>
                          <div style={{ height:6, background:'var(--border)', borderRadius:99, overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${pct}%`, background:col, borderRadius:99 }}/>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div style={{ borderTop:'1px solid var(--border)', paddingTop:12, display:'flex', justifyContent:'space-between', marginTop:10 }}>
                    <span style={{ fontSize:14, fontWeight:700, color:'var(--textPrimary)' }}>Total Expenses</span>
                    <span style={{ fontSize:16, fontWeight:800, color:'#ef4444' }}>{fmtKES(reports.total_expenses||0)}</span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Profit summary */}
            <Card style={{ marginTop:0 }}>
              <div style={{ padding:'8px 0' }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:16 }}>
                  {[
                    { label:'Gross Revenue',          value: fmtKES(grand),                                    color:'var(--textPrimary)' },
                    { label:'Less: VAT (16%)',         value: `(${fmtKES(reports.vat_collected)})`,             color:'#ef4444' },
                    { label:'Less: Catering Levy (2%)',value: `(${fmtKES(reports.catering_levy||0)})`,          color:'#e67e22' },
                    { label:'Net Revenue',             value: fmtKES(reports.net_revenue),                     color:'#C9A84C' },
                    { label:'Total Expenses',          value: `(${fmtKES(reports.total_expenses)})`,            color:'#ef4444' },
                    { label:'Net Profit/(Loss)',        value: fmtKES(reports.net_profit),                      color: reports.net_profit >= 0 ? '#22c55e' : '#ef4444' },
                  ].map(item => (
                    <div key={item.label} style={{ padding:'12px 16px', background:'var(--bgInput)', borderRadius:8, border:'1px solid var(--border)' }}>
                      <div style={{ fontSize:11, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:5, fontWeight:600 }}>{item.label}</div>
                      <div style={{ fontSize:18, fontWeight:800, color:item.color, letterSpacing:'-.3px' }}>{item.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop:12, padding:'10px 14px', background:'#1a0f00', border:'1px solid #e67e2233', borderRadius:6, fontSize:11, color:'#e67e22' }}>
                  ⚠️ <strong>Catering Levy</strong>: KES {fmtKES(reports.catering_levy||0)} is a statutory 2% charge on hotel room revenue. Remit to the Tourism Fund.
                </div>
              </div>
            </Card>

            {/* Gift Shop Sales Export */}
            <AdminGiftShopExport />

            {/* Daily trend table (for custom ranges) */}
            {(reports.daily_trend||[]).length > 0 && (
              <Card title={`Daily Breakdown (${(reports.daily_trend||[]).length} days)`} style={{ marginTop:0 }}>
                <div style={{ overflowX:'auto', maxHeight:300, overflowY:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead>
                      <tr>{['Date','Revenue','Expenses','Net'].map(h=>(
                        <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:10, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1, borderBottom:'2px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {(reports.daily_trend||[]).map((day,i)=>(
                        <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                          <td style={{ padding:'7px 10px', color:'var(--textSecond)', fontFamily:'monospace', fontSize:11 }}>{day.date}</td>
                          <td style={{ padding:'7px 10px', color:'#22c55e', fontWeight:600 }}>{fmtKES(day.revenue)}</td>
                          <td style={{ padding:'7px 10px', color:'#ef4444' }}>{fmtKES(day.expense)}</td>
                          <td style={{ padding:'7px 10px', fontWeight:700, color: day.revenue-day.expense >= 0 ? '#22c55e' : '#ef4444' }}>{fmtKES(day.revenue - day.expense)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Waiter performance */}
            {(reports.waiter_stats||[]).length > 0 && (
              <Card title="Staff Performance" style={{ marginTop:0 }}>
                <Table
                  headers={['Staff Member', 'Orders', 'Revenue', 'Pending Bills']}
                  rows={(reports.waiter_stats||[]).map(w => [
                    <div>
                      <div style={{ fontWeight:600, color:'var(--textPrimary)' }}>{w.name}</div>
                      <div style={{ fontSize:11, color:'var(--textMuted)' }}>{w.username}</div>
                    </div>,
                    <span className="badge badge-blue">{w.orders}</span>,
                    <span style={{ fontWeight:700, color:'#C9A84C' }}>{fmtKES(w.revenue)}</span>,
                    <span className={w.pending > 0 ? 'badge badge-amber' : 'badge badge-green'}>{w.pending}</span>,
                  ])}
                />
              </Card>
            )}
          </>
        );
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BRANDING SETTINGS
// ─────────────────────────────────────────────────────────────────────────────

const PRESET_COLORS = [
  { name:'Gold',       primary:'#c8a96e', dark:'#a8893e' },
  { name:'Royal Blue', primary:'#2980b9', dark:'#1a6fa0' },
  { name:'Emerald',    primary:'#27ae60', dark:'#1a8a45' },
  { name:'Rose',       primary:'#c0392b', dark:'#9b2c22' },
  { name:'Amethyst',   primary:'#8e44ad', dark:'#703590' },
  { name:'Coral',      primary:'#e67e22', dark:'#c8690e' },
  { name:'Teal',       primary:'#16a085', dark:'#0e7a65' },
  { name:'Midnight',   primary:'#2c3e50', dark:'#1a2533' },
];

function GuestReviews() {
  const [reviews, setReviews]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter,  setFilter]    = useState(0);       // 0 = all stars
  const [catFilter, setCatFilter] = useState('');
  const [search,  setSearch]    = useState('');

  useEffect(() => {
    api.getFeedback()
      .then(r => setReviews(r.data || []))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  }, []);

  const STAR_COLOR = ['','#e74c3c','#e67e22','#f1c40f','#2ecc71','#27ae60'];
  const stars = n => '★'.repeat(n) + '☆'.repeat(5-n);
  const cats  = [...new Set(reviews.map(r => r.category))].sort();

  const avg = reviews.length
    ? (reviews.reduce((s,r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : '—';
  const dist = [5,4,3,2,1].map(n => ({ n, count: reviews.filter(r=>r.rating===n).length }));

  const displayed = reviews.filter(r =>
    (!filter    || r.rating === filter) &&
    (!catFilter || r.category === catFilter) &&
    (!search    || r.message.toLowerCase().includes(search.toLowerCase()) ||
                   r.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div>
      <PageHeader icon="⭐" title="Guest Reviews" sub="All feedback submitted by guests via the landing page" />

      {loading ? <Empty text="Loading reviews…" /> : (
        <>
          {/* Summary */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16, background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:6, padding:20, marginBottom:14 }}>
            <div style={{ textAlign:'center', paddingRight:20, borderRight:'1px solid var(--border)' }}>
              <div style={{ fontSize:56, fontWeight:700, color:'var(--primary)', lineHeight:1 }}>{avg}</div>
              <div style={{ fontSize:22, color:'#f1c40f', letterSpacing:2, margin:'6px 0' }}>{reviews.length ? stars(Math.round(parseFloat(avg))) : '☆☆☆☆☆'}</div>
              <div style={{ fontSize:12, color:'var(--textMuted)' }}>{reviews.length} review{reviews.length!==1?'s':''}</div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', gap:6 }}>
              {dist.map(({n, count}) => {
                const pct = reviews.length ? Math.round(count/reviews.length*100) : 0;
                return (
                  <div key={n} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }} onClick={() => setFilter(filter===n ? 0 : n)}>
                    <span style={{ fontSize:11, color:filter===n?'var(--primary)':'var(--textMuted)', width:10, fontWeight:filter===n?700:400 }}>{n}</span>
                    <span style={{ color:'#f1c40f', fontSize:13 }}>★</span>
                    <div style={{ flex:1, height:8, background:'var(--bgHover)', borderRadius:4, overflow:'hidden' }}>
                      <div style={{ width:`${pct}%`, height:'100%', background: filter===n?'var(--primary)':'#f1c40f', borderRadius:4, transition:'width .4s' }}/>
                    </div>
                    <span style={{ fontSize:11, color:'var(--textMuted)', width:36, textAlign:'right' }}>{count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Filters */}
          <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
            <input style={{ ...inputStyle, flex:1, minWidth:180 }} placeholder="🔍 Search reviews…" value={search} onChange={e=>setSearch(e.target.value)}/>
            <select style={{ ...inputStyle, width:160 }} value={catFilter} onChange={e=>setCatFilter(e.target.value)}>
              <option value="">All Categories</option>
              {cats.map(c=><option key={c}>{c}</option>)}
            </select>
            <select style={{ ...inputStyle, width:140 }} value={filter} onChange={e=>setFilter(+e.target.value)}>
              <option value={0}>All Ratings</option>
              {[5,4,3,2,1].map(n=><option key={n} value={n}>{stars(n)}</option>)}
            </select>
            {(filter||catFilter||search) && (
              <button style={{ ...btnSm, color:'var(--danger)', borderColor:'var(--danger)44' }} onClick={()=>{setFilter(0);setCatFilter('');setSearch('');}}>✕ Clear</button>
            )}
          </div>

          {/* Review cards */}
          {displayed.length === 0 ? <Empty text="No reviews match your filters" /> : (
            <div style={{ display:'grid', gap:10 }}>
              {displayed.map(r => (
                <div key={r.id} style={{ background:'var(--bgCard)', border:`1px solid var(--border)`, borderLeft:`4px solid ${STAR_COLOR[r.rating]||'var(--border)'}`, borderRadius:4, padding:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                    <div>
                      <span style={{ fontWeight:700, color:'var(--textPrimary)', fontSize:14 }}>{r.name}</span>
                      {r.email && <span style={{ color:'var(--textMuted)', fontSize:12, marginLeft:8 }}>{r.email}</span>}
                      <div style={{ marginTop:4 }}>
                        <span style={{ color:STAR_COLOR[r.rating], fontSize:16, letterSpacing:1 }}>{stars(r.rating)}</span>
                        <span style={{ fontSize:11, color:'var(--textMuted)', marginLeft:8, background:'var(--bgHover)', padding:'2px 8px', borderRadius:10 }}>{r.category}</span>
                      </div>
                    </div>
                    <div style={{ fontSize:11, color:'var(--textFaint)', textAlign:'right' }}>
                      {new Date(r.created_at).toLocaleString('en-KE')}
                      {r.ip && <div style={{ color:'var(--textFaint)', fontSize:10 }}>IP: {r.ip}</div>}
                    </div>
                  </div>
                  <div style={{ fontSize:13, color:'var(--textSecond)', lineHeight:1.7, padding:'10px 12px', background:'var(--bgHover)', borderRadius:4 }}>
                    "{r.message}"
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BrandingSettings({ notify }) {
  const { theme, setTheme, toggleMode, resetTheme } = useTheme();
  const logoInputRef = useRef();
  const [form, setForm] = useState({ ...theme });
  const [logoPreview, setLogoPreview] = useState(theme.logoUrl || '');
  const [saved, setSaved] = useState(false);
  const [activeSection, setActiveSection] = useState('identity');

  const isDark = theme.mode === 'dark';

  // (Removed mount-time push of localStorage → server: a browser with a stale
  //  cached name would overwrite the saved branding. Server is the source of truth.)

  const up = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024) { notify('Logo must be under 500KB', 'error'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setLogoPreview(ev.target.result); up('logoUrl', ev.target.result); };
    reader.readAsDataURL(file);
  };

  // ── Approval Stamps (per role) ───────────────────────────────────────────────
  const STAMP_ROLES = [
    { key: 'storekeeper',  label: 'Storekeeper',  icon: '📦', docs: 'Store issuance vouchers' },
    { key: 'accountant',   label: 'Accountant',   icon: '📊', docs: 'Shopping lists, purchase orders' },
    { key: 'manager',      label: 'Manager',      icon: '🏨', docs: 'Complimentary bills, leave approvals' },
    { key: 'director',     label: 'Director',     icon: '🎖️', docs: 'Payslips, high-value authorizations' },
  ];
  const STAMP_STORAGE_KEYS = {
    storekeeper: 'grand_stamp_storekeeper_v1',
    accountant:  'grand_stamp_accountant_v1',
    manager:     'grand_stamp_manager_v1',
    director:    'grand_stamp_director_v1',
  };
  const stampInputRef = React.useRef();
  const [activeStampRole, setActiveStampRole] = React.useState('storekeeper');
  const [stampPreviews, setStampPreviews] = React.useState(() => {
    const result = {};
    Object.entries(STAMP_STORAGE_KEYS).forEach(([role, key]) => {
      try { result[role] = localStorage.getItem(key) || ''; } catch { result[role] = ''; }
    });
    return result;
  });

  const handleStampUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { notify('Stamp image must be under 1MB', 'error'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const storageKey = STAMP_STORAGE_KEYS[activeStampRole];
      try { localStorage.setItem(storageKey, dataUrl); } catch {}
      setStampPreviews(prev => ({ ...prev, [activeStampRole]: dataUrl }));
      const roleLabel = STAMP_ROLES.find(r => r.key === activeStampRole)?.label || activeStampRole;
      notify(`${roleLabel} stamp saved — approved documents will now show this stamp`);
    };
    reader.readAsDataURL(file);
  };

  const handleClearStamp = (role) => {
    const storageKey = STAMP_STORAGE_KEYS[role || activeStampRole];
    try { localStorage.removeItem(storageKey); } catch {}
    setStampPreviews(prev => ({ ...prev, [role || activeStampRole]: '' }));
    const roleLabel = STAMP_ROLES.find(r => r.key === (role || activeStampRole))?.label || activeStampRole;
    notify(`${roleLabel} stamp removed — documents will use the built-in SVG stamp`);
  };

  const handleSave = async () => {
    // 1. Save to localStorage (updates the live UI instantly)
    setTheme(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);

    // 2. Sync to backend so PDF/Excel exports use the correct name & logo
    try {
      await api.updateBranding({
        hotel_name:    form.hotelName    || '',
        hotel_tagline: form.hotelTagline || '',
        hotel_phone:   form.hotelPhone   || '',
        hotel_email:   form.hotelEmail   || '',
        hotel_address: form.hotelAddress || '',
        // Only send logo if it's a fresh base64 upload (starts with data:)
        ...(form.logoUrl && form.logoUrl.startsWith('data:')
          ? { hotel_logo_b64: form.logoUrl }
          : {}),
      });
      notify('Branding saved — all exports updated ✓');
    } catch (e) {
      const status = e?.response?.status;
      const msg = status === 403
        ? 'Permission denied — only Admin role can sync branding to server.'
        : status === 401
        ? 'Session expired — please log in again.'
        : `Server sync failed (${status || e?.message || 'network error'}) — name saved locally only.`;
      notify(msg, 'error');
    }
  };

  const handleReset = async () => {
    resetTheme();
    setForm({ ...theme });
    setLogoPreview('');
    // Clear backend branding too
    try {
      await api.updateBranding({
        hotel_name: theme.hotelName || 'Grand Memories Resort & Spa', hotel_tagline: theme.hotelTagline || '',
        hotel_phone: '', hotel_email: '', hotel_address: '', hotel_logo_b64: '',
      });
    } catch (e) { /* non-critical */ }
    notify('Branding reset to defaults');
  };

  const SECTIONS = [
    { id: 'identity', label: 'Hotel Identity' },
    { id: 'logo',     label: 'Logo' },
    { id: 'stamp',    label: 'Approval Stamp' },
    { id: 'colors',   label: 'Colors' },
    { id: 'mode',     label: 'Display Mode' },
    { id: 'type',     label: 'Typography' },
  ];

  return (
    <div style={{ maxWidth: 860 }}>
      <PageHeader
        title="Branding"
        sub="Customise hotel identity, colours, and appearance across the entire system"
      />

      {/* Live preview strip */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px',
        background: 'var(--bgCard)', border: '1px solid var(--border)',
        borderRadius: 5, marginBottom: 20,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 6, flexShrink: 0,
          background: form.primaryColor || 'var(--primary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {logoPreview
            ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
            : <span style={{ fontSize: 16 }}>🏨</span>
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: form.primaryColor || 'var(--primary)', letterSpacing: -.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {form.hotelName || 'Hotel Name'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--textFaint)', marginTop: 1 }}>
            {form.hotelTagline || 'Tagline'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: form.primaryColor || 'var(--primary)', flexShrink: 0 }}/>
          <span style={{ fontSize: 11, color: 'var(--textMuted)', fontFamily: 'monospace' }}>{form.primaryColor || '—'}</span>
          <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 4px' }}/>
          <button onClick={toggleMode} style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
            background: 'var(--bgHover)', border: '1px solid var(--border)', borderRadius: 4,
            cursor: 'pointer', fontSize: 12, color: 'var(--textSecond)', fontFamily: 'inherit',
          }}>
            {isDark ? '☀ Light' : '🌙 Dark'}
          </button>
        </div>
      </div>

      {/* Section tabs */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '1px solid var(--border)',
        marginBottom: 20, overflowX: 'auto',
      }}>
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)} style={{
            padding: '0 14px', height: 36, border: 'none', background: 'transparent',
            borderBottom: `2px solid ${activeSection === s.id ? 'var(--primary)' : 'transparent'}`,
            color: activeSection === s.id ? 'var(--primary)' : 'var(--textMuted)',
            fontWeight: activeSection === s.id ? 600 : 500, fontSize: 13, cursor: 'pointer',
            fontFamily: 'inherit', whiteSpace: 'nowrap', marginBottom: -1, flexShrink: 0,
            transition: 'color .12s, border-color .12s',
          }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ── Identity ── */}
      {activeSection === 'identity' && (
        <div>
          <div style={{ marginBottom: 8, padding: '8px 10px', background: 'var(--bgHover)', border: '1px solid var(--border)', borderRadius: 5, fontSize: 12, color: 'var(--textMuted)' }}>
            Hotel name and tagline update everywhere — navigation, landing page, browser tab, and all modules.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }} className="form-grid">
            <Field label="Hotel Name" required>
              <input style={inputStyle} value={form.hotelName || ''} onChange={e => up('hotelName', e.target.value)} placeholder="Grand Memories Resort & Spa" />
            </Field>
            <Field label="Tagline">
              <input style={inputStyle} value={form.hotelTagline || ''} onChange={e => up('hotelTagline', e.target.value)} placeholder="Lodge & Retreat" />
            </Field>
            <Field label="Phone">
              <input style={inputStyle} value={form.hotelPhone || ''} onChange={e => up('hotelPhone', e.target.value)} placeholder="(+254) 705 225 225" />
            </Field>
            <Field label="Email">
              <input style={inputStyle} value={form.hotelEmail || ''} onChange={e => up('hotelEmail', e.target.value)} placeholder="info@hotel.com" />
            </Field>
            <Field label="Address">
              <input style={inputStyle} value={form.hotelAddress || ''} onChange={e => up('hotelAddress', e.target.value)} placeholder="Nakuru, Kenya" />
            </Field>
            <Field label="Symbol (fallback when no logo)">
              <input style={{ ...inputStyle, fontSize: 20, textAlign: 'center', letterSpacing: 2 }}
                value={form.hotelSymbol || ''} onChange={e => up('hotelSymbol', e.target.value)}
                placeholder="◈" maxLength={4} />
            </Field>
          </div>
        </div>
      )}

      {/* ── Logo ── */}
      {activeSection === 'logo' && (
        <div>
          {logoPreview ? (
            <div style={{
              padding: 20, background: 'var(--bgHover)', border: '1px solid var(--border)',
              borderRadius: 5, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16,
            }}>
              <img src={logoPreview} alt="logo" style={{ maxHeight: 56, maxWidth: 160, objectFit: 'contain' }}/>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--textPrimary)', marginBottom: 4 }}>Logo uploaded</div>
                <div style={{ fontSize: 12, color: 'var(--textMuted)', marginBottom: 10 }}>Appears in the navigation bar and landing page.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-default btn-sm" onClick={() => logoInputRef.current?.click()}>Change</button>
                  <button className="btn btn-danger btn-sm" onClick={() => { setLogoPreview(''); up('logoUrl', ''); }}>Remove</button>
                </div>
              </div>
            </div>
          ) : (
            <div
              onClick={() => logoInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleLogoUpload({ target: { files: [f] } }); }}
              style={{
                border: '1.5px dashed var(--borderMid)', borderRadius: 5, padding: '36px 24px',
                textAlign: 'center', cursor: 'pointer', marginBottom: 16,
                transition: 'border-color .15s, background .15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--bgHover)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--borderMid)'; e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ fontSize: 24, marginBottom: 8, opacity: .35 }}>🖼</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--textSecond)', marginBottom: 4 }}>Click or drag & drop to upload</div>
              <div style={{ fontSize: 12, color: 'var(--textFaint)' }}>PNG, JPG, SVG — max 500 KB</div>
            </div>
          )}
          <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
          {!logoPreview && (
            <button className="btn btn-default" onClick={() => logoInputRef.current?.click()} style={{ width: '100%' }}>
              Upload Logo
            </button>
          )}
          <p style={{ fontSize: 12, color: 'var(--textFaint)', marginTop: 10 }}>
            If no logo is uploaded, the symbol set in Hotel Identity is shown instead.
          </p>
        </div>
      )}


      {/* ── Approval Stamps (per role) ── */}
      {activeSection === 'stamp' && (
        <div>
          <div style={{ fontSize: 13, color: 'var(--textSecond)', marginBottom: 16, lineHeight: 1.7 }}>
            Upload a stamp image for each role. Each stamp appears automatically on documents
            approved by that role — the correct stamp is applied based on who approved each document.
          </div>

          {/* Role selector tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            {STAMP_ROLES.map(r => (
              <button key={r.key} onClick={() => setActiveStampRole(r.key)} style={{
                padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                fontSize: 13, fontWeight: activeStampRole === r.key ? 700 : 400,
                border: `2px solid ${activeStampRole === r.key ? '#c8a96e' : 'var(--border)'}`,
                background: activeStampRole === r.key ? '#1a1500' : 'var(--bgInput)',
                color: activeStampRole === r.key ? '#c8a96e' : 'var(--textMuted)',
                display: 'flex', alignItems: 'center', gap: 6, transition: 'all .15s',
              }}>
                <span>{r.icon}</span>
                <span>{r.label}</span>
                {stampPreviews[r.key] && (
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2ecc71', display: 'inline-block' }} />
                )}
              </button>
            ))}
          </div>

          {/* Active role stamp manager */}
          {STAMP_ROLES.map(r => activeStampRole === r.key && (
            <div key={r.key}>
              <div style={{ fontSize: 12, color: 'var(--textFaint)', marginBottom: 12, padding: '8px 12px', background: 'var(--bgInput)', borderRadius: 6, borderLeft: '3px solid #c8a96e' }}>
                <strong style={{ color: '#c8a96e' }}>{r.icon} {r.label} Stamp</strong> — appears on: {r.docs}
              </div>

              {stampPreviews[r.key] ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: 20,
                  background: 'var(--bgInput)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 16 }}>
                  <img src={stampPreviews[r.key]} alt={`${r.label} Stamp`}
                    style={{ width: 140, height: 140, objectFit: 'contain', background: '#fff', padding: 8,
                      borderRadius: 8, border: '1px solid var(--border)' }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#2ecc71', marginBottom: 6 }}>
                      ✓ {r.label} stamp uploaded
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--textMuted)', marginBottom: 14 }}>
                      This stamp will appear on all {r.docs.toLowerCase()}.
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bgCard)', color: 'var(--textSecond)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}
                        onClick={() => stampInputRef.current?.click()}>
                        Replace
                      </button>
                      <button style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid #e74c3c44', background: '#3a1010', color: '#e74c3c', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}
                        onClick={() => handleClearStamp(r.key)}>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => stampInputRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleStampUpload({ target: { files: [f] } }); }}
                  style={{ border: '2px dashed var(--border)', borderRadius: 8, padding: '36px 24px',
                    textAlign: 'center', cursor: 'pointer', marginBottom: 16,
                    transition: 'border-color .15s', background: 'var(--bgInput)' }}>
                  <div style={{ fontSize: 48, marginBottom: 10 }}>{r.icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                    Upload {r.label} Stamp
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--textMuted)' }}>
                    PNG with transparent background recommended · Max 1MB<br/>
                    Drag &amp; drop or click to browse
                  </div>
                </div>
              )}
            </div>
          ))}

          <input ref={stampInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleStampUpload} />

          {/* Overview grid of all stamps */}
          <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--bgInput)', border: '1px solid var(--border)', borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>All Role Stamps</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12 }}>
              {STAMP_ROLES.map(r => (
                <div key={r.key} onClick={() => setActiveStampRole(r.key)} style={{ textAlign: 'center', cursor: 'pointer', padding: 10, borderRadius: 6, border: `1px solid ${activeStampRole === r.key ? '#c8a96e' : 'var(--border)'}`, background: 'var(--bgCard)', transition: 'all .15s' }}>
                  {stampPreviews[r.key]
                    ? <img src={stampPreviews[r.key]} alt={r.label} style={{ width: 60, height: 60, objectFit: 'contain', background: '#fff', borderRadius: 4, padding: 4 }} />
                    : <div style={{ width: 60, height: 60, background: 'var(--bgInput)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, margin: '0 auto' }}>{r.icon}</div>
                  }
                  <div style={{ fontSize: 10, color: 'var(--textMuted)', marginTop: 6, fontWeight: 600 }}>{r.label}</div>
                  <div style={{ fontSize: 9, color: stampPreviews[r.key] ? '#2ecc71' : '#555', marginTop: 2 }}>
                    {stampPreviews[r.key] ? '● Uploaded' : '○ Using SVG default'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bgInput)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, color: 'var(--textMuted)', lineHeight: 1.7 }}>
            <strong style={{ color: 'var(--text)' }}>💡 Tip:</strong> If no stamp is uploaded for a role, a built-in SVG
            stamp is used automatically with the correct role title (Storekeeper, Accountant, Manager, or Director).
            The uploaded images you provided are perfect — upload each one to the correct role above.
          </div>
        </div>
      )}

      {/* ── Colors ── */}
      {activeSection === 'colors' && (
        <div>
          {/* Presets */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>
              Quick Presets
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PRESET_COLORS.map(p => {
                const isActive = form.primaryColor === p.primary;
                return (
                  <button key={p.name} onClick={() => setForm(f => ({ ...f, primaryColor: p.primary, primaryDark: p.dark }))} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px',
                    background: isActive ? p.primary + '14' : 'var(--bgCard)',
                    border: `1px solid ${isActive ? p.primary : 'var(--border)'}`,
                    borderRadius: 4, cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit',
                    color: isActive ? p.primary : 'var(--textSecond)', transition: 'all .12s',
                    fontWeight: isActive ? 600 : 400,
                  }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: p.primary, flexShrink: 0 }}/>
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="form-grid">
            {[
              { key: 'primaryColor', label: 'Primary Color', hint: 'Buttons, links, active states' },
              { key: 'primaryDark',  label: 'Primary Hover',  hint: 'Hover state of primary' },
              { key: 'accentColor',  label: 'Accent / Success', hint: 'Positive indicators' },
              { key: 'dangerColor',  label: 'Danger / Error', hint: 'Destructive actions, errors' },
            ].map(({ key, label, hint }) => (
              <Field key={key} label={label}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form[key] || '#000000'}
                    onChange={e => up(key, e.target.value)}
                    style={{ width: 36, height: 34, border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', padding: 2, background: 'var(--bgCard)', flexShrink: 0 }}
                  />
                  <input style={{ ...inputStyle, fontFamily: 'ui-monospace,monospace', letterSpacing: .5 }}
                    value={form[key] || ''} onChange={e => up(key, e.target.value)} placeholder="#000000"
                  />
                </div>
                <div style={{ fontSize: 11, color: 'var(--textFaint)', marginTop: 4 }}>{hint}</div>
              </Field>
            ))}
          </div>

          {/* Color preview row */}
          <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--bgHover)', border: '1px solid var(--border)', borderRadius: 5, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--textMuted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', marginRight: 4 }}>Preview</span>
            {[
              { label: 'Primary', color: form.primaryColor },
              { label: 'Hover', color: form.primaryDark },
              { label: 'Accent', color: form.accentColor },
              { label: 'Danger', color: form.dangerColor },
            ].map(({ label, color }) => color ? (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 20, height: 20, borderRadius: 3, background: color, border: '1px solid rgba(0,0,0,.08)', flexShrink: 0 }}/>
                <span style={{ fontSize: 11, color: 'var(--textMuted)' }}>{label}</span>
              </div>
            ) : null)}
          </div>
        </div>
      )}

      {/* ── Display Mode ── */}
      {activeSection === 'mode' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-grid">
            {[
              { id: 'light', label: 'Light Mode', desc: 'White background, dark text. Best for well-lit environments.' },
              { id: 'dark',  label: 'Dark Mode',  desc: 'Dark background, light text. Easier on eyes in low-light.' },
            ].map(m => {
              const isActive = form.mode === m.id;
              return (
                <div key={m.id} onClick={() => up('mode', m.id)} style={{
                  padding: '16px', borderRadius: 5, cursor: 'pointer',
                  border: `1.5px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
                  background: isActive ? 'rgba(37,99,235,.05)' : 'var(--bgCard)',
                  transition: 'border-color .12s, background .12s',
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 5, flexShrink: 0,
                    background: m.id === 'dark' ? '#18181b' : '#f4f4f5',
                    border: `1.5px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                  }}>
                    {m.id === 'dark' ? '🌙' : '☀️'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 500, color: isActive ? 'var(--primary)' : 'var(--textPrimary)', marginBottom: 3 }}>
                      {m.label}
                      {isActive && <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--primary)', color: '#fff', padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>Active</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--textMuted)', lineHeight: 1.4 }}>{m.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Typography ── */}
      {activeSection === 'type' && (
        <div>
          <div style={{ marginBottom: 8, padding: '8px 10px', background: 'var(--bgHover)', border: '1px solid var(--border)', borderRadius: 5, fontSize: 12, color: 'var(--textMuted)' }}>
            Enter a valid CSS <code style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11 }}>font-family</code> value. Fonts must be loaded separately via Google Fonts or be system fonts.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 16 }} className="form-grid">
            <Field label="Display / Heading Font" hint="Used for hotel name and page headers">
              <input style={{ ...inputStyle, fontFamily: form.displayFont || 'inherit' }}
                value={form.displayFont || ''} onChange={e => up('displayFont', e.target.value)}
                placeholder="'Playfair Display', serif"
              />
              <div style={{ marginTop: 6, padding: '8px 10px', background: 'var(--bgHover)', borderRadius: 4, fontSize: 16, fontFamily: form.displayFont || 'inherit', color: 'var(--textPrimary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {form.hotelName || 'Grand Memories Resort & Spa'}
              </div>
            </Field>
            <Field label="Body / UI Font" hint="Used for all interface text, forms, and tables">
              <input style={{ ...inputStyle, fontFamily: form.bodyFont || 'inherit' }}
                value={form.bodyFont || ''} onChange={e => up('bodyFont', e.target.value)}
                placeholder="'Inter', sans-serif"
              />
              <div style={{ marginTop: 6, padding: '8px 10px', background: 'var(--bgHover)', borderRadius: 4, fontSize: 13, fontFamily: form.bodyFont || 'inherit', color: 'var(--textPrimary)' }}>
                The quick brown fox jumps over the lazy dog.
              </div>
            </Field>
          </div>
        </div>
      )}

      {/* ── Action bar ── */}
      <div style={{ display: 'flex', gap: 8, marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <button
          onClick={handleSave}
          className="btn btn-primary"
          style={{ minWidth: 140, justifyContent: 'center', background: saved ? '#16a34a' : undefined, borderColor: saved ? '#16a34a' : undefined }}
        >
          {saved ? '✓ Saved' : 'Save Changes'}
        </button>
        <button onClick={handleReset} className="btn btn-default" style={{ color: '#dc2626', borderColor: '#fca5a5' }}
          onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--bgCard)'}>
          Reset to Defaults
        </button>
      </div>

      {/* Info note */}
      <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--bgInfo)', border: '1px solid var(--borderMid)', borderRadius: 5, fontSize: 12, color: 'var(--textSecond)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--primary)', fontWeight: 600 }}>Note:</strong> Changes are stored in the browser and apply instantly across the entire system. To persist across all devices, a backend settings endpoint can be configured.
      </div>
    </div>
  );
}


// ─── Landing Page Content Editor ──────────────────────────────────────────────
function LandingEditor({ notify }) {
  const { theme, setTheme } = useTheme();
  const fileRefs = {
    hero: useRef(), banner: useRef(), room: useRef(),
    gal0: useRef(), gal1: useRef(), gal2: useRef(), gal3: useRef(),
  };

  const [form, setForm] = useState({
    heroHeadline:  theme.heroHeadline  || 'Where the Wild Meets Luxury',
    heroSubtitle:  theme.heroSubtitle  || 'A sanctuary of elegance against the breathtaking backdrop of Lake Nakuru — world-class hospitality in the heart of Kenya.',
    heroTag:       theme.heroTag       || 'Nakuru, Kenya',
    whyTitle:      theme.whyTitle      || 'The Finest Hospitality\nin Nakuru',
    whyBody:       theme.whyBody       || 'Nestled by Lake Nakuru, we blend world-class comfort with the warmth of authentic Kenyan hospitality.',
    whyPointsText: (theme.whyPoints || ['Award-winning restaurant & rooftop bar','Complimentary pool for all hotel guests','24/7 concierge & butler service','Fully equipped conference facilities']).join('\n'),
    bannerTitle:   theme.bannerTitle   || 'Your Home Away From Home',
    bannerBody:    theme.bannerBody    || 'Whether a romantic getaway, family holiday, or corporate retreat — every stay is thoughtfully curated.',
    footerTagline: theme.footerTagline || 'A sanctuary of elegance in the heart of Kenya — where world-class hospitality meets the soul of Africa.',
    landingRoomsJSON: theme.landingRooms ? JSON.stringify(theme.landingRooms, null, 2) : '',
    landingAmenitiesJSON: theme.landingAmenities ? JSON.stringify(theme.landingAmenities, null, 2) : '',
  });

  const [images, setImages] = useState({
    heroImage:    theme.heroImage    || '',
    bannerImage:  theme.bannerImage  || '',
    roomImage:    theme.roomImage    || '',
    galleryImages: theme.galleryImages || ['','','',''],
  });

  const [saved, setSaved]   = useState(false);
  const [jsonErr, setJsonErr] = useState('');
  const [activeTab, setActiveTab] = useState('text');

  const readFile = (file, maxMB = 2) => new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    if (file.size > maxMB * 1024 * 1024) { reject(`File must be under ${maxMB}MB`); return; }
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = () => reject('Read failed');
    reader.readAsDataURL(file);
  });

  const handleImageUpload = async (key, file, galIndex = null) => {
    try {
      const data = await readFile(file, 2);
      if (!data) return;
      if (galIndex !== null) {
        const arr = [...images.galleryImages];
        arr[galIndex] = data;
        setImages(prev => ({ ...prev, galleryImages: arr }));
      } else {
        setImages(prev => ({ ...prev, [key]: data }));
      }
      notify('Image uploaded — save to apply!');
    } catch(e) { notify(e, 'error'); }
  };

  const handleImageUrl = (key, url, galIndex = null) => {
    if (galIndex !== null) {
      const arr = [...images.galleryImages];
      arr[galIndex] = url;
      setImages(prev => ({ ...prev, galleryImages: arr }));
    } else {
      setImages(prev => ({ ...prev, [key]: url }));
    }
  };

  const clearImage = (key, galIndex = null) => {
    if (galIndex !== null) {
      const arr = [...images.galleryImages];
      arr[galIndex] = '';
      setImages(prev => ({ ...prev, galleryImages: arr }));
    } else {
      setImages(prev => ({ ...prev, [key]: '' }));
    }
  };

  const handleSave = () => {
    setJsonErr('');
    let landingRooms = null, landingAmenities = null;
    try { if (form.landingRoomsJSON.trim()) landingRooms = JSON.parse(form.landingRoomsJSON); }
    catch(e) { setJsonErr('Rooms JSON is invalid: ' + e.message); return; }
    try { if (form.landingAmenitiesJSON.trim()) landingAmenities = JSON.parse(form.landingAmenitiesJSON); }
    catch(e) { setJsonErr('Amenities JSON is invalid: ' + e.message); return; }

    const whyPoints = form.whyPointsText.split('\n').map(s => s.trim()).filter(Boolean);

    const updates = {
      heroHeadline: form.heroHeadline,
      heroSubtitle: form.heroSubtitle,
      heroTag:      form.heroTag,
      whyTitle:     form.whyTitle,
      whyBody:      form.whyBody,
      whyPoints,
      bannerTitle:  form.bannerTitle,
      bannerBody:   form.bannerBody,
      footerTagline:form.footerTagline,
      landingRooms,
      landingAmenities,
      heroImage:    images.heroImage   || undefined,
      bannerImage:  images.bannerImage || undefined,
      roomImage:    images.roomImage   || undefined,
      galleryImages: images.galleryImages.map((img,i) => img || (theme.galleryImages?.[i] || '')),
    };
    // Remove undefined
    Object.keys(updates).forEach(k => updates[k] === undefined && delete updates[k]);
    setTheme(updates);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    notify('🌐 Landing page updated — changes are live!');
  };

  const DEFAULT_ROOMS_TEMPLATE = JSON.stringify([
    { type: 'Standard', price: 4500, desc: 'Serene comfort with garden views, king bed, and classic African décor.', icon: '🛏️', features: ['Free WiFi', 'Air Con', 'Mini Bar', 'Room Service'] },
    { type: 'Deluxe', price: 7500, desc: 'Spacious luxury with private balcony, bathtub, and panoramic savanna views.', icon: '✨', features: ['Free WiFi', 'Air Con', 'Mini Bar', 'Balcony', 'Bathtub'] },
    { type: 'Suite', price: 15000, desc: 'Pinnacle of opulence — private dining, butler service, and savanna views.', icon: '👑', features: ['Free WiFi', 'Air Con', 'Butler', 'Private Dining', 'Jacuzzi'] },
    { type: 'Presidential', price: 25000, desc: 'An entire floor of luxury — two bedrooms, private gym, and 360° views.', icon: '🏰', features: ['Free WiFi', 'Air Con', 'Butler', 'Private Gym', '360° Views'] },
  ], null, 2);

  const DEFAULT_AMENITIES_TEMPLATE = JSON.stringify([
    { icon: '🏊', label: 'Infinity Pool', desc: 'Open 6am–10pm daily. Hotel guests swim free.' },
    { icon: '🎠', label: 'Kids Zone', desc: 'Slides, trampoline, mini golf & more' },
    { icon: '🍽️', label: 'Fine Dining', desc: 'International & local cuisine' },
    { icon: '🍹', label: 'Savanna Bar', desc: 'Premium drinks & cocktails' },
    { icon: '💆', label: 'Spa & Wellness', desc: 'Full treatment menu' },
    { icon: '🏋️', label: 'Fitness Centre', desc: 'Modern equipment, 24/7' },
    { icon: '🏛️', label: 'Conference Rooms', desc: 'Up to 200 delegates' },
  ], null, 2);

  // Image upload card component
  const ImageCard = ({ label, imgKey, galIndex = null, refKey }) => {
    const currentSrc = galIndex !== null ? images.galleryImages[galIndex] : images[imgKey];
    const [urlMode, setUrlMode] = useState(false);
    const [urlVal, setUrlVal] = useState(currentSrc && !currentSrc.startsWith('data:') ? currentSrc : '');
    return (
      <div style={{ border:'1px solid var(--borderMid)', borderRadius:8, overflow:'hidden', background:'var(--bgHover)' }}>
        {/* Preview */}
        <div style={{ height:120, position:'relative', background:'var(--bgInput)', overflow:'hidden' }}>
          {currentSrc ? (
            <img src={currentSrc} alt={label} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          ) : (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', flexDirection:'column', gap:6, opacity:0.4 }}>
              <span style={{ fontSize:24 }}>🖼️</span>
              <span style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1 }}>No image set</span>
            </div>
          )}
          {currentSrc && (
            <button onClick={() => clearImage(imgKey, galIndex)} style={{ position:'absolute', top:6, right:6, background:'rgba(0,0,0,0.6)', border:'none', borderRadius:4, color:'#fff', cursor:'pointer', fontSize:11, padding:'3px 8px' }}>✕ Remove</button>
          )}
        </div>
        <div style={{ padding:'10px 12px' }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--textSecond)', marginBottom:8, letterSpacing:0.5 }}>{label}</div>
          <div style={{ display:'flex', gap:6, marginBottom:8 }}>
            <button onClick={() => setUrlMode(false)} style={{ flex:1, fontSize:10, padding:'5px', borderRadius:4, border:`1px solid ${!urlMode?'var(--primary)':'var(--borderMid)'}`, background:!urlMode?'var(--primary)':'var(--bgCard)', color:!urlMode?'#fff':'var(--textMuted)', cursor:'pointer', fontFamily:'inherit' }}>⬆ Upload</button>
            <button onClick={() => setUrlMode(true)} style={{ flex:1, fontSize:10, padding:'5px', borderRadius:4, border:`1px solid ${urlMode?'var(--primary)':'var(--borderMid)'}`, background:urlMode?'var(--primary)':'var(--bgCard)', color:urlMode?'#fff':'var(--textMuted)', cursor:'pointer', fontFamily:'inherit' }}>🔗 URL</button>
          </div>
          {!urlMode ? (
            <button onClick={() => fileRefs[refKey]?.current?.click()} style={{ ...btnSm, width:'100%', padding:'7px', textAlign:'center', fontSize:11 }}>
              {currentSrc ? '↺ Replace Image' : '+ Upload Image'}
            </button>
          ) : (
            <div style={{ display:'flex', gap:4 }}>
              <input style={{ ...inputStyle, flex:1, fontSize:11 }} placeholder="Paste image URL…" value={urlVal} onChange={e => setUrlVal(e.target.value)} />
              <button onClick={() => { handleImageUrl(imgKey, urlVal, galIndex); }} style={{ ...btnSm, padding:'5px 10px', fontSize:11 }}>Set</button>
            </div>
          )}
          <input ref={fileRefs[refKey]} type="file" accept="image/*" style={{ display:'none' }} onChange={e => handleImageUpload(imgKey, e.target.files[0], galIndex)} />
          <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:5 }}>Max 2MB · PNG, JPG, WebP</div>
        </div>
      </div>
    );
  };

  const editorTabs = [
    { id:'text', label:'📝 Text Content' },
    { id:'images', label:'🖼️ Images' },
    { id:'rooms', label:'🛏️ Rooms' },
    { id:'amenities', label:'✨ Amenities' },
    { id:'layout', label:'🎨 Layout Style' },
  ];

  return (
    <div>
      <PageHeader icon="🌐" title="Landing Page Editor" sub="Customize the public-facing website content — text, images, rooms & amenities" />

      <div style={{ padding: '12px 16px', background: 'var(--bgInfo)', border: '1px solid #3498db33', borderRadius: 4, marginBottom: 16, fontSize: 12, color: 'var(--textSecond)' }}>
        ℹ️ Changes apply immediately to the landing page when you save. The hotel name in <strong>Branding</strong> also updates everywhere automatically.
      </div>

      {/* Sub-tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
        {editorTabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding:'8px 16px', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
            border:`1px solid ${activeTab===t.id?'var(--primary)':'var(--borderMid)'}`,
            background:activeTab===t.id?'var(--primary)':'var(--bgCard)',
            color:activeTab===t.id?'#fff':'var(--textMuted)',
            transition:'all .15s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── TEXT CONTENT TAB ── */}
      {activeTab === 'text' && (
        <div>
          <Card title="Hero Section">
            <Field label="Location Tag (small text above hotel name)">
              <input style={inputStyle} value={form.heroTag} onChange={e => setForm(f => ({ ...f, heroTag: e.target.value }))} placeholder="Nakuru, Kenya" />
            </Field>
            <Field label="Hero Headline (large italic tagline)">
              <input style={inputStyle} value={form.heroHeadline} onChange={e => setForm(f => ({ ...f, heroHeadline: e.target.value }))} placeholder="Where the Wild Meets Luxury" />
            </Field>
            <Field label="Hero Subtitle (description paragraph)">
              <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={form.heroSubtitle} onChange={e => setForm(f => ({ ...f, heroSubtitle: e.target.value }))} placeholder="Describe your hotel…" />
            </Field>
          </Card>

          <Card title="Why Choose Us Section">
            <Field label="Section Heading (use \\n for a line break)">
              <input style={inputStyle} value={form.whyTitle} onChange={e => setForm(f => ({ ...f, whyTitle: e.target.value }))} placeholder="The Finest Hospitality\nin Nakuru" />
            </Field>
            <Field label="Body Paragraph">
              <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={form.whyBody} onChange={e => setForm(f => ({ ...f, whyBody: e.target.value }))} />
            </Field>
            <Field label="Bullet Points (one per line, max 6)">
              <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical', fontFamily:'monospace', fontSize:12 }} value={form.whyPointsText} onChange={e => setForm(f => ({ ...f, whyPointsText: e.target.value }))} placeholder="Award-winning restaurant & rooftop bar&#10;Complimentary pool for all hotel guests" />
            </Field>
          </Card>

          <Card title="Mid-Page Banner Section">
            <Field label="Banner Headline (use \\n for line break)">
              <input style={inputStyle} value={form.bannerTitle} onChange={e => setForm(f => ({ ...f, bannerTitle: e.target.value }))} placeholder="Your Home Away From Home" />
            </Field>
            <Field label="Banner Body Text">
              <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={form.bannerBody} onChange={e => setForm(f => ({ ...f, bannerBody: e.target.value }))} />
            </Field>
          </Card>

          <Card title="Footer Tagline">
            <Field label="Footer description text">
              <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.footerTagline} onChange={e => setForm(f => ({ ...f, footerTagline: e.target.value }))} />
            </Field>
          </Card>
        </div>
      )}

      {/* ── IMAGES TAB ── */}
      {activeTab === 'images' && (
        <div>
          <div style={{ padding:'10px 14px', background:'rgba(196,94,62,0.07)', border:'1px solid rgba(196,94,62,0.2)', borderRadius:6, marginBottom:16, fontSize:12, color:'var(--textSecond)' }}>
            📸 Upload images from your computer or paste image URLs. Large base64 images are stored in the browser — for production use, link to hosted URLs instead.
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:12, marginBottom:16 }}>
            <ImageCard label="Hero Background (main banner)" imgKey="heroImage" refKey="hero" />
            <ImageCard label="Banner Section (mid-page)" imgKey="bannerImage" refKey="banner" />
            <ImageCard label="Room Cards Image" imgKey="roomImage" refKey="room" />
          </div>
          <Card title="Gallery Images (4 photos in Amenities section)">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
              {['Luxury Suites','Infinity Pool','Fine Dining','Spa & Wellness'].map((label, i) => (
                <ImageCard key={i} label={`Gallery ${i+1}: ${label}`} imgKey="galleryImages" galIndex={i} refKey={`gal${i}`} />
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* ── ROOMS TAB ── */}
      {activeTab === 'rooms' && (
        <Card title="Rooms & Suites (JSON)" action={
          <button style={btnSm} onClick={() => setForm(f => ({ ...f, landingRoomsJSON: DEFAULT_ROOMS_TEMPLATE }))}>Load Template</button>
        }>
          <div style={{ fontSize: 12, color: 'var(--textMuted)', marginBottom: 8 }}>
            Edit room types, prices, descriptions and features. Each room object: <code style={{ color: 'var(--primary)', background: 'var(--bgHover)', padding: '1px 5px', borderRadius: 3 }}>{'{ type, price, desc, icon, features[] }'}</code>
          </div>
          <textarea
            style={{ ...inputStyle, minHeight: 320, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
            value={form.landingRoomsJSON}
            onChange={e => setForm(f => ({ ...f, landingRoomsJSON: e.target.value }))}
            placeholder={DEFAULT_ROOMS_TEMPLATE}
          />
          <button style={{ ...btnSm, marginTop: 6, color: 'var(--danger)' }} onClick={() => setForm(f => ({ ...f, landingRoomsJSON: '' }))}>Reset</button>
        </Card>
      )}

      {/* ── AMENITIES TAB ── */}
      {activeTab === 'amenities' && (
        <Card title="Amenities Section (JSON)" action={
          <button style={btnSm} onClick={() => setForm(f => ({ ...f, landingAmenitiesJSON: DEFAULT_AMENITIES_TEMPLATE }))}>Load Template</button>
        }>
          <div style={{ fontSize: 12, color: 'var(--textMuted)', marginBottom: 8 }}>
            Edit amenities shown on landing page. Each: <code style={{ color: 'var(--primary)', background: 'var(--bgHover)', padding: '1px 5px', borderRadius: 3 }}>{'{ icon, label, desc }'}</code>
          </div>
          <textarea
            style={{ ...inputStyle, minHeight: 260, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
            value={form.landingAmenitiesJSON}
            onChange={e => setForm(f => ({ ...f, landingAmenitiesJSON: e.target.value }))}
            placeholder={DEFAULT_AMENITIES_TEMPLATE}
          />
          <button style={{ ...btnSm, marginTop: 6, color: 'var(--danger)' }} onClick={() => setForm(f => ({ ...f, landingAmenitiesJSON: '' }))}>Reset</button>
        </Card>
      )}

      {/* ── LAYOUT STYLE TAB ── */}
      {activeTab === 'layout' && (
        <Card title="🎨 Landing Page Layout Style">
          <div style={{ fontSize: 12, color: 'var(--textMuted)', marginBottom: 16 }}>
            Choose how your landing page is laid out. Changes apply when you save.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
            {[
              { id: 'classic',  label: 'Classic', desc: 'Fullscreen hero with centered text overlay', icon: '🏔️', preview: 'Full-width hero image, overlaid hotel name & tagline, then sections below' },
              { id: 'modern',   label: 'Modern',  desc: 'Split hero — image left, text right', icon: '🏙️', preview: '50/50 split hero panel, bold typography, clean minimal sections' },
              { id: 'minimal',  label: 'Minimal', desc: 'Text-centered, no hero image', icon: '✦', preview: 'Clean white-space layout, large typography, content-first design' },
              { id: 'luxury',   label: 'Luxury',  desc: 'Magazine style — editorial layout', icon: '👑', preview: 'Gold accents, editorial grid, high-fashion hotel presentation' },
              { id: 'resort',   label: 'Resort',  desc: 'Parallax sections — immersive scroll', icon: '🌴', preview: 'Full-page sections, sticky navigation, parallax scrolling effects' },
            ].map(layout => {
              const isActive = (theme.landingLayout || 'classic') === layout.id;
              return (
                <div key={layout.id} onClick={() => { setTheme({ landingLayout: layout.id }); notify(`Layout changed to ${layout.label} — save to apply`); }}
                  style={{ cursor: 'pointer', border: `2px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
                    borderRadius: 8, padding: 16, background: isActive ? 'var(--primary)10' : 'var(--bgCard)',
                    transition: 'all .15s' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{layout.icon}</div>
                  <div style={{ fontWeight: 700, color: isActive ? 'var(--primary)' : 'var(--textPrimary)', fontSize: 14, marginBottom: 4 }}>
                    {layout.label} {isActive && '✓'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--textMuted)', marginBottom: 6 }}>{layout.desc}</div>
                  <div style={{ fontSize: 10, color: 'var(--textFaint)', fontStyle: 'italic' }}>{layout.preview}</div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 16, padding: '10px 12px', background: 'var(--bgHover)', borderRadius: 4, fontSize: 12, color: 'var(--textMuted)' }}>
            💡 <strong>Current layout:</strong> {(theme.landingLayout || 'classic').charAt(0).toUpperCase() + (theme.landingLayout || 'classic').slice(1)} — visit the landing page to preview your selection.
          </div>
        </Card>
      )}

      {jsonErr && (
        <div style={{ padding: '10px 14px', background: 'var(--bgDanger)', border: '1px solid var(--danger)44', borderRadius: 4, color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
          ⚠️ {jsonErr}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        <button style={{ ...btnPrimary, flex: 2, marginTop: 0, background: saved ? '#2ecc71' : undefined }} onClick={handleSave}>
          {saved ? '✓ Landing Page Updated!' : '💾 Save All Landing Page Changes'}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// ─── v1.3 ADMIN PANEL COMPONENTS ───────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════════════════

// ─── Admin: Complimentary Bills Overview ─────────────────────────────────────
function AdminComplimentaries({ notify }) {
  const bp = useBreakpoint();
  const [bills, setBills]         = useState([]);
  const [filter, setFilter]       = useState('all');
  const [selected, setSelected]   = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const STATUS_COLOR = { pending:'#e67e22', approved:'#2ecc71', rejected:'#e74c3c' };

  const load = async () => {
    try { const r = await api.getComplimentaryBills(); setBills(r.data.results || r.data); } catch {}
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const doApprove = async (b) => {
    try {
      await api.approveComplimentaryBill(b.id);
      notify('✅ Complimentary bill approved & expensed');
      setSelected(null); load();
    } catch(e) { notify(e.response?.data?.error || 'Failed', 'error'); }
  };
  const doReject = async () => {
    if (!rejectModal) return;
    try {
      await api.rejectComplimentaryBill(rejectModal.id, { reason: rejectReason });
      notify('Complimentary bill rejected');
      setRejectModal(null); setRejectReason(''); setSelected(null); load();
    } catch(e) { notify(e.response?.data?.error || 'Failed', 'error'); }
  };

  const filtered = filter === 'all' ? bills : bills.filter(b => b.status === filter);
  const counts   = { all: bills.length, pending: bills.filter(b=>b.status==='pending').length,
                     approved: bills.filter(b=>b.status==='approved').length,
                     rejected: bills.filter(b=>b.status==='rejected').length };
  const total = bills.filter(b=>b.status==='approved').reduce((s,b)=>s+parseFloat(b.amount||0),0);

  return (
    <div>
      <StatCards cards={[
        { label:'Pending Approval', value: counts.pending, icon:'⏳', color:'#e67e22' },
        { label:'Approved', value: counts.approved, icon:'✅', color:'#2ecc71' },
        { label:'Total Expensed (KES)', value: total.toLocaleString('en-KE',{minimumFractionDigits:2}), icon:'💸', color:'var(--accent)' },
        { label:'Total Records', value: counts.all, icon:'📋', color:'#3498db' },
      ]} />

      <div style={{ display:'flex', gap:6, marginBottom:12 }}>
        {Object.entries(counts).map(([k,v]) => (
          <button key={k} onClick={()=>setFilter(k)} style={{
            padding:'5px 14px', borderRadius:4, fontSize:11, fontWeight:600, cursor:'pointer',
            border:`1px solid ${filter===k?'#c8a96e':'var(--border)'}`,
            background:filter===k?'#c8a96e22':'var(--bgInput)',
            color:filter===k?'#c8a96e':'var(--textMuted)',
          }}>{k.charAt(0).toUpperCase()+k.slice(1)} ({v})</button>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns: bp === 'sm' ? '1fr' : selected ? '1fr 380px' : '1fr', gap:16 }}>
        <Card title={`Complimentary Bills (${filtered.length})`}>
          {filtered.length === 0 ? <Empty text="No records found" /> :
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead><tr style={{ background:'var(--bgInput)' }}>
                  {['Ref','Guest','Category','Reason','Amount','Requested By','Status'].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10,
                      fontWeight:700, color:'var(--textMuted)', borderBottom:'1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filtered.map(b => (
                    <tr key={b.id} onClick={()=>setSelected(b)}
                      style={{ borderBottom:'1px solid var(--border)', cursor:'pointer',
                        background:selected?.id===b.id?'var(--bgHover)':'transparent' }}>
                      <td style={{ padding:'8px 10px', fontFamily:'monospace', color:'var(--accent)', fontSize:11 }}>{b.ref}</td>
                      <td style={{ padding:'8px 10px', fontWeight:600, color:'var(--textPrimary)' }}>{b.guest_name}</td>
                      <td style={{ padding:'8px 10px', color:'var(--textMuted)', textTransform:'capitalize', fontSize:11 }}>{b.category?.replace('_',' ')}</td>
                      <td style={{ padding:'8px 10px', color:'var(--textSecond)', maxWidth:180 }}>
                        <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{b.reason?.slice(0,50)}</div>
                      </td>
                      <td style={{ padding:'8px 10px', fontWeight:700, color:'var(--accent)' }}>KES {parseFloat(b.amount||0).toLocaleString()}</td>
                      <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11 }}>{b.requested_by_name}</td>
                      <td style={{ padding:'8px 10px' }}>
                        <span style={{ padding:'2px 8px', borderRadius:3, fontSize:10, fontWeight:700,
                          background:(STATUS_COLOR[b.status]||'#888')+'22', color:STATUS_COLOR[b.status]||'#888',
                          textTransform:'capitalize' }}>{b.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
        </Card>

        {selected && (
          <Card title={`${selected.ref} — Detail`}>
            <button style={{ ...btnSm, marginBottom:12, color:'var(--textMuted)' }} onClick={()=>setSelected(null)}>✕</button>
            {[
              ['Guest', selected.guest_name],
              ['Category', selected.category?.replace('_',' ')],
              ['Amount', `KES ${parseFloat(selected.amount||0).toLocaleString()}`],
              ['Requested By', selected.requested_by_name],
              ['Date', new Date(selected.created_at).toLocaleDateString('en-KE')],
              ['Status', selected.status],
              selected.approved_by_name && ['Approved By', selected.approved_by_name],
            ].filter(Boolean).map(([l,v]) => (
              <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0',
                borderBottom:'1px solid var(--border)', fontSize:12 }}>
                <span style={{ color:'var(--textMuted)' }}>{l}</span>
                <span style={{ fontWeight:600, color:'var(--textPrimary)', textTransform:'capitalize' }}>{v}</span>
              </div>
            ))}
            <div style={{ padding:'10px', background:'var(--bgInput)', borderRadius:4, margin:'12px 0', fontSize:12, color:'var(--textSecond)' }}>
              <div style={{ fontSize:10, color:'var(--textMuted)', marginBottom:4 }}>Reason</div>
              {selected.reason}
            </div>
            {selected.status === 'pending' && (
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <button style={{ ...btnPrimary, flex:1, background:'linear-gradient(135deg,#27ae60,#1e8449)', fontSize:12 }}
                  onClick={() => doApprove(selected)}>✅ Approve & Expense</button>
                <button style={{ ...btnPrimary, flex:1, background:'linear-gradient(135deg,#c0392b,#922b21)', fontSize:12 }}
                  onClick={() => { setRejectModal(selected); setRejectReason(''); }}>❌ Reject</button>
              </div>
            )}
            {selected.expense && (
              <div style={{ marginTop:10, padding:'8px 10px', background:'var(--bgSuccess)', borderRadius:4,
                border:'1px solid #2ecc7133', fontSize:11, color:'#2ecc71' }}>
                ✅ Auto-expensed as Expense #{selected.expense}
              </div>
            )}
            <button style={{ ...btnSm, width:'100%', marginTop:10, background:'var(--bgInput)', color:'var(--textMuted)' }}
              onClick={() => printComplimentaryAuthorization(selected)}>
              🖨️ Print Authorization
            </button>
          </Card>
        )}
      </div>

      {/* Reject modal */}
      {rejectModal && (
        <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div className="modal-inner" style={{ background:'var(--bgCard)', border:'2px solid #e74c3c44', borderRadius:10, padding:24, maxWidth:440, width:'100%' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#e74c3c', marginBottom:6 }}>❌ Reject Complimentary Bill</div>
            <div style={{ fontSize:13, color:'var(--textMuted)', marginBottom:14 }}>
              Rejecting <strong>{rejectModal.ref}</strong> — {rejectModal.guest_name} — KES {parseFloat(rejectModal.amount||0).toLocaleString()}
            </div>
            <label style={{ fontSize:12, color:'var(--textMuted)', display:'block', marginBottom:6 }}>Rejection reason (optional)</label>
            <textarea
              style={{ width:'100%', padding:'10px 12px', background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:6, color:'var(--textPrimary)', fontSize:13, resize:'vertical', minHeight:80, boxSizing:'border-box' }}
              value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder="Explain why this complimentary is being rejected…"
            />
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:14 }}>
              <button onClick={() => { setRejectModal(null); setRejectReason(''); }} style={{ padding:'10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bgInput)', color:'var(--textMuted)', cursor:'pointer', fontWeight:600 }}>Cancel</button>
              <button onClick={doReject} style={{ padding:'10px', borderRadius:6, border:'none', background:'#e74c3c', color:'#fff', cursor:'pointer', fontWeight:700 }}>Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Admin: Gate Log & Export ─────────────────────────────────────────────────
function AdminGateLog({ notify }) {
  const bp = useBreakpoint();
  const [visitors, setVisitors]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);
  const [filter, setFilter]         = useState('all');   // all | in | out
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch]         = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [selected, setSelected]     = useState(null);

  const TYPE_LABELS = {
    guest:'Hotel Guest', visitor:'Visitor', conference:'Conference Attendee',
    delivery:'Delivery / Supplier', staff:'Staff', other:'Other',
  };

  const load = async () => {
    setLoading(true);
    try {
      let params = '?';
      if (filter !== 'all') params += `status=${filter}&`;
      if (typeFilter) params += `visitor_type=${typeFilter}&`;
      if (dateFrom) params += `date=${dateFrom}&`;
      const r = await api.getGateVisitors(params);
      setVisitors(r.data.results || r.data || []);
    } catch { notify('Failed to load gate log', 'error'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter, typeFilter, dateFrom]);

  const fmtDur = (v) => {
    if (!v.time_in || !v.time_out) return null;
    const mins = Math.round((new Date(v.time_out) - new Date(v.time_in)) / 60000);
    const h = Math.floor(mins / 60), m = mins % 60;
    return h ? `${h}h ${m}m` : `${m}m`;
  };

  const doExport = async () => {
    setExporting(true);
    notify('Preparing gate log export…');
    try {
      let params = '?';
      if (dateFrom)    params += `date_from=${dateFrom}&`;
      if (dateTo)      params += `date_to=${dateTo}&`;
      if (typeFilter)  params += `visitor_type=${typeFilter}&`;
      if (filter !== 'all') params += `status=${filter}&`;
      const res = await axiosClient.get(api.exportGateLogExcel(params), { responseType: 'blob', baseURL: '' });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 50) throw new Error('Empty file returned');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_GateLog_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      notify('Gate log exported ✓');
    } catch(e) { notify(`Export failed: ${await readBlobError(e)}`, 'error'); }
    finally { setExporting(false); }
  };

  // Computed stats
  const inside    = visitors.filter(v => v.status === 'in');
  const exited    = visitors.filter(v => v.status === 'out');
  const withCar   = visitors.filter(v => v.car_plate);
  const avgDurMins = (() => {
    const withDur = exited.filter(v => v.time_in && v.time_out);
    if (!withDur.length) return 0;
    const total = withDur.reduce((s,v) => s + Math.round((new Date(v.time_out) - new Date(v.time_in)) / 60000), 0);
    return Math.round(total / withDur.length);
  })();

  // Search filter (client-side)
  const displayed = visitors.filter(v => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (v.name||'').toLowerCase().includes(q) ||
           (v.id_number||'').toLowerCase().includes(q) ||
           (v.car_plate||'').toLowerCase().includes(q) ||
           (v.phone||'').toLowerCase().includes(q) ||
           (v.purpose||'').toLowerCase().includes(q);
  });

  const SCOLOR = { in:'#22c55e', out:'#94a3b8' };

  return (
    <div>
      <PageHeader icon="🔐" title="Gate Visitor Log" sub="Full visitor history, time-tracking analytics, and Excel export" />

      {/* Stats */}
      <StatCards cards={[
        { label:'Currently Inside',    value:inside.length,        color:'#22c55e', icon:'🟢' },
        { label:'Total Records',       value:visitors.length,      color:'#3b82f6', icon:'📋' },
        { label:'With Vehicles',       value:withCar.length,       color:'#f59e0b', icon:'🚗' },
        { label:'Avg Time on Premise', value:avgDurMins > 0 ? `${Math.floor(avgDurMins/60)}h ${avgDurMins%60}m` : '—', color:'#8b5cf6', icon:'⏱️' },
      ]} />

      {/* Filters + Export bar */}
      <Card title="Filter & Export">
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:14 }}>
          <Field label="Status">
            <select style={inputStyle} value={filter} onChange={e=>setFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="in">Currently Inside</option>
              <option value="out">Exited</option>
            </select>
          </Field>
          <Field label="Visitor Type">
            <select style={inputStyle} value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
              <option value="">All Types</option>
              {Object.entries(TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Date From">
            <input type="date" style={inputStyle} value={dateFrom} onChange={e=>setDateFrom(e.target.value)} />
          </Field>
          <Field label="Date To (export)">
            <input type="date" style={inputStyle} value={dateTo} onChange={e=>setDateTo(e.target.value)} />
          </Field>
          <Field label="Search">
            <input style={inputStyle} value={search} onChange={e=>setSearch(e.target.value)} placeholder="Name, ID, plate, phone…" />
          </Field>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <button onClick={load} style={{ ...btnSm, background:'var(--bgInput)', border:'1px solid var(--border)', color:'var(--textMuted)' }}>↻ Refresh</button>
          <button onClick={doExport} disabled={exporting} style={{ ...btnPrimary, flex:1, minWidth:180 }}>
            {exporting ? '⏳ Exporting…' : '⬇ Export to Excel'}
          </button>
        </div>
        <div style={{ marginTop:10, padding:'8px 12px', background:'var(--bgInput)', borderRadius:6, fontSize:11, color:'var(--textMuted)', lineHeight:1.7 }}>
          Export includes: visitor name, ID, phone, vehicle plate, visitor type, purpose, host/dept,<br/>
          <strong>time in, time out, duration on premises</strong>, gate officer who logged entry, and officer who recorded exit.
        </div>
      </Card>

      {/* Type breakdown */}
      <Card title="Visitor Breakdown by Type">
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:10 }}>
          {Object.entries(TYPE_LABELS).map(([k,v]) => {
            const count = visitors.filter(vis => vis.visitor_type === k).length;
            if (!count) return null;
            const pct = visitors.length ? Math.round(count/visitors.length*100) : 0;
            return (
              <div key={k} style={{ background:'var(--bgInput)', borderRadius:8, padding:'12px 14px', textAlign:'center', border:'1px solid var(--border)' }}>
                <div style={{ fontSize:20, fontWeight:800, color:'var(--primary)' }}>{count}</div>
                <div style={{ fontSize:10, color:'var(--textMuted)', marginTop:2 }}>{v}</div>
                <div style={{ marginTop:6, height:4, background:'var(--border)', borderRadius:4, overflow:'hidden' }}>
                  <div style={{ width:`${pct}%`, height:'100%', background:'var(--primary)', borderRadius:4 }} />
                </div>
                <div style={{ fontSize:9, color:'var(--textFaint)', marginTop:3 }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Log table + detail pane */}
      <div style={{ display:'grid', gridTemplateColumns: selected ? (bp==='sm'?'1fr':'1fr 340px') : '1fr', gap:14 }} className="layout-sidebar">
        <Card title={`Visitor Log (${displayed.length} records${search ? ' — filtered' : ''})`}>
          {loading ? <div style={{ textAlign:'center', padding:30, color:'var(--textMuted)' }}>Loading…</div> :
           displayed.length === 0 ? <div style={{ textAlign:'center', padding:30, color:'var(--textFaint)' }}>No records match your filters</div> : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid var(--border)' }}>
                    {['#','Name','ID','Plate','Type','Purpose','Host','Time In','Time Out','Duration','By','Status'].map(h => (
                      <th key={h} style={{ padding:'8px 8px', textAlign:'left', fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:0.5, whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayed.map((v,i) => {
                    const dur = fmtDur(v);
                    const isSelected = selected?.id === v.id;
                    return (
                      <tr key={v.id} onClick={() => setSelected(isSelected ? null : v)} style={{
                        borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background .12s',
                        background: isSelected ? 'var(--bgHover)' : i%2===0 ? 'transparent' : 'rgba(0,0,0,0.012)',
                      }}>
                        <td style={{ padding:'8px 8px', color:'#3b82f6', fontWeight:700, fontFamily:'monospace', fontSize:11 }}>{v.id}</td>
                        <td style={{ padding:'8px 8px', fontWeight:600, color:'var(--textPrimary)', whiteSpace:'nowrap', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis' }}>{v.name}</td>
                        <td style={{ padding:'8px 8px', color:'var(--textMuted)', fontFamily:'monospace', fontSize:11, whiteSpace:'nowrap' }}>{v.id_number}</td>
                        <td style={{ padding:'8px 8px', fontFamily:'monospace', fontWeight:700, color:'#c45e3e', letterSpacing:1, whiteSpace:'nowrap' }}>{v.car_plate || '—'}</td>
                        <td style={{ padding:'8px 8px', color:'var(--textMuted)', fontSize:11, whiteSpace:'nowrap' }}>{TYPE_LABELS[v.visitor_type]||v.visitor_type}</td>
                        <td style={{ padding:'8px 8px', color:'var(--textSecond)', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v.purpose||'—'}</td>
                        <td style={{ padding:'8px 8px', color:'var(--textMuted)', fontSize:11, whiteSpace:'nowrap' }}>{v.host_name||'—'}</td>
                        <td style={{ padding:'8px 8px', color:'var(--textPrimary)', whiteSpace:'nowrap', fontSize:11 }}>
                          {v.time_in ? new Date(v.time_in).toLocaleString('en-KE',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}
                        </td>
                        <td style={{ padding:'8px 8px', color: v.time_out ? 'var(--textSecond)' : 'var(--textFaint)', whiteSpace:'nowrap', fontSize:11 }}>
                          {v.time_out ? new Date(v.time_out).toLocaleString('en-KE',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}
                        </td>
                        <td style={{ padding:'8px 8px', color: dur ? '#8b5cf6' : 'var(--textFaint)', fontWeight: dur ? 700 : 400, whiteSpace:'nowrap', fontSize:11 }}>
                          {dur || (v.status==='in' ? '⏳ Inside' : '—')}
                        </td>
                        <td style={{ padding:'8px 8px', color:'var(--textFaint)', fontSize:10, whiteSpace:'nowrap' }}>{v.logged_by_name||'—'}</td>
                        <td style={{ padding:'8px 8px', whiteSpace:'nowrap' }}>
                          <span style={{ fontSize:10, fontWeight:700, borderRadius:4, padding:'3px 8px', background:v.status==='in'?'rgba(34,197,94,0.12)':'rgba(148,163,184,0.12)', color:SCOLOR[v.status]||'#888', textTransform:'uppercase', letterSpacing:0.5 }}>
                            {v.status === 'in' ? '🟢 Inside' : '✓ Exited'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Detail pane */}
        {selected && (
          <Card title={`Pass #${selected.id} — Detail`}>
            <button onClick={() => setSelected(null)} style={{ ...btnSm, marginBottom:12, background:'var(--bgInput)', color:'var(--textMuted)' }}>✕ Close</button>
            
            {/* Status badge */}
            <div style={{ textAlign:'center', marginBottom:14 }}>
              <span style={{ fontSize:12, fontWeight:700, borderRadius:6, padding:'6px 18px',
                background:selected.status==='in'?'rgba(34,197,94,0.12)':'rgba(148,163,184,0.1)',
                color:selected.status==='in'?'#22c55e':'#94a3b8', textTransform:'uppercase', letterSpacing:1 }}>
                {selected.status === 'in' ? '🟢 Currently Inside' : '✓ Exited Premises'}
              </span>
            </div>

            {[
              ['Name',         selected.name],
              ['ID / Passport',selected.id_number],
              selected.phone   ? ['Phone',        selected.phone]    : null,
              selected.car_plate? ['Vehicle Plate', selected.car_plate] : null,
              ['Visitor Type', TYPE_LABELS[selected.visitor_type]||selected.visitor_type],
              selected.purpose ? ['Purpose',       selected.purpose]  : null,
              selected.host_name?['Host / Dept',   selected.host_name]: null,
              ['Time In',      selected.time_in ? new Date(selected.time_in).toLocaleString('en-KE') : '—'],
              ['Time Out',     selected.time_out ? new Date(selected.time_out).toLocaleString('en-KE') : '—'],
              ['Duration',     fmtDur(selected) || (selected.status==='in' ? 'Still on premises' : '—')],
              ['Logged By',    selected.logged_by_name || '—'],
              ['Exit Scan By', selected.exit_scanned_by_name || '—'],
              selected.notes   ? ['Notes',         selected.notes]    : null,
            ].filter(Boolean).map(([k,v]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between', gap:8, borderBottom:'1px solid var(--border)', padding:'7px 0', fontSize:12 }}>
                <span style={{ color:'var(--textMuted)', fontWeight:600, flexShrink:0, fontSize:11 }}>{k}</span>
                <span style={{ color: k==='Vehicle Plate'?'#c45e3e':k==='Duration'?'#8b5cf6':'var(--textPrimary)',
                  fontWeight: k==='Vehicle Plate'||k==='Duration'?700:400,
                  fontFamily: k==='Vehicle Plate'?'monospace':'inherit',
                  letterSpacing: k==='Vehicle Plate'?2:0,
                  textAlign:'right', wordBreak:'break-word', fontSize:12 }}>{v}</span>
              </div>
            ))}

            {/* Duration bar if exited */}
            {selected.time_in && selected.time_out && (() => {
              const mins = Math.round((new Date(selected.time_out) - new Date(selected.time_in)) / 60000);
              const pct = Math.min(100, Math.round(mins / 480 * 100)); // 480 = 8hr reference
              return (
                <div style={{ marginTop:12 }}>
                  <div style={{ fontSize:10, color:'var(--textMuted)', marginBottom:5, textTransform:'uppercase', letterSpacing:0.5 }}>Time on Premises</div>
                  <div style={{ height:8, background:'var(--border)', borderRadius:4, overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:'#8b5cf6', borderRadius:4 }} />
                  </div>
                  <div style={{ fontSize:10, color:'#8b5cf6', marginTop:3, fontWeight:700 }}>{fmtDur(selected)}</div>
                </div>
              );
            })()}
          </Card>
        )}
      </div>
    </div>
  );
}


// ─── Admin: Donations ─────────────────────────────────────────────────────────
function AdminDonations({ notify }) {
  const [donations, setDonations] = useState([]);
  const [form, setForm] = useState({
    donor_name:'', donor_email:'', category:'general', amount_kes:'',
    currency:'KES', amount_original:'', exchange_rate:1, payment_mode:'cash',
    transaction_ref:'', notes:'',
  });
  const [currencies, setCurrencies] = useState([]);
  const [showForm, setShowForm]     = useState(false);

  const load = async () => {
    try {
      const [d, c] = await Promise.all([api.getDonations(), api.getCurrencyRates()]);
      setDonations(d.data.results || d.data);
      setCurrencies(c.data.results || c.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const totalKes = donations.reduce((s,d)=>s+parseFloat(d.amount_kes||0),0);

  const CATEGORIES = [
    { id:'wildlife','label':'🦁 Wildlife Conservation' },
    { id:'reforestation','label':'🌳 Reforestation' },
    { id:'community','label':'🤝 Community Programme' },
    { id:'ocean','label':'🌊 Ocean / Water Conservation' },
    { id:'general','label':'🌿 General Conservation' },
    { id:'other','label':'📋 Other' },
  ];

  const save = async () => {
    if (!form.donor_name || !form.amount_kes) { notify('Donor name and amount required','error'); return; }
    try {
      await api.createDonation({
        ...form,
        amount_kes: parseFloat(form.amount_kes),
        amount_original: parseFloat(form.amount_original || form.amount_kes),
        exchange_rate: parseFloat(form.exchange_rate || 1),
      });
      notify('✅ Donation recorded!');
      setShowForm(false);
      setForm({ donor_name:'', donor_email:'', category:'general', amount_kes:'', currency:'KES',
        amount_original:'', exchange_rate:1, payment_mode:'cash', transaction_ref:'', notes:'' });
      load();
    } catch(e) { notify('Failed to record donation','error'); }
  };

  const handleCurrencyChange = (code) => {
    const rate = currencies.find(c=>c.code===code);
    setForm(f => ({
      ...f, currency: code,
      exchange_rate: rate ? parseFloat(rate.rate_to_kes) : 1,
    }));
  };

  const handleOriginalAmountChange = (val) => {
    const kes = parseFloat(val || 0) * parseFloat(form.exchange_rate || 1);
    setForm(f => ({ ...f, amount_original: val, amount_kes: kes.toFixed(2) }));
  };

  return (
    <div>
      <StatCards cards={[
        { label:'Total Donations (KES)', value: totalKes.toLocaleString('en-KE',{minimumFractionDigits:2}), icon:'🌿', color:'#2ecc71' },
        { label:'Total Records', value: donations.length, icon:'📋', color:'var(--accent)' },
      ]} />

      <button style={{ ...btnPrimary, marginBottom:16, width:'auto' }} onClick={()=>setShowForm(!showForm)}>
        {showForm ? '✕ Cancel' : '+ Record Donation'}
      </button>

      {showForm && (
        <Card title="Record New Donation" style={{ marginBottom:16 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
            <Field label="Donor Name *"><input style={inputStyle} value={form.donor_name} onChange={e=>setForm(f=>({...f,donor_name:e.target.value}))} placeholder="John Doe" /></Field>
            <Field label="Email"><input style={inputStyle} value={form.donor_email} onChange={e=>setForm(f=>({...f,donor_email:e.target.value}))} placeholder="donor@email.com" /></Field>
            <Field label="Category">
              <select style={inputStyle} value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Currency">
              <select style={inputStyle} value={form.currency} onChange={e=>handleCurrencyChange(e.target.value)}>
                <option value="KES">KES — Kenyan Shilling</option>
                {currencies.filter(c=>c.is_active).map(c=>(
                  <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                ))}
              </select>
            </Field>
            {form.currency !== 'KES' ? (
              <>
                <Field label={`Amount (${form.currency})`}>
                  <input style={inputStyle} type="number" value={form.amount_original}
                    onChange={e=>handleOriginalAmountChange(e.target.value)} placeholder="100.00" />
                </Field>
                <Field label={`Rate (1 ${form.currency} = ? KES)`}>
                  <input style={inputStyle} type="number" value={form.exchange_rate}
                    onChange={e=>{ setForm(f=>({...f,exchange_rate:e.target.value})); }} />
                </Field>
                <Field label="Equivalent KES">
                  <input style={{ ...inputStyle, background:'#1a1a1a' }} readOnly value={form.amount_kes} />
                </Field>
              </>
            ) : (
              <Field label="Amount (KES) *"><input style={inputStyle} type="number" value={form.amount_kes} onChange={e=>setForm(f=>({...f,amount_kes:e.target.value,amount_original:e.target.value}))} placeholder="5000" /></Field>
            )}
            <Field label="Payment Mode">
              <select style={inputStyle} value={form.payment_mode} onChange={e=>setForm(f=>({...f,payment_mode:e.target.value}))}>
                {['cash','mpesa','bank','card','cheque'].map(m=><option key={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Transaction Ref"><input style={inputStyle} value={form.transaction_ref} onChange={e=>setForm(f=>({...f,transaction_ref:e.target.value}))} placeholder="M-Pesa code, receipt no." /></Field>
            <Field label="Notes" style={{ gridColumn:'1/-1' }}>
              <textarea style={{ ...inputStyle, minHeight:60 }} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} />
            </Field>
          </div>
          <button style={{ ...btnPrimary, marginTop:12 }} onClick={save}>✓ Record Donation</button>
        </Card>
      )}

      <Card title={`Donation Log (${donations.length})`}>
        {donations.length === 0 ? <Empty text="No donations recorded yet" /> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead><tr style={{ background:'var(--bgInput)' }}>
                {['Ref','Donor','Category','Currency','Amount','KES Equiv.','Method','Date','By'].map(h=>(
                  <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, fontWeight:700, color:'var(--textMuted)', borderBottom:'1px solid var(--border)' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {donations.map(d => (
                  <tr key={d.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'8px 10px', fontFamily:'monospace', color:'#2ecc71', fontSize:11 }}>{d.ref}</td>
                    <td style={{ padding:'8px 10px', fontWeight:600, color:'var(--textPrimary)' }}>{d.donor_name}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textMuted)', textTransform:'capitalize', fontSize:11 }}>{d.category?.replace('_',' ')}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textSecond)' }}>{d.currency}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textSecond)' }}>
                      {d.currency !== 'KES' ? `${d.currency} ${parseFloat(d.amount_original||0).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ padding:'8px 10px', fontWeight:700, color:'#2ecc71' }}>KES {parseFloat(d.amount_kes||0).toLocaleString()}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textMuted)', textTransform:'capitalize' }}>{d.payment_mode}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11 }}>{new Date(d.donated_at).toLocaleDateString('en-KE')}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11 }}>{d.recorded_by_name}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background:'var(--bgInput)', fontWeight:700 }}>
                  <td colSpan={5} style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11 }}>Total</td>
                  <td colSpan={4} style={{ padding:'8px 10px', color:'#2ecc71', fontWeight:700 }}>
                    KES {totalKes.toLocaleString('en-KE',{minimumFractionDigits:2})}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}


// ─── Admin: Currency Rates ─────────────────────────────────────────────────────
function AdminCurrencies({ notify }) {
  const [rates, setRates]     = useState([]);
  const [form, setForm]       = useState({ code:'', name:'', rate_to_kes:'', symbol:'$', is_active:true });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);

  const load = async () => {
    try { const r = await api.getCurrencyRates(); setRates(r.data.results||r.data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const PRESETS = [
    { code:'USD', name:'US Dollar',    rate_to_kes:129.5,  symbol:'$' },
    { code:'EUR', name:'Euro',         rate_to_kes:140.2,  symbol:'€' },
    { code:'GBP', name:'British Pound',rate_to_kes:163.8,  symbol:'£' },
    { code:'CHF', name:'Swiss Franc',  rate_to_kes:148.1,  symbol:'Fr' },
    { code:'CNY', name:'Chinese Yuan', rate_to_kes:17.9,   symbol:'¥' },
    { code:'INR', name:'Indian Rupee', rate_to_kes:1.55,   symbol:'₹' },
    { code:'ZAR', name:'South African Rand', rate_to_kes:7.1, symbol:'R' },
    { code:'UGX', name:'Ugandan Shilling', rate_to_kes:0.034, symbol:'Ush' },
    { code:'TZS', name:'Tanzanian Shilling', rate_to_kes:0.049, symbol:'TSh' },
  ];

  const save = async () => {
    if (!form.code || !form.rate_to_kes) { notify('Code and rate required','error'); return; }
    try {
      if (editing) { await api.updateCurrencyRate(editing.id, form); notify('Rate updated'); }
      else { await api.createCurrencyRate(form); notify('Currency added'); }
      setShowForm(false); setEditing(null);
      setForm({ code:'', name:'', rate_to_kes:'', symbol:'$', is_active:true });
      load();
    } catch(e) { notify(e.response?.data?.code?.[0] || 'Failed','error'); }
  };

  const del = async (id) => {
    try { await api.deleteCurrencyRate(id); load(); notify('Removed'); } catch {}
  };

  return (
    <div>
      <div style={{ padding:'10px 14px', background:'var(--bgHover)', border:'1px solid #3498db33', borderRadius:6, marginBottom:16, fontSize:12, color:'#3498db' }}>
        💱 Set exchange rates against <strong>KES (Kenyan Shilling)</strong>. These rates are used for multi-currency payments, donations, and financial reports.
        <span style={{ color:'var(--textMuted)', marginLeft:8 }}>Update rates regularly for accuracy.</span>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <button style={{ ...btnPrimary, width:'auto' }} onClick={()=>{ setShowForm(!showForm); setEditing(null); setForm({ code:'', name:'', rate_to_kes:'', symbol:'$', is_active:true }); }}>
          {showForm && !editing ? '✕ Cancel' : '+ Add Currency'}
        </button>
        <div style={{ fontSize:11, color:'var(--textMuted)', alignSelf:'center' }}>Quick add:</div>
        {PRESETS.filter(p => !rates.find(r=>r.code===p.code)).slice(0,5).map(p => (
          <button key={p.code} onClick={() => setForm(p) || setShowForm(true)} style={{
            padding:'5px 12px', borderRadius:4, background:'var(--bgInput)', color:'var(--textSecond)',
            border:'1px solid var(--border)', fontSize:11, cursor:'pointer' }}>
            {p.symbol} {p.code}
          </button>
        ))}
      </div>

      {showForm && (
        <Card title={editing ? 'Edit Rate' : 'Add Currency'} style={{ marginBottom:16 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10 }}>
            <Field label="Currency Code (e.g. USD)"><input style={inputStyle} value={form.code} onChange={e=>setForm(f=>({...f,code:e.target.value.toUpperCase()}))} placeholder="USD" maxLength={10} /></Field>
            <Field label="Currency Name"><input style={inputStyle} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="US Dollar" /></Field>
            <Field label="Symbol"><input style={inputStyle} value={form.symbol} onChange={e=>setForm(f=>({...f,symbol:e.target.value}))} placeholder="$" maxLength={5} /></Field>
            <Field label="Rate (1 unit = X KES)"><input style={inputStyle} type="number" step="0.0001" value={form.rate_to_kes} onChange={e=>setForm(f=>({...f,rate_to_kes:e.target.value}))} placeholder="129.50" /></Field>
            <div style={{ alignSelf:'flex-end' }}><button style={btnPrimary} onClick={save}>✓ Save</button></div>
          </div>
        </Card>
      )}

      <Card title={`Exchange Rates — Base: KES (${rates.length} currencies)`}>
        {rates.length === 0 ? <Empty text="No currencies configured. Add USD, EUR, GBP to enable multi-currency payments." /> : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead><tr style={{ background:'var(--bgInput)' }}>
              {['Code','Name','Symbol','1 Unit = KES','Active','Updated','Actions'].map(h=>(
                <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, fontWeight:700, color:'var(--textMuted)', borderBottom:'1px solid var(--border)' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rates.map(r => (
                <tr key={r.id} style={{ borderBottom:'1px solid var(--border)' }}>
                  <td style={{ padding:'8px 10px', fontWeight:800, color:'var(--accent)', fontFamily:'monospace' }}>{r.code}</td>
                  <td style={{ padding:'8px 10px', color:'var(--textPrimary)' }}>{r.name}</td>
                  <td style={{ padding:'8px 10px', color:'var(--textSecond)', fontFamily:'monospace' }}>{r.symbol}</td>
                  <td style={{ padding:'8px 10px', fontWeight:700, color:'#2ecc71' }}>
                    {r.symbol} 1 = KES {parseFloat(r.rate_to_kes).toFixed(4)}
                  </td>
                  <td style={{ padding:'8px 10px' }}>
                    <span style={{ color: r.is_active?'#2ecc71':'#e74c3c', fontSize:12, fontWeight:700 }}>
                      {r.is_active ? '● Active' : '● Off'}
                    </span>
                  </td>
                  <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11 }}>
                    {new Date(r.updated_at).toLocaleDateString('en-KE')}
                  </td>
                  <td style={{ padding:'8px 10px' }}>
                    <div style={{ display:'flex', gap:6 }}>
                      <button style={{ ...btnSm, fontSize:10 }} onClick={() => {
                        setEditing(r);
                        setForm({ code:r.code, name:r.name, rate_to_kes:r.rate_to_kes, symbol:r.symbol, is_active:r.is_active });
                        setShowForm(true);
                      }}>✏️ Edit</button>
                      <button style={{ ...btnSm, background:'#5c1a1a', fontSize:10 }} onClick={()=>del(r.id)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}


// ─── Admin: Leave Requests ────────────────────────────────────────────────────
function AdminLeaveRequests({ notify }) {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter]     = useState('pending');
  const [activeTab, setActiveTab] = useState('manage'); // 'manage' | 'reports'
  const [hrNotes, setHrNotes]   = useState({});
  const [search, setSearch]     = useState('');

  const load = useCallback(async () => {
    try { const r = await api.getLeaveRequests(); setRequests(r.data.results || r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const doApprove = async (lr) => {
    try {
      await api.approveLeaveRequest(lr.id, { hr_notes: hrNotes[lr.id] || 'Approved by Admin' });
      notify(`✅ Leave approved — ${lr.employee_name}'s account deactivated`);
      load();
    } catch(e) { notify(e.response?.data?.error || 'Failed', 'error'); }
  };

  const doReject = async (lr) => {
    try {
      await api.rejectLeaveRequest(lr.id, { reason: hrNotes[lr.id] || 'Rejected by Admin' });
      notify('Rejected');
      load();
    } catch {}
  };

  const doReactivate = async (lr) => {
    try {
      await api.reactivateEmployee(lr.id);
      notify(`✅ ${lr.employee_name} reactivated`);
      load();
    } catch {}
  };

  const STATUS_COLOR = { pending:'#e67e22', approved:'#2ecc71', rejected:'#e74c3c', cancelled:'#888' };
  const STATUS_ICON  = { pending:'⏳', approved:'✅', rejected:'❌', cancelled:'🚫' };
  const LEAVE_TYPES  = { annual:'Annual Leave', sick:'Sick Day', emergency:'Emergency', unpaid:'Unpaid', maternity:'Maternity/Paternity', other:'Other' };
  const LEAVE_ICONS  = { annual:'🏖️', sick:'🏥', emergency:'🚨', unpaid:'💸', maternity:'👶', other:'📋' };

  const searchLower = search.toLowerCase();
  const filteredBase = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const filtered = search
    ? filteredBase.filter(r =>
        (r.employee_name||'').toLowerCase().includes(searchLower) ||
        (r.employee_role||'').toLowerCase().includes(searchLower) ||
        (r.leave_type||'').toLowerCase().includes(searchLower) ||
        (r.reason||'').toLowerCase().includes(searchLower)
      )
    : filteredBase;

  const counts = {
    all: requests.length,
    pending:   requests.filter(r=>r.status==='pending').length,
    approved:  requests.filter(r=>r.status==='approved').length,
    rejected:  requests.filter(r=>r.status==='rejected').length,
    cancelled: requests.filter(r=>r.status==='cancelled').length,
  };

  // ── Reports data ──
  const totalDays   = requests.reduce((s,r) => s + (r.days||0), 0);
  const approvedDays = requests.filter(r=>r.status==='approved').reduce((s,r)=>s+(r.days||0),0);

  // By leave type
  const byType = Object.entries(
    requests.reduce((acc, r) => {
      const k = r.leave_type||'other';
      acc[k] = (acc[k]||0) + 1;
      return acc;
    }, {})
  ).sort((a,b)=>b[1]-a[1]);

  // By status distribution
  const statusDist = ['pending','approved','rejected','cancelled'].map(s => ({
    label: s.charAt(0).toUpperCase()+s.slice(1),
    value: counts[s],
    color: STATUS_COLOR[s],
  }));

  // By employee (top leave-takers)
  const byEmployee = Object.entries(
    requests.reduce((acc, r) => {
      const k = r.employee_name||'Unknown';
      if (!acc[k]) acc[k] = { name:k, role:r.employee_role||'', total:0, days:0, approved:0 };
      acc[k].total++;
      acc[k].days += r.days||0;
      if (r.status==='approved') acc[k].approved++;
      return acc;
    }, {})
  ).map(([,v])=>v).sort((a,b)=>b.days-a.days);

  // By month (trend)
  const byMonth = Object.entries(
    requests.reduce((acc, r) => {
      if (!r.created_at) return acc;
      const m = r.created_at.slice(0,7);
      if (!acc[m]) acc[m] = { month:m, total:0, approved:0, days:0 };
      acc[m].total++;
      acc[m].days += r.days||0;
      if (r.status==='approved') acc[m].approved++;
      return acc;
    }, {})
  ).map(([,v])=>v).sort((a,b)=>a.month.localeCompare(b.month)).slice(-12);

  const maxMonthTotal = Math.max(...byMonth.map(m=>m.total), 1);
  const maxEmpDays    = Math.max(...byEmployee.map(e=>e.days), 1);
  const maxTypeCount  = Math.max(...byType.map(([,v])=>v), 1);

  const tabStyle = (t) => ({
    padding:'8px 20px', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer',
    border:`1px solid ${activeTab===t?'#c8a96e':'var(--border)'}`,
    background:activeTab===t?'#c8a96e':'var(--bgCard)',
    color:activeTab===t?'#1a1200':'var(--textMuted)',
    transition:'all .15s',
  });

  return (
    <div>
      {/* ── Top summary stats ── */}
      <StatCards cards={[
        { label:'Pending Review',    value: counts.pending,   icon:'⏳', color:'#e67e22' },
        { label:'Currently On Leave',value: counts.approved,  icon:'🏖️', color:'#2ecc71' },
        { label:'Total Requests',    value: counts.all,       icon:'📋', color:'#3498db' },
        { label:'Total Days Taken',  value: approvedDays,     icon:'📅', color:'#c8a96e' },
      ]} />

      {/* ── Tab switcher ── */}
      <div style={{ display:'flex', gap:8, marginBottom:18 }}>
        <button style={tabStyle('manage')} onClick={()=>setActiveTab('manage')}>
          🗂️ Manage Requests
        </button>
        <button style={tabStyle('reports')} onClick={()=>setActiveTab('reports')}>
          📊 Leave Reports
        </button>
      </div>

      {/* ══════════ MANAGE TAB ══════════ */}
      {activeTab === 'manage' && (
        <div>
          {/* Filters + Search */}
          <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
            {['all','pending','approved','rejected','cancelled'].map(k => (
              <button key={k} onClick={()=>setFilter(k)} style={{
                padding:'5px 14px', borderRadius:4, fontSize:11, fontWeight:600, cursor:'pointer',
                border:`1px solid ${filter===k?'#c8a96e':'var(--border)'}`,
                background:filter===k?'#c8a96e22':'var(--bgInput)',
                color:filter===k?'#c8a96e':'var(--textMuted)',
              }}>
                {STATUS_ICON[k]||'📋'} {k.charAt(0).toUpperCase()+k.slice(1)}
                {counts[k]!==undefined && <span style={{ marginLeft:4, opacity:.7 }}>({counts[k]})</span>}
              </button>
            ))}
            <input
              type="text"
              placeholder="🔍 Search employee / type / reason…"
              value={search}
              onChange={e=>setSearch(e.target.value)}
              style={{ ...inputStyle, flex:1, minWidth:180, fontSize:11, padding:'5px 10px' }}
            />
          </div>

          <Card title={`Leave Requests — ${filter.charAt(0).toUpperCase()+filter.slice(1)} (${filtered.length})`}>
            {filtered.length === 0 ? <Empty text="No requests match the current filter" /> : filtered.map(lr => (
              <div key={lr.id} style={{
                padding:'14px 16px', borderBottom:'1px solid var(--border)',
                borderLeft:`3px solid ${STATUS_COLOR[lr.status]||'#888'}`,
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:220 }}>
                    {/* Employee + type */}
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5, flexWrap:'wrap' }}>
                      <span style={{ fontSize:16 }}>{LEAVE_ICONS[lr.leave_type]||'📋'}</span>
                      <span style={{ fontWeight:700, color:'var(--textPrimary)', fontSize:13 }}>{lr.employee_name}</span>
                      <span style={{ fontSize:10, color:'var(--textMuted)', background:'var(--bgInput)', padding:'1px 7px', borderRadius:10 }}>{lr.employee_role}</span>
                      <span style={{ padding:'2px 8px', borderRadius:10, fontSize:10, fontWeight:700,
                        background:(STATUS_COLOR[lr.status]||'#888')+'22', color:STATUS_COLOR[lr.status]||'#888',
                        border:`1px solid ${(STATUS_COLOR[lr.status]||'#888')}44` }}>
                        {STATUS_ICON[lr.status]} {lr.status}
                      </span>
                    </div>

                    {/* Dates & type */}
                    <div style={{ fontSize:11, color:'var(--textSecond)', marginBottom:3 }}>
                      <strong style={{ color:'#c8a96e' }}>{LEAVE_TYPES[lr.leave_type]||lr.leave_type}</strong>
                      {' · '}{lr.date_from} → {lr.date_to}
                      <span style={{ marginLeft:8, color:'#c8a96e', fontWeight:700 }}>{lr.days} day{lr.days!==1?'s':''}</span>
                    </div>

                    {/* Reason */}
                    <div style={{ fontSize:11, color:'var(--textMuted)', fontStyle:'italic', marginBottom:4 }}>
                      "{lr.reason?.slice(0,120)}{lr.reason?.length>120?'…':''}"
                    </div>

                    {/* HR Notes / reviewer */}
                    {lr.hr_notes && (
                      <div style={{ fontSize:11, color:'var(--textMuted)', padding:'5px 10px',
                        background:'#1a1a1a', borderRadius:4, marginTop:4,
                        borderLeft:`3px solid ${STATUS_COLOR[lr.status]||'#888'}` }}>
                        <span style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase' }}>HR Note: </span>
                        {lr.hr_notes}
                      </div>
                    )}
                    {lr.reviewed_by_name && (
                      <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:5 }}>
                        Reviewed by <strong>{lr.reviewed_by_name}</strong>
                        {lr.reviewed_at ? ` · ${new Date(lr.reviewed_at).toLocaleDateString('en-KE')}` : ''}
                      </div>
                    )}
                    <div style={{ fontSize:10, color:'#444', marginTop:2 }}>
                      Submitted: {new Date(lr.created_at).toLocaleDateString('en-KE')}
                    </div>
                    {lr.credentials_deactivated && (
                      <div style={{ fontSize:10, color:'#e74c3c', marginTop:3 }}>🔒 Account currently locked</div>
                    )}
                  </div>

                  {/* Action column */}
                  <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end', minWidth:160 }}>
                    {lr.status === 'pending' && (
                      <>
                        <input
                          type="text"
                          placeholder="Admin note (optional)…"
                          value={hrNotes[lr.id]||''}
                          onChange={e=>setHrNotes(n=>({...n,[lr.id]:e.target.value}))}
                          style={{ ...inputStyle, fontSize:10, padding:'4px 8px', width:160 }}
                        />
                        <div style={{ display:'flex', gap:6 }}>
                          <button style={{ ...btnSm, background:'#0d2b1a', color:'#2ecc71', fontSize:10, border:'1px solid #2ecc7144' }}
                            onClick={()=>doApprove(lr)}>✅ Approve</button>
                          <button style={{ ...btnSm, background:'#2b0d0d', color:'#e74c3c', fontSize:10, border:'1px solid #e74c3c44' }}
                            onClick={()=>doReject(lr)}>❌ Reject</button>
                        </div>
                      </>
                    )}
                    {lr.status === 'approved' && lr.credentials_deactivated && (
                      <button style={{ ...btnSm, background:'#0d1a2b', color:'#3498db', fontSize:10, border:'1px solid #3498db44' }}
                        onClick={()=>doReactivate(lr)}>
                        🔓 Reactivate Staff
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* ══════════ REPORTS TAB ══════════ */}
      {activeTab === 'reports' && (
        <div style={{ display:'grid', gap:18 }}>

          {/* ── Top KPI row ── */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12 }}>
            {[
              { label:'Total Requests',     value:counts.all,       color:'#3498db', icon:'📋' },
              { label:'Approved Leaves',    value:counts.approved,  color:'#2ecc71', icon:'✅' },
              { label:'Rejected',           value:counts.rejected,  color:'#e74c3c', icon:'❌' },
              { label:'Cancelled',          value:counts.cancelled, color:'#888',    icon:'🚫' },
              { label:'Total Days (approved)', value:approvedDays,  color:'#c8a96e', icon:'📅' },
              { label:'Avg Days / Request', value: counts.all ? (totalDays/counts.all).toFixed(1) : 0, color:'#9b59b6', icon:'📐' },
            ].map(k => (
              <div key={k.label} style={{
                background:'var(--bgCard)', border:`1px solid ${k.color}22`,
                borderTop:`3px solid ${k.color}`, borderRadius:8, padding:'14px 16px', textAlign:'center',
              }}>
                <div style={{ fontSize:22, marginBottom:2 }}>{k.icon}</div>
                <div style={{ fontSize:26, fontWeight:800, color:k.color }}>{k.value}</div>
                <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:3 }}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* ── Status distribution + Leave type breakdown ── */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:18 }}>

            {/* Status distribution */}
            <Card title="📊 Requests by Status">
              {statusDist.map(s => (
                <div key={s.label} style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:12 }}>
                    <span style={{ color:'var(--textSecond)' }}>{s.label}</span>
                    <span style={{ fontWeight:700, color:s.color }}>{s.value}</span>
                  </div>
                  <div style={{ background:'var(--bgInput)', borderRadius:4, overflow:'hidden', height:8 }}>
                    <div style={{
                      height:'100%', borderRadius:4,
                      width:`${counts.all ? (s.value/counts.all)*100 : 0}%`,
                      background:s.color, transition:'width .4s',
                    }} />
                  </div>
                  <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:2 }}>
                    {counts.all ? ((s.value/counts.all)*100).toFixed(1) : 0}% of all requests
                  </div>
                </div>
              ))}
            </Card>

            {/* Leave type breakdown */}
            <Card title="🏷️ Requests by Leave Type">
              {byType.length === 0 ? <Empty text="No data" /> : byType.map(([type, cnt]) => (
                <div key={type} style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:12 }}>
                    <span style={{ color:'var(--textSecond)' }}>{LEAVE_ICONS[type]} {LEAVE_TYPES[type]||type}</span>
                    <span style={{ fontWeight:700, color:'#c8a96e' }}>{cnt}</span>
                  </div>
                  <div style={{ background:'var(--bgInput)', borderRadius:4, overflow:'hidden', height:8 }}>
                    <div style={{
                      height:'100%', borderRadius:4,
                      width:`${(cnt/maxTypeCount)*100}%`,
                      background:'linear-gradient(90deg,#c8a96e,#e8c980)',
                      transition:'width .4s',
                    }} />
                  </div>
                  <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:2 }}>
                    {counts.all ? ((cnt/counts.all)*100).toFixed(1) : 0}% of all requests
                  </div>
                </div>
              ))}
            </Card>
          </div>

          {/* ── Monthly trend ── */}
          <Card title="📈 Monthly Leave Trend (Last 12 Months)">
            {byMonth.length === 0 ? <Empty text="No historical data yet" /> : (
              <div>
                <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:120, padding:'0 4px', marginBottom:8 }}>
                  {byMonth.map(m => (
                    <div key={m.month} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:2, height:'100%', justifyContent:'flex-end' }}>
                      <div style={{ fontSize:9, color:'#c8a96e', fontWeight:700 }}>{m.total}</div>
                      <div style={{
                        width:'100%', borderRadius:'3px 3px 0 0',
                        height:`${Math.max((m.total/maxMonthTotal)*96,4)}px`,
                        background:`linear-gradient(180deg,#c8a96e,#a07a40)`,
                        position:'relative',
                      }}>
                        {m.approved > 0 && (
                          <div style={{
                            position:'absolute', bottom:0, left:0, right:0, borderRadius:'3px 3px 0 0',
                            height:`${(m.approved/m.total)*100}%`,
                            background:'#2ecc71',
                          }} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                  {byMonth.map(m => (
                    <div key={m.month} style={{ flex:1, textAlign:'center', fontSize:8, color:'var(--textFaint)' }}>
                      {m.month.slice(5)}
                    </div>
                  ))}
                </div>
                <div style={{ display:'flex', gap:16, fontSize:10, color:'var(--textMuted)' }}>
                  <span><span style={{ display:'inline-block',width:10,height:10,background:'#c8a96e',borderRadius:2,marginRight:4,verticalAlign:'middle' }} />Requested</span>
                  <span><span style={{ display:'inline-block',width:10,height:10,background:'#2ecc71',borderRadius:2,marginRight:4,verticalAlign:'middle' }} />Approved portion</span>
                </div>
              </div>
            )}
          </Card>

          {/* ── Per-employee breakdown ── */}
          <Card title={`👥 Leave by Employee (${byEmployee.length} staff with requests)`}>
            {byEmployee.length === 0 ? <Empty text="No employee data" /> : (
              <div>
                {/* Table header */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 70px 70px 90px', gap:8,
                  padding:'6px 10px', fontSize:10, color:'var(--textFaint)', borderBottom:'1px solid var(--border)',
                  textTransform:'uppercase', letterSpacing:.4, fontWeight:700 }}>
                  <span>Employee</span>
                  <span>Role</span>
                  <span style={{ textAlign:'center' }}>Requests</span>
                  <span style={{ textAlign:'center' }}>Days</span>
                  <span style={{ textAlign:'center' }}>Approved</span>
                </div>
                {byEmployee.map((emp, idx) => (
                  <div key={emp.name} style={{
                    display:'grid', gridTemplateColumns:'1fr 120px 70px 70px 90px', gap:8,
                    padding:'10px 10px', fontSize:12,
                    borderBottom:'1px solid var(--border)',
                    background: idx % 2 === 0 ? 'transparent' : 'var(--bgInput)',
                    alignItems:'center',
                  }}>
                    <div>
                      <div style={{ fontWeight:600, color:'var(--textPrimary)' }}>{emp.name}</div>
                      <div style={{ height:4, marginTop:4, background:'var(--bgInput)', borderRadius:2, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${(emp.days/maxEmpDays)*100}%`, background:'linear-gradient(90deg,#c8a96e,#e8c980)', borderRadius:2 }} />
                      </div>
                    </div>
                    <div style={{ fontSize:10, color:'var(--textMuted)' }}>{emp.role}</div>
                    <div style={{ textAlign:'center', fontWeight:700, color:'#3498db' }}>{emp.total}</div>
                    <div style={{ textAlign:'center', fontWeight:700, color:'#c8a96e' }}>{emp.days}</div>
                    <div style={{ textAlign:'center' }}>
                      <span style={{ padding:'2px 8px', borderRadius:10, fontSize:10, fontWeight:700,
                        background:'#2ecc7122', color:'#2ecc71', border:'1px solid #2ecc7144' }}>
                        {emp.approved}
                      </span>
                    </div>
                  </div>
                ))}

                {/* Footer totals */}
                <div style={{
                  display:'grid', gridTemplateColumns:'1fr 120px 70px 70px 90px', gap:8,
                  padding:'10px 10px', fontSize:12, borderTop:'2px solid var(--border)',
                  fontWeight:700, color:'var(--textPrimary)',
                }}>
                  <span style={{ color:'var(--textMuted)', fontStyle:'italic' }}>Totals</span>
                  <span />
                  <span style={{ textAlign:'center', color:'#3498db' }}>{counts.all}</span>
                  <span style={{ textAlign:'center', color:'#c8a96e' }}>{totalDays}</span>
                  <span style={{ textAlign:'center', color:'#2ecc71' }}>{counts.approved}</span>
                </div>
              </div>
            )}
          </Card>

          {/* ── Full leave log ── */}
          <Card title={`📄 Full Leave Log — All Staff (${requests.length} total)`}>
            <div style={{ marginBottom:10 }}>
              <input
                type="text"
                placeholder="🔍 Search name, type, reason…"
                value={search}
                onChange={e=>setSearch(e.target.value)}
                style={{ ...inputStyle, fontSize:11, padding:'5px 10px', width:'100%', maxWidth:340 }}
              />
            </div>
            {/* Table header */}
            <div style={{ display:'grid', gridTemplateColumns:'130px 100px 80px 130px 70px 80px 1fr',
              gap:6, padding:'6px 8px', fontSize:9, color:'var(--textFaint)',
              borderBottom:'1px solid var(--border)', textTransform:'uppercase', letterSpacing:.4, fontWeight:700 }}>
              <span>Employee</span><span>Role</span><span>Type</span>
              <span>Dates</span><span>Days</span><span>Status</span><span>Reason</span>
            </div>
            {(search
              ? requests.filter(r =>
                  (r.employee_name||'').toLowerCase().includes(searchLower) ||
                  (r.leave_type||'').toLowerCase().includes(searchLower) ||
                  (r.reason||'').toLowerCase().includes(searchLower)
                )
              : requests
            ).map((lr, idx) => (
              <div key={lr.id} style={{
                display:'grid', gridTemplateColumns:'130px 100px 80px 130px 70px 80px 1fr',
                gap:6, padding:'8px 8px', fontSize:10, alignItems:'center',
                borderBottom:'1px solid var(--border)',
                background: idx%2===0?'transparent':'var(--bgInput)',
                borderLeft:`3px solid ${STATUS_COLOR[lr.status]||'#888'}`,
              }}>
                <span style={{ fontWeight:600, color:'var(--textPrimary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {lr.employee_name}
                </span>
                <span style={{ color:'var(--textMuted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:9 }}>
                  {lr.employee_role}
                </span>
                <span style={{ color:'var(--textSecond)' }}>
                  {LEAVE_ICONS[lr.leave_type]} {lr.leave_type}
                </span>
                <span style={{ color:'var(--textMuted)', fontSize:9 }}>
                  {lr.date_from} → {lr.date_to}
                </span>
                <span style={{ textAlign:'center', fontWeight:700, color:'#c8a96e' }}>
                  {lr.days}
                </span>
                <span style={{
                  padding:'2px 6px', borderRadius:10, fontSize:9, fontWeight:700, textAlign:'center',
                  background:(STATUS_COLOR[lr.status]||'#888')+'22',
                  color:STATUS_COLOR[lr.status]||'#888',
                }}>
                  {STATUS_ICON[lr.status]} {lr.status}
                </span>
                <span style={{ color:'var(--textFaint)', fontStyle:'italic', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {lr.reason?.slice(0,60)}{lr.reason?.length>60?'…':''}
                </span>
              </div>
            ))}
          </Card>

        </div>
      )}
    </div>
  );
}


// ─── Admin: Session Settings ──────────────────────────────────────────────────
// ─── Data Backup & Restore ────────────────────────────────────────────────────
function DataBackup({ notify }) {
  const [status, setStatus] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);

  // Keys we want to snapshot from localStorage
  const BACKUP_KEYS = [
    'grand_theme_v4', 'grand_theme_v2',
    'grand_approval_stamp_v1',
    'grand_stamp_storekeeper_v1', 'grand_stamp_accountant_v1',
    'grand_stamp_manager_v1', 'grand_stamp_director_v1',
    'grand_landing_content_v1',
    'token', 'user',
  ];

  const readBackupInfo = () => {
    try {
      const raw = localStorage.getItem('grand_backup_meta_v1');
      if (raw) setBackupInfo(JSON.parse(raw));
    } catch {}
  };

  useEffect(() => { readBackupInfo(); }, []);

  const exportBackup = async () => {
    setStatus('Exporting…');
    try {
      // 1. Pull all localStorage settings
      const lsSnapshot = {};
      BACKUP_KEYS.forEach(k => {
        const v = localStorage.getItem(k);
        if (v) lsSnapshot[k] = v;
      });

      // 2. Pull key server data
      const [guests, orders, conference, rooms, menu, menuBar, staff] = await Promise.allSettled([
        api.getGuests(),
        api.getOrders(),
        api.getConferenceBookings(),
        api.getRooms ? api.getRooms() : Promise.resolve({ data: [] }),
        api.getMenu('food'),
        api.getMenu('bar'),
        api.getUsers(),
      ]);

      const safePull = (r) => {
        if (r.status === 'fulfilled') {
          const d = r.value?.data;
          return d?.results || d || [];
        }
        return [];
      };

      const backup = {
        version: '7.5.1',
        exported_at: new Date().toISOString(),
        exported_by: (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').name || 'Unknown'; } catch { return 'Unknown'; } })(),
        localStorage: lsSnapshot,
        data: {
          guests:      safePull(guests),
          orders:      safePull(orders),
          conference:  safePull(conference),
          rooms:       safePull(rooms),
          menu_food:   safePull(menu),
          menu_bar:    safePull(menuBar),
          staff:       safePull(staff),
        },
        summary: {
          guests_count:     safePull(guests).length,
          orders_count:     safePull(orders).length,
          conference_count: safePull(conference).length,
          rooms_count:      safePull(rooms).length,
          menu_items:       safePull(menu).length + safePull(menuBar).length,
          staff_count:      safePull(staff).length,
        },
      };

      // 3. Save backup metadata to localStorage so we know when last backed up
      const meta = { exported_at: backup.exported_at, summary: backup.summary };
      localStorage.setItem('grand_backup_meta_v1', JSON.stringify(meta));
      setBackupInfo(meta);

      // 4. Download as JSON file
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `GrandMemories_Backup_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatus(`✅ Backup downloaded — ${backup.summary.guests_count} guests, ${backup.summary.orders_count} orders, ${backup.summary.conference_count} conference bookings`);
      notify('✅ Full backup exported successfully!');
    } catch (e) {
      setStatus('❌ Export failed — ' + (e.message || 'Unknown error'));
      notify('Backup export failed', 'error');
    }
  };

  const importBackup = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) { notify('Please select a valid .json backup file', 'error'); return; }
    setRestoring(true);
    setStatus('Reading backup file…');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const backup = JSON.parse(ev.target.result);
        if (!backup.version || !backup.localStorage) throw new Error('Invalid backup file format');

        // Restore localStorage settings (branding, stamps, etc.)
        Object.entries(backup.localStorage || {}).forEach(([k, v]) => {
          // Never restore token/user — would cause auth confusion
          if (k !== 'token' && k !== 'user') {
            try { localStorage.setItem(k, v); } catch {}
          }
        });

        const s = backup.summary || {};
        setStatus(`✅ Settings restored from backup dated ${new Date(backup.exported_at).toLocaleString('en-KE')}.\n` +
          `Backup contained: ${s.guests_count||0} guests, ${s.orders_count||0} orders, ${s.conference_count||0} conference bookings, ${s.rooms_count||0} rooms.\n\n` +
          `⚠ Note: Live database records (guests, orders, bookings) must be restored by your system administrator from the server backup. This tool restores UI settings and branding only.`);
        notify('✅ Settings restored from backup!');
        setRestoring(false);
      } catch(err) {
        setStatus('❌ Restore failed — ' + err.message);
        notify('Restore failed: ' + err.message, 'error');
        setRestoring(false);
      }
    };
    reader.onerror = () => { setStatus('❌ Could not read file'); setRestoring(false); };
    reader.readAsText(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const inputStyle2 = {
    background:'var(--bgInput)', border:'1px solid var(--borderMid)', borderRadius:4,
    color:'var(--textPrimary)', padding:'8px 12px', fontSize:13, fontFamily:'inherit', width:'100%',
  };
  const btnPrimary2 = {
    background:'#c8a96e', color:'#111', border:'none', borderRadius:4, padding:'10px 20px',
    fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
  };

  return (
    <div>
      <div style={{ marginBottom:20, padding:'12px 16px', background:'#0a1a0a', border:'1px solid #1e3a1e', borderRadius:4 }}>
        <div style={{ fontSize:12, color:'#2ecc71', fontWeight:700, marginBottom:4 }}>💾 Why regular backups matter</div>
        <div style={{ fontSize:12, color:'#556655', lineHeight:1.7 }}>
          Your data lives in a database on the server. UI settings (branding, stamps, theme) live in your browser. A full backup captures both so you can recover from browser clears, server issues, or accidental data loss. <strong style={{ color:'#2ecc71' }}>Backup weekly — or before any major change.</strong>
        </div>
      </div>

      {backupInfo && (
        <div style={{ marginBottom:16, padding:'10px 14px', background:'#111', border:'1px solid #2a2a2a', borderRadius:4, fontSize:12 }}>
          <div style={{ color:'var(--textFaint)', fontSize:10, textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>Last Backup</div>
          <div style={{ color:'#c8a96e', fontWeight:700 }}>{new Date(backupInfo.exported_at).toLocaleString('en-KE')}</div>
          {backupInfo.summary && (
            <div style={{ color:'var(--textMuted)', marginTop:4 }}>
              {backupInfo.summary.guests_count} guests · {backupInfo.summary.orders_count} orders · {backupInfo.summary.conference_count} conference bookings · {backupInfo.summary.rooms_count} rooms
            </div>
          )}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16, marginBottom:20 }}>
        {/* Export */}
        <div style={{ background:'#111', border:'1px solid #2a2a2a', borderRadius:6, padding:20 }}>
          <div style={{ fontSize:15, fontWeight:700, color:'var(--textPrimary)', marginBottom:6 }}>📥 Export Full Backup</div>
          <div style={{ fontSize:12, color:'var(--textMuted)', lineHeight:1.6, marginBottom:16 }}>
            Downloads a <code>.json</code> file containing all branding settings, approval stamps, theme config, and a snapshot of guests, orders, rooms, staff, menu items, and conference bookings.
          </div>
          <button style={btnPrimary2} onClick={exportBackup}>⬇ Download Backup Now</button>
        </div>

        {/* Import */}
        <div style={{ background:'#111', border:'1px solid #2a2a2a', borderRadius:6, padding:20 }}>
          <div style={{ fontSize:15, fontWeight:700, color:'var(--textPrimary)', marginBottom:6 }}>📤 Restore from Backup</div>
          <div style={{ fontSize:12, color:'var(--textMuted)', lineHeight:1.6, marginBottom:12 }}>
            Select a previously exported <code>.json</code> backup file. This will restore branding and UI settings instantly. Database records require server-side restore.
          </div>
          <label style={{ ...btnPrimary2, background:'#1a1a2a', color:'#aaa', border:'1px solid #3a3a5a', display:'inline-block', cursor:'pointer' }}>
            {restoring ? '⏳ Restoring…' : '📂 Choose Backup File'}
            <input type="file" accept=".json" onChange={importBackup} style={{ display:'none' }} disabled={restoring}/>
          </label>
        </div>
      </div>

      {/* Auto-backup reminder tip */}
      <div style={{ marginBottom:16, padding:'10px 14px', background:'#0a0a1a', border:'1px solid #1e1e3a', borderRadius:4 }}>
        <div style={{ fontSize:11, color:'#7a7aaa', fontWeight:700, marginBottom:4 }}>📅 Recommended Backup Schedule</div>
        <div style={{ fontSize:11, color:'#555577', lineHeight:1.7 }}>
          Daily: export before end-of-day shift close &nbsp;·&nbsp; Weekly: store a copy to Google Drive or USB &nbsp;·&nbsp; Before updates: always backup first<br/>
          Ask your IT/hosting provider to enable <strong style={{ color:'#7a7aaa' }}>automatic database backups</strong> on a daily schedule on the server.
        </div>
      </div>

      {status && (
        <div style={{ padding:'12px 14px', background: status.startsWith('✅') ? '#0a1a0a' : status.startsWith('❌') ? '#1a0a0a' : '#111',
          border:`1px solid ${status.startsWith('✅') ? '#1e3a1e' : status.startsWith('❌') ? '#3a1e1e' : '#2a2a2a'}`,
          borderRadius:4, fontSize:12, color: status.startsWith('✅') ? '#2ecc71' : status.startsWith('❌') ? '#e74c3c' : '#aaa',
          whiteSpace:'pre-wrap', lineHeight:1.7 }}>
          {status}
        </div>
      )}
    </div>
  );
}

function AdminSessionSettings({ notify }) {
  const [config, setConfig]   = useState(null);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    api.getSystemConfig().then(r => {
      setConfig(r.data);
      setForm(r.data);
    }).catch(() => notify('Failed to load config','error'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Don't send branding fields — they're owned by the Branding tab and a stale
      // copy here would overwrite a newer hotel name/logo.
      const { hotel_name, hotel_tagline, hotel_phone, hotel_email, hotel_address, hotel_logo_b64, ...sessionFields } = form; // eslint-disable-line no-unused-vars
      await api.updateSystemConfig(sessionFields);
      notify('✅ Session settings saved');
      setConfig(form);
    } catch(e) { notify('Failed to save','error'); }
    finally { setSaving(false); }
  };

  if (!config) return <div style={{ padding:20, color:'var(--textMuted)' }}>Loading config…</div>;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:16 }}>
      <Card title="🔐 Session & Login Controls">
        <div style={{ fontSize:11, color:'var(--textMuted)', marginBottom:16, padding:'8px 12px',
          background:'#1a1a0a', border:'1px solid #c8a96e33', borderRadius:4 }}>
          ⚠️ Admin accounts are always exempt from login window and single-session restrictions.
        </div>

        <Field label="Session Duration (hours — 0 = never expire)">
          <input type="number" style={inputStyle} min={0} max={168}
            value={form.session_hours ?? 12}
            onChange={e=>setForm(f=>({...f,session_hours:parseInt(e.target.value)||0}))} />
          <div style={{ fontSize:10, color:'var(--textMuted)', marginTop:3 }}>
            Currently: {form.session_hours === 0 ? 'Never expires' : `${form.session_hours}h (tokens expire after ${form.session_hours} hours of no activity)`}
          </div>
        </Field>

        <div style={{ padding:'12px 14px', background:'var(--bgInput)', borderRadius:6, marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <div>
              <div style={{ fontWeight:600, color:'var(--textPrimary)', fontSize:13 }}>Single Session Only</div>
              <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2 }}>When enabled, logging in on a new device automatically logs out the old session. Prevents concurrent logins.</div>
            </div>
            <div onClick={()=>setForm(f=>({...f,single_session_only:!f.single_session_only}))}
              style={{ width:48, height:26, borderRadius:13, cursor:'pointer', transition:'background .2s',
                background:form.single_session_only?'#2ecc71':'#333',
                position:'relative' }}>
              <div style={{ position:'absolute', top:3, left:form.single_session_only?24:3,
                width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left .2s' }} />
            </div>
          </div>
        </div>

        <div style={{ padding:'12px 14px', background:'var(--bgInput)', borderRadius:6, marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:form.login_window_enabled?12:0 }}>
            <div>
              <div style={{ fontWeight:600, color:'var(--textPrimary)', fontSize:13 }}>Login Time Window</div>
              <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2 }}>Restrict staff from logging in outside defined hours (e.g. 6am–11pm).</div>
            </div>
            <div onClick={()=>setForm(f=>({...f,login_window_enabled:!f.login_window_enabled}))}
              style={{ width:48, height:26, borderRadius:13, cursor:'pointer', transition:'background .2s',
                background:form.login_window_enabled?'#2ecc71':'#333', position:'relative' }}>
              <div style={{ position:'absolute', top:3, left:form.login_window_enabled?24:3,
                width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left .2s' }} />
            </div>
          </div>
          {form.login_window_enabled && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10, marginTop:10 }}>
              <Field label="Allow Login From">
                <input type="time" style={inputStyle}
                  value={form.login_window_start || '06:00'}
                  onChange={e=>setForm(f=>({...f,login_window_start:e.target.value}))} />
              </Field>
              <Field label="Block Login After">
                <input type="time" style={inputStyle}
                  value={form.login_window_end || '23:00'}
                  onChange={e=>setForm(f=>({...f,login_window_end:e.target.value}))} />
              </Field>
            </div>
          )}
        </div>

        <button style={{ ...btnPrimary, marginTop:8 }} onClick={save} disabled={saving}>
          {saving ? '⏳ Saving…' : '✅ Save Settings'}
        </button>
      </Card>

      <Card title="ℹ️ How Sessions Work">
        <div style={{ fontSize:11, lineHeight:1.8, color:'var(--textSecond)' }}>
          <div style={{ fontWeight:700, color:'var(--accent)', marginBottom:8 }}>Session Duration</div>
          <div>DRF Token Authentication is used. Tokens don't naturally expire, but with this setting, the backend will reject tokens older than the configured hours.</div>
          <div style={{ marginTop:10, fontWeight:700, color:'var(--accent)', marginBottom:8 }}>Single Session</div>
          <div>When enabled, the old token is deleted on each new login. The old device will receive 401 Unauthorized on its next request and be forced to log in again.</div>
          <div style={{ marginTop:10, fontWeight:700, color:'var(--accent)', marginBottom:8 }}>Login Window</div>
          <div>Staff will see an error like: <em style={{ color:'#e67e22' }}>"Login not permitted outside 06:00–23:00"</em> if they try to log in outside the window. Admin accounts bypass this restriction.</div>
          <div style={{ marginTop:10, fontWeight:700, color:'#2ecc71', marginBottom:8 }}>Leave & Termination</div>
          <div>When HR approves a leave request, the employee's account is instantly deactivated and their active token is deleted — locking them out immediately.</div>
        </div>
      </Card>
    </div>
  );
}


// ─── Admin: Employee Profiles ─────────────────────────────────────────────────
function EmployeeProfiles({ notify }) {
  const bp = useBreakpoint();
  const [users, setUsers]         = useState([]);
  const [selected, setSelected]   = useState(null);
  const [profile, setProfile]     = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileTab, setProfileTab] = useState('overview');
  const [search, setSearch]       = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showPass, setShowPass]   = useState(false);
  const [newPass, setNewPass]     = useState('');
  const [savingPass, setSavingPass] = useState(false);

  const roleColors = { admin:'#c8a96e', waiter:'#3498db', cashier:'#2ecc71', kitchen:'#e67e22', chef:'#e74c3c', reception:'#9b59b6', gate:'#e74c3c', housekeeping:'#1abc9c', accountant:'#f39c12', gift_shop:'#e91e8c', restaurant_manager:'#00b300', storekeeper:'#1a9ba0', hr:'#8e44ad', marketing:'#e84393', swimming_attendant:'#0ea5e9', kids_amenities:'#f472b6' };

  const load = useCallback(async () => {
    try { const r = await api.getUsers(); setUsers(r.data.results || r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const openProfile = async (user) => {
    setSelected(user);
    setProfile(null);
    setProfileTab('overview');
    setNewPass('');
    setShowPass(false);
    setProfileLoading(true);
    try {
      const r = await api.getEmployeeProfile(user.id);
      setProfile(r.data);
    } catch(e) {
      notify(e.response?.data?.error || 'Failed to load profile', 'error');
    } finally {
      setProfileLoading(false);
    }
  };

  const savePassword = async () => {
    if (!newPass || newPass.length < 4) { notify('Password must be at least 4 characters', 'error'); return; }
    setSavingPass(true);
    try {
      await api.updateUser(selected.id, { password: newPass });
      notify(`✅ Password updated for ${selected.name}`);
      setNewPass('');
      setShowPass(false);
    } catch(e) { notify('Failed to update password', 'error'); }
    finally { setSavingPass(false); }
  };

  const allRoles = [...new Set(users.map(u => u.role))].sort();
  const displayed = users.filter(u =>
    !u.is_terminated &&
    (!search || u.name.toLowerCase().includes(search.toLowerCase()) || u.username.toLowerCase().includes(search.toLowerCase())) &&
    (!roleFilter || u.role === roleFilter)
  );

  const PROFILE_TABS = [
    { id:'overview',   label:'👤 Overview' },
    { id:'logins',     label:'🔑 Login History' },
    { id:'attendance', label:'📅 Attendance' },
    { id:'activity',   label:'📋 Activity Log' },
    { id:'security',   label:'🔒 Security' },
  ];

  const actionColors = {
    login:'#22c55e', logout:'#94a3b8', order_created:'#3b82f6', bill_paid:'#22c55e',
    order_voided:'#e74c3c', refund_issued:'#e74c3c', discount_applied:'#f59e0b',
    guest_checkin:'#8b5cf6', guest_checkout:'#6366f1', user_created:'#c8a96e',
    export_excel:'#14b8a6', export_pdf:'#14b8a6', other:'#888',
  };

  return (
    <div>
      <PageHeader icon="👥" title="Employee Profiles" sub="Complete staff dossiers — profile, login history, attendance records & security" />

      <div style={{ display:'grid', gridTemplateColumns: selected && bp !== 'sm' ? '300px 1fr' : '1fr', gap:16, alignItems:'start' }}>

        {/* ── Staff list panel ── */}
        <div>
          <Card title={`Staff Directory (${displayed.length})`}>
            {/* Filters */}
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:14 }}>
              <input style={inputStyle} placeholder="🔍 Search name or username…" value={search} onChange={e=>setSearch(e.target.value)} />
              <select style={inputStyle} value={roleFilter} onChange={e=>setRoleFilter(e.target.value)}>
                <option value="">All Roles</option>
                {allRoles.map(r=><option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
              </select>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              {displayed.map(u => {
                const isSelected = selected?.id === u.id;
                return (
                  <div key={u.id} onClick={() => openProfile(u)} style={{
                    display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
                    borderRadius:8, cursor:'pointer', transition:'background .12s',
                    background: isSelected ? 'var(--bgHover)' : 'transparent',
                    border: `1px solid ${isSelected ? 'var(--borderMid)' : 'transparent'}`,
                  }}
                    onMouseEnter={e=>{ if (!isSelected) e.currentTarget.style.background='var(--bgHover)'; }}
                    onMouseLeave={e=>{ if (!isSelected) e.currentTarget.style.background='transparent'; }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width:38, height:38, borderRadius:'50%', flexShrink:0,
                      background: (roleColors[u.role]||'#888') + '22',
                      border: `2px solid ${roleColors[u.role]||'#888'}44`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:15, fontWeight:700, color: roleColors[u.role]||'#888',
                    }}>
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'var(--textPrimary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.name}</div>
                      <div style={{ fontSize:10, color:'var(--textMuted)', display:'flex', gap:6, alignItems:'center', marginTop:2 }}>
                        <code>@{u.username}</code>
                        <span style={{ padding:'1px 6px', borderRadius:3, background:(roleColors[u.role]||'#888')+'22', color:roleColors[u.role]||'#888', fontSize:9, fontWeight:700, textTransform:'uppercase' }}>{u.role.replace(/_/g,' ')}</span>
                      </div>
                    </div>
                    <div style={{ width:8, height:8, borderRadius:'50%', background: u.is_active?'#22c55e':'#e74c3c', flexShrink:0 }} />
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* ── Profile detail panel ── */}
        {selected && (
          <div>
            {/* Profile header */}
            <div style={{
              background:'var(--bgCard)', border:'1px solid var(--border)',
              borderRadius:10, padding:'20px 24px', marginBottom:14,
              display:'flex', alignItems:'center', gap:18, flexWrap:'wrap',
            }}>
              {/* Big avatar */}
              <div style={{
                width:72, height:72, borderRadius:'50%', flexShrink:0,
                background: (roleColors[selected.role]||'#888') + '18',
                border: `3px solid ${roleColors[selected.role]||'#888'}66`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:30, fontWeight:700, color: roleColors[selected.role]||'#888',
              }}>
                {selected.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:22, fontWeight:800, color:'var(--textPrimary)', letterSpacing:-.3 }}>{selected.name}</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6, alignItems:'center' }}>
                  <code style={{ fontSize:12, color:'var(--textMuted)', background:'var(--bgInput)', padding:'2px 8px', borderRadius:4 }}>@{selected.username}</code>
                  <span style={{ padding:'3px 10px', borderRadius:4, fontSize:11, fontWeight:700, background:(roleColors[selected.role]||'#888')+'22', color:roleColors[selected.role]||'#888', textTransform:'capitalize' }}>
                    {selected.role.replace(/_/g,' ')}
                  </span>
                  {selected.kitchen_station && (
                    <span style={{ fontSize:11, color:'var(--accent)', background:'var(--bgHover)', padding:'2px 8px', borderRadius:4 }}>📍 {selected.kitchen_station}</span>
                  )}
                  <span style={{ fontSize:11, fontWeight:700, color: selected.is_active?'#22c55e':'#e74c3c' }}>
                    {selected.is_active ? '● Active' : '● Inactive'}
                  </span>
                </div>
                <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:4 }}>
                  Member since {new Date(selected.created_at || Date.now()).toLocaleDateString('en-KE', {day:'numeric', month:'long', year:'numeric'})}
                </div>
              </div>
              {/* Close */}
              <button onClick={()=>{ setSelected(null); setProfile(null); }} style={{ ...btnSm, background:'var(--bgInput)', color:'var(--textMuted)', flexShrink:0 }}>✕ Close</button>
            </div>

            {/* Stats row (from profile) */}
            {profile && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:10, marginBottom:14 }}>
                {[
                  { label:'Days Worked',    value: profile.stats?.days_worked  || 0,  color:'#3b82f6',  icon:'📅' },
                  { label:'Total Logins',   value: profile.stats?.total_logins || 0,  color:'#22c55e',  icon:'🔑' },
                  { label:'Total Actions',  value: profile.stats?.total_actions|| 0,  color:'#8b5cf6',  icon:'⚡' },
                ].map((s,i)=>(
                  <div key={i} style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px', textAlign:'center' }}>
                    <div style={{ fontSize:18, marginBottom:4 }}>{s.icon}</div>
                    <div style={{ fontSize:22, fontWeight:800, color:s.color, lineHeight:1 }}>{s.value}</div>
                    <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:3, textTransform:'uppercase', letterSpacing:.8 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Profile sub-tabs */}
            <div style={{ display:'flex', gap:0, borderBottom:'1px solid var(--border)', marginBottom:14, overflowX:'auto' }}>
              {PROFILE_TABS.map(t => (
                <button key={t.id} onClick={()=>setProfileTab(t.id)} style={{
                  padding:'0 14px', height:36, border:'none', background:'transparent',
                  borderBottom:`2px solid ${profileTab===t.id?'var(--primary)':'transparent'}`,
                  color: profileTab===t.id?'var(--primary)':'var(--textMuted)',
                  fontWeight: profileTab===t.id?600:400, fontSize:12.5, cursor:'pointer',
                  fontFamily:'inherit', whiteSpace:'nowrap', marginBottom:-1, flexShrink:0,
                  transition:'color .12s, border-color .12s',
                }}>{t.label}</button>
              ))}
            </div>

            {profileLoading && <Loading/>}

            {!profileLoading && profile && (
              <>
                {/* ── Overview tab ── */}
                {profileTab === 'overview' && (
                  <Card title="Profile Details">
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:0 }}>
                      {[
                        ['Full Name',     profile.profile.name],
                        ['Username',      '@' + profile.profile.username],
                        ['Role',          profile.profile.role.replace(/_/g,' ')],
                        ['Kitchen Station', profile.profile.kitchen_station || '—'],
                        ['Account Status', profile.profile.is_active ? '✅ Active' : '🔴 Inactive'],
                        ['Member Since',  new Date(profile.profile.created_at).toLocaleDateString('en-KE', {day:'numeric',month:'long',year:'numeric'})],
                        profile.profile.is_terminated ? ['Terminated On', new Date(profile.profile.terminated_at).toLocaleDateString('en-KE')] : null,
                        profile.profile.termination_reason ? ['Termination Reason', profile.profile.termination_reason] : null,
                      ].filter(Boolean).map(([k,v])=>(
                        <div key={k} style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', gap:8 }}>
                          <span style={{ fontSize:12, color:'var(--textMuted)', fontWeight:600 }}>{k}</span>
                          <span style={{ fontSize:12, color:'var(--textPrimary)', textAlign:'right', textTransform:'capitalize' }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* ── Login History tab ── */}
                {profileTab === 'logins' && (
                  <Card title={`Login & Logout History (${profile.login_history?.length || 0} records)`}>
                    {(!profile.login_history || profile.login_history.length === 0) ? (
                      <Empty text="No login records found"/>
                    ) : (
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                          <thead>
                            <tr>{['Event','IP Address','Date & Time'].map(h=>(
                              <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:10, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1, borderBottom:'2px solid var(--border)' }}>{h}</th>
                            ))}</tr>
                          </thead>
                          <tbody>
                            {profile.login_history.map((log, i)=>(
                              <tr key={log.id||i} style={{ borderBottom:'1px solid var(--border)' }}>
                                <td style={{ padding:'8px 10px' }}>
                                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:4,
                                    background: log.action==='login' ? 'rgba(34,197,94,0.12)' : 'rgba(148,163,184,0.12)',
                                    color: log.action==='login' ? '#22c55e' : '#94a3b8',
                                    textTransform:'uppercase', letterSpacing:.5 }}>
                                    {log.action==='login' ? '🔑 Login' : '🚪 Logout'}
                                  </span>
                                </td>
                                <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontFamily:'monospace', fontSize:11 }}>{log.ip_address || '—'}</td>
                                <td style={{ padding:'8px 10px', color:'var(--textSecond)', fontSize:11 }}>
                                  {new Date(log.created_at).toLocaleString('en-KE', {dateStyle:'medium', timeStyle:'short'})}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                )}

                {/* ── Attendance tab ── */}
                {profileTab === 'attendance' && (
                  <div>
                    <Card title={`Attendance Records — ${profile.stats?.days_worked || 0} Days Worked`}>
                      {(!profile.attendance || profile.attendance.length === 0) ? (
                        <Empty text="No attendance records found"/>
                      ) : (
                        <>
                          {/* Calendar-style attendance grid */}
                          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))', gap:6, marginBottom:16 }}>
                            {profile.attendance.slice(0,60).map((day, i) => (
                              <div key={i} style={{
                                background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.2)',
                                borderRadius:6, padding:'8px 10px', textAlign:'center',
                              }}>
                                <div style={{ fontSize:12, fontWeight:700, color:'#22c55e' }}>
                                  {new Date(day.date).toLocaleDateString('en-KE', {month:'short', day:'numeric'})}
                                </div>
                                <div style={{ fontSize:10, color:'var(--textMuted)', marginTop:2 }}>
                                  {new Date(day.date).toLocaleDateString('en-KE', {weekday:'short'})}
                                </div>
                                <div style={{ fontSize:9, color:'var(--textFaint)', marginTop:2 }}>
                                  {day.sessions} session{day.sessions!==1?'s':''}
                                </div>
                              </div>
                            ))}
                          </div>
                          {profile.attendance.length > 60 && (
                            <div style={{ fontSize:11, color:'var(--textMuted)', textAlign:'center', padding:'8px 0' }}>
                              Showing 60 most recent of {profile.attendance.length} working days
                            </div>
                          )}
                          {/* Summary table */}
                          <div style={{ borderTop:'1px solid var(--border)', paddingTop:12 }}>
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                              <thead>
                                <tr>{['Date','Day','Sessions'].map(h=>(
                                  <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontSize:10, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid var(--border)' }}>{h}</th>
                                ))}</tr>
                              </thead>
                              <tbody>
                                {profile.attendance.map((day, i)=>(
                                  <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                                    <td style={{ padding:'7px 10px', color:'var(--textPrimary)', fontFamily:'monospace', fontSize:11 }}>{day.date}</td>
                                    <td style={{ padding:'7px 10px', color:'var(--textMuted)', fontSize:11 }}>{new Date(day.date+'T12:00:00').toLocaleDateString('en-KE',{weekday:'long'})}</td>
                                    <td style={{ padding:'7px 10px' }}>
                                      <span style={{ fontSize:11, fontWeight:700, color:'#22c55e' }}>{day.sessions} login{day.sessions!==1?'s':''}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </Card>
                  </div>
                )}

                {/* ── Activity Log tab ── */}
                {profileTab === 'activity' && (
                  <Card title={`System Activity (${profile.activity?.length || 0} records)`}>
                    {(!profile.activity || profile.activity.length === 0) ? (
                      <Empty text="No activity records found"/>
                    ) : (
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                          <thead>
                            <tr>{['Action','Description','Target','Date & Time'].map(h=>(
                              <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:10, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1, borderBottom:'2px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                            ))}</tr>
                          </thead>
                          <tbody>
                            {profile.activity.map((act, i)=>(
                              <tr key={act.id||i} style={{ borderBottom:'1px solid var(--border)' }}
                                onMouseEnter={e=>e.currentTarget.style.background='var(--bgHover)'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <td style={{ padding:'8px 10px', whiteSpace:'nowrap' }}>
                                  <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:4,
                                    background:(actionColors[act.action]||'#888')+'18',
                                    color:actionColors[act.action]||'#888',
                                    textTransform:'uppercase', letterSpacing:.5 }}>
                                    {act.action.replace(/_/g,' ')}
                                  </span>
                                </td>
                                <td style={{ padding:'8px 10px', color:'var(--textSecond)', maxWidth:240, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{act.description}</td>
                                <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11, whiteSpace:'nowrap' }}>
                                  {act.target_type ? `${act.target_type} #${act.target_id}` : '—'}
                                </td>
                                <td style={{ padding:'8px 10px', color:'var(--textFaint)', fontSize:10, whiteSpace:'nowrap' }}>
                                  {new Date(act.created_at).toLocaleString('en-KE', {dateStyle:'short', timeStyle:'short'})}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                )}

                {/* ── Security tab ── */}
                {profileTab === 'security' && (
                  <div style={{ display:'grid', gap:14 }}>
                    <Card title="🔒 Change Password">
                      <div style={{ maxWidth:360 }}>
                        <div style={{ padding:'10px 14px', background:'rgba(196,94,62,0.07)', border:'1px solid rgba(196,94,62,0.2)', borderRadius:6, marginBottom:14, fontSize:12, color:'var(--textSecond)' }}>
                          Setting a new password will immediately invalidate their current session and force a re-login.
                        </div>
                        <Field label={`New Password for ${selected.name}`}>
                          <div style={{ display:'flex', gap:8 }}>
                            <input
                              type={showPass ? 'text' : 'password'}
                              style={{ ...inputStyle, flex:1 }}
                              value={newPass}
                              onChange={e=>setNewPass(e.target.value)}
                              placeholder="Enter new password…"
                            />
                            <button onClick={()=>setShowPass(!showPass)} style={{ ...btnSm, background:'var(--bgInput)', color:'var(--textMuted)', flexShrink:0 }}>
                              {showPass ? '🙈' : '👁'}
                            </button>
                          </div>
                        </Field>
                        {newPass && (
                          <div style={{ marginBottom:10, fontSize:11, color: newPass.length >= 8 ? '#22c55e' : newPass.length >= 4 ? '#f59e0b' : '#e74c3c' }}>
                            Strength: {newPass.length >= 8 ? '✓ Strong' : newPass.length >= 4 ? '⚠ Fair' : '✗ Too short'}
                          </div>
                        )}
                        <button
                          onClick={savePassword}
                          disabled={savingPass || !newPass || newPass.length < 4}
                          style={{ ...btnPrimary, marginTop:0, minWidth:160, opacity: savingPass || !newPass || newPass.length < 4 ? .6 : 1 }}>
                          {savingPass ? '⏳ Saving…' : '🔑 Set New Password'}
                        </button>
                      </div>
                    </Card>

                    <Card title="Account Info">
                      {[
                        ['User ID',       `#${profile.profile.id}`],
                        ['Username',      '@' + profile.profile.username],
                        ['Account Created', new Date(profile.profile.created_at).toLocaleString('en-KE')],
                        ['Status',        profile.profile.is_active ? 'Active' : 'Inactive'],
                        ['Role',          profile.profile.role.replace(/_/g,' ')],
                        ['Total Logins',  profile.stats?.total_logins],
                        ['Last Action',   profile.activity?.[0] ? new Date(profile.activity[0].created_at).toLocaleString('en-KE') : 'Never'],
                      ].map(([k,v])=>(
                        <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                          <span style={{ color:'var(--textMuted)', fontWeight:600 }}>{k}</span>
                          <span style={{ color:'var(--textPrimary)', fontFamily: k==='User ID' ? 'monospace' : 'inherit' }}>{v}</span>
                        </div>
                      ))}
                    </Card>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Admin: Guest / Client History ───────────────────────────────────────────
function GuestHistory({ notify }) {
  const [guests, setGuests]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [filter, setFilter]       = useState('all');  // all | active | checked-out | deleted
  const [search, setSearch]       = useState('');
  const [deleteModal, setDeleteModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getGuestHistory();
      setGuests(r.data || []);
    } catch(e) {
      notify(e.response?.data?.error || 'Failed to load guest history', 'error');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doSoftDelete = async () => {
    if (!deleteModal) return;
    try {
      await api.softDeleteGuest(deleteModal.id);
      notify(`${deleteModal.name} removed. History preserved.`);
      setDeleteModal(null);
      setSelected(null);
      load();
    } catch(e) { notify(e.response?.data?.error || 'Failed', 'error'); }
  };

  const displayed = guests.filter(g => {
    const matchFilter =
      filter === 'all'        ? true :
      filter === 'active'     ? g.status === 'checked-in' && !g.is_deleted :
      filter === 'checked-out'? g.status === 'checked-out' && !g.is_deleted :
      filter === 'deleted'    ? g.is_deleted : true;
    const q = search.toLowerCase();
    const matchSearch = !search ||
      g.name.toLowerCase().includes(q) ||
      g.id_number.toLowerCase().includes(q) ||
      (g.phone||'').includes(q) ||
      (g.email||'').toLowerCase().includes(q) ||
      String(g.room||'').includes(q);
    return matchFilter && matchSearch;
  });

  const counts = {
    all:         guests.length,
    active:      guests.filter(g=>g.status==='checked-in'&&!g.is_deleted).length,
    'checked-out':guests.filter(g=>g.status==='checked-out'&&!g.is_deleted).length,
    deleted:     guests.filter(g=>g.is_deleted).length,
  };

  const filterLabels = { all:'All Guests', active:'Checked In', 'checked-out':'Checked Out', deleted:'Removed' };
  const filterColors = { all:'var(--primary)', active:'#22c55e', 'checked-out':'#3b82f6', deleted:'#94a3b8' };

  return (
    <div>
      <PageHeader icon="📁" title="Guest History" sub="Complete guest records — all past and present guests, including removed entries, are preserved forever" />

      <div style={{ padding:'10px 14px', background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:6, marginBottom:16, fontSize:12, color:'#22c55e' }}>
        ✅ Guest records are <strong>never permanently deleted</strong>. Removed guests are archived here with all their folio charges, payments, and stay history fully intact.
      </div>

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:10, marginBottom:16 }}>
        {Object.entries(counts).map(([k,v])=>(
          <div key={k} onClick={()=>setFilter(k)} style={{
            background: filter===k ? (filterColors[k]+'18') : 'var(--bgCard)',
            border: `1px solid ${filter===k ? filterColors[k]+'66' : 'var(--border)'}`,
            borderRadius:8, padding:'12px 14px', textAlign:'center', cursor:'pointer',
            transition:'all .14s',
          }}>
            <div style={{ fontSize:22, fontWeight:800, color: filter===k ? filterColors[k] : 'var(--textPrimary)' }}>{v}</div>
            <div style={{ fontSize:10, color:'var(--textMuted)', marginTop:2, textTransform:'uppercase', letterSpacing:.8 }}>{filterLabels[k]}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom:12 }}>
        <input style={inputStyle} placeholder="🔍 Search name, ID number, phone, email, room…"
          value={search} onChange={e=>setSearch(e.target.value)} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap:14, alignItems:'start' }}>

        {/* Guest table */}
        <Card title={`${filterLabels[filter]} (${displayed.length})`}>
          {loading ? <Loading/> : displayed.length === 0 ? <Empty text="No guests match your filters"/> : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr>{['Name','ID No.','Room','Check-in','Check-out','Nights','Payments','Status'].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:.8, borderBottom:'2px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {displayed.map((g,i)=>{
                    const isSel = selected?.id === g.id;
                    return (
                      <tr key={g.id} onClick={()=>setSelected(isSel?null:g)} style={{
                        borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background .1s',
                        background: isSel ? 'var(--bgHover)' : g.is_deleted ? 'rgba(148,163,184,0.04)' : 'transparent',
                        opacity: g.is_deleted ? 0.65 : 1,
                      }}>
                        <td style={{ padding:'9px 10px', fontWeight:600, color: g.is_deleted ? 'var(--textMuted)' : 'var(--textPrimary)', whiteSpace:'nowrap' }}>
                          {g.is_deleted && <span style={{ fontSize:9, color:'#94a3b8', marginRight:5 }}>🗂</span>}
                          {g.name}
                        </td>
                        <td style={{ padding:'9px 10px', color:'var(--textMuted)', fontFamily:'monospace', fontSize:11 }}>{g.id_number}</td>
                        <td style={{ padding:'9px 10px', color:'var(--accent)', fontWeight:600 }}>{g.room ? `Room ${g.room}` : '—'}</td>
                        <td style={{ padding:'9px 10px', color:'var(--textSecond)', fontSize:11, whiteSpace:'nowrap' }}>{g.check_in}</td>
                        <td style={{ padding:'9px 10px', color:'var(--textSecond)', fontSize:11, whiteSpace:'nowrap' }}>{g.check_out}</td>
                        <td style={{ padding:'9px 10px', color:'var(--textMuted)', textAlign:'center' }}>{g.nights}</td>
                        <td style={{ padding:'9px 10px', fontWeight:700, color:'#22c55e' }}>{fmtKES(g.payment_total)}</td>
                        <td style={{ padding:'9px 10px' }}>
                          {g.is_deleted ? (
                            <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:4, background:'rgba(148,163,184,0.12)', color:'#94a3b8' }}>🗂 Archived</span>
                          ) : g.status === 'checked-in' ? (
                            <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:4, background:'rgba(34,197,94,0.12)', color:'#22c55e' }}>● In-House</span>
                          ) : (
                            <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:4, background:'rgba(59,130,246,0.12)', color:'#3b82f6' }}>✓ Checked Out</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Guest detail pane */}
        {selected && (
          <Card title={`Guest #${selected.id} — Detail`}>
            <button onClick={()=>setSelected(null)} style={{ ...btnSm, marginBottom:12, background:'var(--bgInput)', color:'var(--textMuted)' }}>✕ Close</button>

            {selected.is_deleted && (
              <div style={{ padding:'8px 12px', background:'rgba(148,163,184,0.1)', border:'1px solid #94a3b844', borderRadius:6, marginBottom:12, fontSize:11, color:'#94a3b8' }}>
                🗂 Archived — account removed but all records preserved
              </div>
            )}

            {[
              ['Name',         selected.name],
              ['ID Number',    selected.id_number],
              ['Phone',        selected.phone],
              selected.email ? ['Email', selected.email] : null,
              ['Room',         selected.room ? `Room ${selected.room} (${selected.room_type})` : '—'],
              ['Adults',       selected.adults],
              ['Children',     selected.children],
              ['Check-in',     selected.check_in],
              ['Check-out',    selected.check_out],
              ['Nights',       selected.nights],
              ['Payment Mode', selected.payment_mode],
              ['Room Charge',  fmtKES(selected.room_charge)],
              ['Folio Total',  fmtKES(selected.folio_total)],
              ['Total Paid',   fmtKES(selected.payment_total)],
              ['Checked In',   selected.checked_in_at ? new Date(selected.checked_in_at).toLocaleString('en-KE') : '—'],
              ['Checked Out',  selected.checked_out_at ? new Date(selected.checked_out_at).toLocaleString('en-KE') : '—'],
              selected.deleted_at ? ['Archived On', new Date(selected.deleted_at).toLocaleString('en-KE')] : null,
            ].filter(Boolean).map(([k,v])=>(
              <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                <span style={{ color:'var(--textMuted)', fontWeight:600, flexShrink:0 }}>{k}</span>
                <span style={{ color:'var(--textPrimary)', textAlign:'right', fontWeight: ['Total Paid','Room Charge','Folio Total'].includes(k) ? 700 : 400, color: ['Total Paid','Room Charge'].includes(k) ? '#22c55e' : 'var(--textPrimary)' }}>{v}</span>
              </div>
            ))}

            {/* Remove button (only for checked-out non-deleted guests) */}
            {!selected.is_deleted && selected.status === 'checked-out' && (
              <button
                onClick={()=>setDeleteModal(selected)}
                style={{ ...btnSm, width:'100%', marginTop:14, background:'rgba(148,163,184,0.12)', color:'#94a3b8', border:'1px solid #94a3b844', fontSize:11 }}>
                🗂 Archive This Guest Record
              </button>
            )}
          </Card>
        )}
      </div>

      {/* Archive confirmation modal */}
      {deleteModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:12, padding:28, maxWidth:440, width:'100%' }}>
            <div style={{ fontSize:16, fontWeight:700, color:'var(--textPrimary)', marginBottom:8 }}>🗂 Archive Guest Record</div>
            <div style={{ fontSize:13, color:'var(--textSecond)', marginBottom:20, lineHeight:1.7 }}>
              Archive guest <strong>{deleteModal.name}</strong>?<br/>
              <span style={{ color:'#22c55e', fontSize:12 }}>✓ All stay history, folio charges, and payments are permanently preserved and viewable here.</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <button onClick={()=>setDeleteModal(null)} style={{ padding:'10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bgInput)', color:'var(--textMuted)', cursor:'pointer', fontWeight:600, fontFamily:'inherit' }}>Cancel</button>
              <button onClick={doSoftDelete} style={{ padding:'10px', borderRadius:6, border:'none', background:'#475569', color:'#fff', cursor:'pointer', fontWeight:700, fontFamily:'inherit' }}>Archive</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Admin: Developer Tools ───────────────────────────────────────────────────
function AdminDevTools({ notify }) {
  const [devToken, setDevToken] = useState('');
  const [authed, setAuthed]     = useState(false);
  const [tables, setTables]     = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [tableData, setTableData] = useState(null);
  const [sql, setSql]           = useState('');
  const [sqlResult, setSqlResult] = useState(null);
  const [devId, setDevId]       = useState('maina');
  const [loading, setLoading]   = useState(false);
  const BACKEND = process.env.REACT_APP_API_URL || window.location.origin;

  const devFetch = async (method, body) => {
    const r = await fetch(`${BACKEND}/api/dev/db/`, {
      method,
      headers: { 'Content-Type': 'application/json', 'X-Dev-Token': devToken },
      body: body ? JSON.stringify({ ...body, developer_id: devId }) : undefined,
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Request failed');
    return d;
  };

  const authenticate = async () => {
    if (!devToken || !devId) { notify('Enter dev token and your developer ID','error'); return; }
    setLoading(true);
    try {
      const d = await devFetch('GET');
      setTables(d.tables || []);
      setAuthed(true);
      notify('✅ Developer access granted');
    } catch(e) { notify(e.message, 'error'); }
    finally { setLoading(false); }
  };

  const loadTable = async (table) => {
    setLoading(true);
    setSelectedTable(table);
    try {
      const d = await devFetch('POST', { action:'list', table, limit:50 });
      setTableData(d);
    } catch(e) { notify(e.message, 'error'); }
    finally { setLoading(false); }
  };

  const runSql = async () => {
    if (!sql.trim()) return;
    if (!window.confirm(`Execute SQL?\n\n${sql}\n\nThis cannot be undone.`)) return;
    setLoading(true);
    try {
      const d = await devFetch('POST', { action:'sql', sql, confirm:true, description: sql.slice(0,100) });
      setSqlResult(d.result);
      notify('SQL executed');
    } catch(e) { notify(e.message, 'error'); setSqlResult({ error: e.message }); }
    finally { setLoading(false); }
  };

  const deleteRecord = async (table, id) => {
    if (!window.confirm(`DELETE from ${table} WHERE id = ${id}? This is permanent.`)) return;
    setLoading(true);
    try {
      await devFetch('POST', { action:'delete', table, record_id:id, confirm:true,
        description:`Delete ${table} id=${id}` });
      notify(`Deleted ${table} id=${id}`);
      loadTable(table);
    } catch(e) { notify(e.message, 'error'); }
    finally { setLoading(false); }
  };

  if (!authed) {
    return (
      <Card title="🛠️ Developer Database Tool">
        <div style={{ maxWidth:480 }}>
          <div style={{ padding:'12px 14px', background:'#1a0a0a', border:'1px solid #e74c3c44',
            borderRadius:6, marginBottom:20, fontSize:12, color:'#e74c3c' }}>
            ⚠️ <strong>STRICTLY DEVELOPER ONLY.</strong> All actions are logged to the DevDBAction audit table.
            This tool should never be accessible to regular staff. Protect the DEV_SECRET_TOKEN carefully.
          </div>
          <Field label="Developer ID (your username, for audit trail)">
            <input style={inputStyle} value={devId} onChange={e=>setDevId(e.target.value)} placeholder="developer_name" />
          </Field>
          <Field label="Developer Secret Token (set as DEV_SECRET_TOKEN in backend .env)">
            <input style={{ ...inputStyle }} type="password" value={devToken}
              onChange={e=>setDevToken(e.target.value)} placeholder="••••••••••••••••" />
          </Field>
          <button style={{ ...btnPrimary, marginTop:8, background:'linear-gradient(135deg,#6c3483,#1a237e)' }}
            onClick={authenticate} disabled={loading}>
            {loading ? '⏳ Authenticating…' : '🔓 Authenticate Developer'}
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:12, color:'#2ecc71' }}>✅ Developer authenticated as <strong>{devId}</strong></div>
        <button style={{ ...btnSm, background:'#5c1a1a' }} onClick={()=>{ setAuthed(false); setDevToken(''); }}>Disconnect</button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'200px 1fr', gap:16 }}>
        {/* Table list */}
        <div>
          <Card title="Database Tables">
            <div style={{ maxHeight:500, overflowY:'auto' }}>
              {tables.map(t => (
                <div key={t} onClick={()=>loadTable(t)}
                  style={{ padding:'6px 10px', cursor:'pointer', borderRadius:3,
                    fontSize:11, color: selectedTable===t?'#c8a96e':'var(--textSecond)',
                    background: selectedTable===t?'var(--bgHover)':'transparent',
                    fontFamily:'monospace' }}>
                  {t}
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Table data + SQL */}
        <div>
          <Card title="🔎 Raw SQL Executor">
            <div style={{ fontSize:10, color:'#e74c3c', marginBottom:8 }}>⚠️ All SQL is logged. Use with care.</div>
            <textarea style={{ ...inputStyle, fontFamily:'monospace', minHeight:70, fontSize:12 }}
              value={sql} onChange={e=>setSql(e.target.value)}
              placeholder="SELECT * FROM api_payment WHERE paid_at > '2025-01-01' LIMIT 10;" />
            <button style={{ ...btnPrimary, marginTop:8, background:'linear-gradient(135deg,#6c3483,#1a237e)', width:'auto' }}
              onClick={runSql} disabled={loading || !sql.trim()}>
              {loading ? '⏳' : '▶ Execute SQL'}
            </button>
            {sqlResult && (
              <div style={{ marginTop:12, background:'var(--bgInput)', padding:10, borderRadius:4,
                fontFamily:'monospace', fontSize:10, color:'#2ecc71', overflowX:'auto',
                maxHeight:200, overflowY:'auto' }}>
                <pre>{JSON.stringify(sqlResult, null, 2)}</pre>
              </div>
            )}
          </Card>

          {tableData && (
            <Card title={`${selectedTable} (${tableData.count} rows)`} style={{ marginTop:12 }}>
              {tableData.rows?.length === 0 ? <Empty text="No data" /> : (
                <div style={{ overflowX:'auto', maxHeight:400, overflowY:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10, fontFamily:'monospace' }}>
                    <thead><tr style={{ background:'var(--bgCard)', position:'sticky', top:0 }}>
                      {(tableData.columns||[]).map(c=>(
                        <th key={c} style={{ padding:'5px 8px', textAlign:'left', color:'var(--accent)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' }}>{c}</th>
                      ))}
                      <th style={{ padding:'5px 8px', color:'#e74c3c' }}>DEL</th>
                    </tr></thead>
                    <tbody>
                      {tableData.rows?.map((row,i) => (
                        <tr key={i} style={{ borderBottom:'1px solid #111' }}>
                          {(tableData.columns||[]).map(c=>(
                            <td key={c} style={{ padding:'4px 8px', color:'var(--textMuted)', maxWidth:200,
                              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {String(row[c] ?? '').slice(0,100)}
                            </td>
                          ))}
                          <td style={{ padding:'4px 6px' }}>
                            <button style={{ ...btnSm, background:'#5c1a1a', fontSize:9, padding:'2px 6px' }}
                              onClick={()=>deleteRecord(selectedTable, row.id)}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ─── ADMIN: CONFERENCE BOOKINGS ─────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
function AdminConferenceBookings({ notify }) {
  const [bookings, setBookings]   = useState([]);
  const [selected, setSelected]   = useState(null);
  const [tab, setTab]             = useState('all');
  const [loading, setLoading]     = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDate, setFilterDate]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [payModal, setPayModal]   = useState(null);
  const [payForm, setPayForm]     = useState({ payment_mode:'cash', transaction_code:'' });

  const STATUS_COLOR = { pending:'#e67e22', confirmed:'#3498db', completed:'#2ecc71', cancelled:'#e74c3c' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getConferenceBookings();
      setBookings(r.data.results || r.data);
    } catch(e) { notify('Failed to load conference bookings','error'); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = (list) => {
    let out = list;
    if (filterSearch) {
      const s = filterSearch.toLowerCase();
      out = out.filter(b =>
        b.organiser_name?.toLowerCase().includes(s) ||
        b.event_name?.toLowerCase().includes(s) ||
        b.confirmation_code?.toLowerCase().includes(s) ||
        b.organiser_phone?.includes(s) ||
        b.organisation?.toLowerCase().includes(s)
      );
    }
    if (filterStatus) out = out.filter(b => b.status === filterStatus);
    if (filterDate)   out = out.filter(b => b.event_date === filterDate);
    return out;
  };

  const filtered  = applyFilters(bookings);
  const pending   = filtered.filter(b => b.status === 'pending');
  const confirmed = filtered.filter(b => b.status === 'confirmed');
  const completed = filtered.filter(b => b.status === 'completed');
  const cancelled = filtered.filter(b => b.status === 'cancelled');

  const displayed =
    tab === 'pending'   ? pending   :
    tab === 'confirmed' ? confirmed :
    tab === 'completed' ? completed :
    tab === 'cancelled' ? cancelled : filtered;

  const doConfirm = async (b) => {
    try {
      await api.confirmConferenceBooking(b.id, {});
      notify(`Conference ${b.confirmation_code} confirmed`);
      setSelected(null); load();
    } catch(e) { notify(e.response?.data?.error || 'Error','error'); }
  };

  const doCancel = async (b) => {
    if (!window.confirm(`Cancel ${b.confirmation_code}?`)) return;
    await api.cancelConferenceBooking(b.id);
    notify('Booking cancelled'); setSelected(null); load();
  };

  const doPayment = async () => {
    try {
      await api.payConferenceBooking(payModal.id, payForm);
      notify('Payment recorded — booking completed');
      setPayModal(null); setPayForm({ payment_mode:'cash', transaction_code:'' });
      load();
    } catch(e) { notify(e.response?.data?.error || 'Error','error'); }
  };

  const sessionLabel = { half_day_am:'Half Day AM', half_day_pm:'Half Day PM', full_day:'Full Day' };

  const BookingRow = (b) => [
    <div>
      <div style={{fontWeight:700,color:'#c8a96e',fontSize:13,fontFamily:'monospace'}}>{b.confirmation_code}</div>
      <div style={{fontSize:10,color:'var(--textFaint)'}}>{b.event_date}</div>
    </div>,
    <div>
      <div style={{fontWeight:600,color:'var(--textPrimary)'}}>{b.organiser_name}</div>
      <div style={{fontSize:11,color:'var(--textFaint)'}}>{b.organisation || b.organiser_phone}</div>
    </div>,
    <div style={{maxWidth:180}}>
      <div style={{fontWeight:500}}>{b.event_name}</div>
      <div style={{fontSize:11,color:'var(--textFaint)'}}>{b.conference_room_name} · {sessionLabel[b.session_type]}</div>
    </div>,
    `${b.attendees} pax`,
    <span style={{color:'#c8a96e',fontWeight:700}}>{fmtKES(b.total)}</span>,
    <span style={{color:STATUS_COLOR[b.status]||'#aaa',fontWeight:700,fontSize:11,textTransform:'uppercase'}}>{b.status}</span>,
    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
      <button style={{...btnSm,background:'#1a2a3a'}} onClick={()=>setSelected(b)}>View</button>
      <button style={{...btnSm,background:'#2a1a0a'}} onClick={()=>printConferenceInvoice(b)}>🖨</button>
      {b.status==='pending' && <button style={{...btnSm,background:'#1a3a1a'}} onClick={()=>doConfirm(b)}>✓</button>}
      {b.status==='confirmed' && <button style={{...btnSm,background:'var(--primary)'}} onClick={()=>setPayModal(b)}>💳 Pay</button>}
    </div>
  ];

  const tabs = [
    {id:'all',       label:`All (${filtered.length})`,        icon:'📋'},
    {id:'pending',   label:`Pending (${pending.length})`,     icon:'⏳'},
    {id:'confirmed', label:`Confirmed (${confirmed.length})`, icon:'✅'},
    {id:'completed', label:`Completed (${completed.length})`, icon:'💰'},
    {id:'cancelled', label:`Cancelled (${cancelled.length})`, icon:'✕'},
  ];

  const totalRevenue = completed.reduce((s,b) => s + parseFloat(b.total||0), 0);

  return (
    <div>
      <PageHeader icon="🏛️" title="Conference Bookings" sub="All conference room bookings — manage, confirm, and process payments"/>
      <StatCards stats={[
        {label:'Pending',   value:pending.length,   color:'#e67e22'},
        {label:'Confirmed', value:confirmed.length, color:'#3498db'},
        {label:'Completed', value:completed.length, color:'#2ecc71'},
        {label:'Revenue',   value:fmtKES(totalRevenue), color:'#c8a96e'},
      ]}/>

      {/* Filters */}
      <Card style={{marginBottom:12}}>
        <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div style={{flex:'1 1 200px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>🔍 Search</div>
            <input style={{...inputStyle,margin:0}} placeholder="Organiser, event, ref code, org…"
              value={filterSearch} onChange={e=>setFilterSearch(e.target.value)}/>
          </div>
          <div style={{flex:'1 1 130px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Status</div>
            <select style={{...inputStyle,margin:0}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div style={{flex:'1 1 140px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Event Date</div>
            <input type="date" style={{...inputStyle,margin:0}} value={filterDate}
              onChange={e=>setFilterDate(e.target.value)}/>
          </div>
          <button style={{...btnSm,background:'#2a2a2a',marginBottom:1}}
            onClick={()=>{setFilterSearch('');setFilterStatus('');setFilterDate('');}}>Clear</button>
        </div>
      </Card>

      <TabBar tabs={tabs} active={tab} onChange={setTab}/>
      <Card title={`${tab.charAt(0).toUpperCase()+tab.slice(1)} Bookings (${displayed.length})`}>
        {loading ? <Loading/> : displayed.length===0 ? <Empty text="No bookings"/> :
          <Table
            headers={['Ref','Organiser','Event','Attendees','Total','Status','Actions']}
            rows={displayed.map(BookingRow)}
          />}
      </Card>

      {/* Detail modal */}
      {selected && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#111',border:'1px solid #222',borderRadius:10,padding:24,maxWidth:600,width:'100%',maxHeight:'92vh',overflowY:'auto',boxShadow:'0 24px 80px #000'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:18}}>
              <div>
                <div style={{fontSize:10,color:'#c8a96e',letterSpacing:3,textTransform:'uppercase'}}>Conference Booking</div>
                <div style={{fontSize:22,fontWeight:800,color:'#c8a96e',fontFamily:'monospace'}}>{selected.confirmation_code}</div>
                <span style={{display:'inline-block',marginTop:4,padding:'3px 12px',background:(STATUS_COLOR[selected.status]||'#aaa')+'22',border:`1px solid ${STATUS_COLOR[selected.status]||'#aaa'}55`,borderRadius:20,fontSize:11,fontWeight:700,color:STATUS_COLOR[selected.status]||'#aaa',textTransform:'uppercase'}}>{selected.status}</span>
              </div>
              <button onClick={()=>setSelected(null)} style={{background:'none',border:'none',color:'var(--textFaint)',fontSize:22,cursor:'pointer'}}>✕</button>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
              {[
                ['Organiser', selected.organiser_name],
                ['Organisation', selected.organisation||'—'],
                ['Phone', selected.organiser_phone],
                ['Email', selected.organiser_email],
                ['Event', selected.event_name],
                ['Date', selected.event_date],
                ['Venue', selected.conference_room_name||'—'],
                ['Session', sessionLabel[selected.session_type]||selected.session_type],
                ['Attendees', selected.attendees+' persons'],
                ['Payment Mode', (selected.payment_mode||'').toUpperCase()],
              ].map(([l,v])=>(
                <div key={l} style={{background:'#0a0a0a',border:'1px solid #1e1e1e',borderRadius:4,padding:'8px 10px'}}>
                  <div style={{fontSize:10,color:'var(--textFaint)',textTransform:'uppercase',letterSpacing:1}}>{l}</div>
                  <div style={{fontSize:13,color:'var(--textSecond)',fontWeight:500,marginTop:2}}>{v}</div>
                </div>
              ))}
            </div>

            {/* Charges */}
            <div style={{background:'#0a0a0a',border:'1px solid #c8a96e33',borderRadius:6,padding:14,marginBottom:14}}>
              <div style={{fontSize:10,color:'#c8a96e',textTransform:'uppercase',letterSpacing:2,marginBottom:8}}>Charges</div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4}}><span style={{color:'var(--textMuted)'}}>Venue Rate</span><span style={{color:'var(--textSecond)'}}>{fmtKES(selected.room_rate)}</span></div>
              {parseFloat(selected.extras_charge)>0 && <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4}}><span style={{color:'var(--textMuted)'}}>Extras</span><span style={{color:'var(--textSecond)'}}>{fmtKES(selected.extras_charge)}</span></div>}
              <div style={{display:'flex',justifyContent:'space-between',fontSize:16,fontWeight:800,marginTop:8,paddingTop:8,borderTop:'1px solid #222'}}><span style={{color:'var(--textPrimary)'}}>TOTAL</span><span style={{color:'#c8a96e'}}>{fmtKES(selected.total)}</span></div>
            </div>

            {/* Extras */}
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:14}}>
              {[
                ['Projector', selected.needs_projector],
                ['Microphone', selected.needs_microphone],
                ['Catering', selected.needs_catering],
                ['WiFi', selected.needs_wifi],
              ].map(([l,v])=>(
                <span key={l} style={{padding:'4px 10px',borderRadius:20,fontSize:11,fontWeight:600,
                  background:v?'#2ecc7120':'#ffffff08',color:v?'#2ecc71':'var(--textFaint)',
                  border:`1px solid ${v?'#2ecc7144':'#333'}`}}>
                  {v?'✓':'✗'} {l}
                </span>
              ))}
            </div>

            {selected.special_requests && (
              <div style={{background:'var(--bgInput)',border:'1px solid #2a2a2a',borderRadius:4,padding:10,marginBottom:14,fontSize:12,color:'var(--textMuted)'}}>
                <div style={{fontSize:10,color:'#c8a96e',textTransform:'uppercase',letterSpacing:1,marginBottom:4}}>Special Requests</div>
                {selected.special_requests}
              </div>
            )}

            <div style={{display:'flex',gap:8,flexWrap:'wrap',paddingTop:14,borderTop:'1px solid #1e1e1e'}}>
              <button style={{...btnSm,background:'#1a2a1a'}} onClick={()=>printConferenceInvoice(selected)}>🖨 Print Invoice</button>
              {selected.status==='pending' && <button style={btnPrimary} onClick={()=>doConfirm(selected)}>✓ Confirm</button>}
              {selected.status==='confirmed' && <button style={{...btnPrimary,background:'#27ae60'}} onClick={()=>{setPayModal(selected);setSelected(null);}}>💳 Process Payment</button>}
              {selected.status!=='cancelled'&&selected.status!=='completed' && <button style={{...btnSm,background:'#3a1010',color:'#e74c3c'}} onClick={()=>doCancel(selected)}>✕ Cancel</button>}
            </div>
          </div>
        </div>
      )}

      {/* Payment modal */}
      {payModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#111',border:'1px solid #222',borderRadius:10,padding:24,maxWidth:420,width:'100%'}}>
            <div style={{fontSize:16,fontWeight:800,color:'var(--textPrimary)',marginBottom:4}}>💳 Process Payment</div>
            <div style={{fontSize:12,color:'var(--textMuted)',marginBottom:18}}>{payModal.confirmation_code} — {payModal.event_name}</div>
            <div style={{fontSize:22,fontWeight:800,color:'#c8a96e',marginBottom:16}}>{fmtKES(payModal.total)}</div>
            <Field label="Payment Mode">
              <select style={inputStyle} value={payForm.payment_mode} onChange={e=>setPayForm(f=>({...f,payment_mode:e.target.value}))}>
                <option value="cash">Cash</option>
                <option value="mpesa">M-Pesa</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="invoice">Invoice</option>
              </select>
            </Field>
            <Field label="Transaction / Ref Code">
              <input style={inputStyle} value={payForm.transaction_code} onChange={e=>setPayForm(f=>({...f,transaction_code:e.target.value}))} placeholder="M-Pesa code, receipt no…"/>
            </Field>
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button style={{...btnPrimary,flex:2}} onClick={doPayment}>✓ Confirm Payment</button>
              <button style={{...btnSm,flex:1}} onClick={()=>setPayModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ─── ADMIN: TRANSPORT MANAGEMENT ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
function AdminTransport({ notify }) {
  const [subTab, setSubTab]       = useState('requests'); // 'requests' | 'zones'
  const [requests, setRequests]   = useState([]);
  const [summary, setSummary]     = useState(null);
  const [selected, setSelected]   = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]   = useState('');
  const [filterSearch, setFilterSearch]   = useState('');
  const [guests, setGuests]       = useState([]);

  // Zones state
  const [zones, setZones]         = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zoneModal, setZoneModal] = useState(false);
  const [editZone, setEditZone]   = useState(null);
  const blankZone = { name:'', description:'', fee_saloon:0, fee_suv:0, fee_minivan:0, fee_shuttle:0, is_active:true, sort_order:0 };
  const [zoneForm, setZoneForm]   = useState(blankZone);
  const [savingZone, setSavingZone] = useState(false);

  const initForm = { requester_name:'', requester_phone:'', pickup_location:'', dropoff_location:'Hotel', pickup_datetime:'', passengers:1, vehicle_type:'saloon', fee:0, expense_amount:0, payment_mode:'cash', transaction_code:'', notes:'', guest:'' };
  const [form, setForm] = useState(initForm);

  const VEHICLE_FEES = { saloon:2500, suv:4000, minivan:5000, shuttle:8000 };
  const STATUS_COLOR = { pending:'#e67e22', confirmed:'#3498db', completed:'#2ecc71', cancelled:'#e74c3c' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus)   params.append('status', filterStatus);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo)   params.append('date_to', filterDateTo);
      const qs = params.toString() ? '?'+params.toString() : '';
      const [r, s, g] = await Promise.all([
        api.getTransportRequests(qs),
        api.getTransportSummary(),
        api.getGuests ? api.getGuests() : Promise.resolve({data:[]}),
      ]);
      setRequests(r.data.results || r.data);
      setSummary(s.data);
      setGuests(g.data.results || g.data || []);
    } catch(e) { notify('Failed to load transport data','error'); }
    finally { setLoading(false); }
  }, [notify, filterStatus, filterDateFrom, filterDateTo]);

  const loadZones = useCallback(async () => {
    setZonesLoading(true);
    try {
      const r = await api.getTransportZones();
      setZones(r.data.results || r.data);
    } catch { notify('Failed to load zones','error'); }
    finally { setZonesLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadZones(); }, [loadZones]);

  const openZoneModal = (z=null) => {
    setEditZone(z);
    setZoneForm(z ? { name:z.name, description:z.description||'', fee_saloon:z.fee_saloon, fee_suv:z.fee_suv, fee_minivan:z.fee_minivan, fee_shuttle:z.fee_shuttle, is_active:z.is_active, sort_order:z.sort_order||0 } : blankZone);
    setZoneModal(true);
  };

  const saveZone = async () => {
    if (!zoneForm.name.trim()) { notify('Zone name is required','error'); return; }
    setSavingZone(true);
    try {
      const payload = { ...zoneForm, fee_saloon:parseFloat(zoneForm.fee_saloon)||0, fee_suv:parseFloat(zoneForm.fee_suv)||0, fee_minivan:parseFloat(zoneForm.fee_minivan)||0, fee_shuttle:parseFloat(zoneForm.fee_shuttle)||0, sort_order:parseInt(zoneForm.sort_order)||0 };
      if (editZone) await api.updateTransportZone(editZone.id, payload);
      else          await api.createTransportZone(payload);
      setZoneModal(false);
      await loadZones();
      notify(editZone ? 'Zone updated ✓' : 'Zone created ✓');
    } catch(e) { notify(e.response?.data?.name?.[0] || 'Error saving zone','error'); }
    setSavingZone(false);
  };

  const deleteZone = async (z) => {
    if (!window.confirm(`Delete zone "${z.name}"? This cannot be undone.`)) return;
    try { await api.deleteTransportZone(z.id); await loadZones(); notify('Zone deleted'); }
    catch { notify('Delete failed — zone may be in use','error'); }
  };

  // When admin picks a zone in the new-request form, auto-fill the fee
  const handleZonePick = (zoneName) => {
    const zone = zones.find(z => z.name === zoneName);
    if (zone) {
      const feeKey = { saloon:'fee_saloon', suv:'fee_suv', minivan:'fee_minivan', shuttle:'fee_shuttle' }[form.vehicle_type] || 'fee_saloon';
      setForm(f => ({ ...f, pickup_location: zoneName, fee: parseFloat(zone[feeKey]) || 0 }));
    } else {
      setForm(f => ({ ...f, pickup_location: zoneName }));
    }
  };

  const handleVehiclePick = (vt) => {
    const zone = zones.find(z => z.name === form.pickup_location);
    const feeKey = { saloon:'fee_saloon', suv:'fee_suv', minivan:'fee_minivan', shuttle:'fee_shuttle' }[vt] || 'fee_saloon';
    const fee = zone ? parseFloat(zone[feeKey]) || 0 : VEHICLE_FEES[vt] || 0;
    setForm(f => ({ ...f, vehicle_type: vt, fee }));
  };

  const filtered = requests.filter(r => {
    if (!filterSearch) return true;
    const s = filterSearch.toLowerCase();
    return r.requester_name?.toLowerCase().includes(s) || r.requester_phone?.includes(s) || r.pickup_location?.toLowerCase().includes(s);
  });

  const doCreate = async () => {
    if (!form.requester_name || !form.requester_phone || !form.pickup_datetime) {
      notify('Name, phone and pickup time are required','error'); return;
    }
    try {
      await api.createTransportRequest(form);
      notify('✅ Transport request created & billed');
      setShowForm(false); setForm(initForm); load();
    } catch(e) { notify(e.response?.data?.error || 'Error','error'); }
  };

  const doComplete = async (tr) => {
    try { await api.completeTransport(tr.id); notify('Transport marked completed'); load(); }
    catch(e) { notify('Error','error'); }
  };

  const doCancel = async (tr) => {
    if (!window.confirm('Cancel this transport request?')) return;
    await api.cancelTransport(tr.id); notify('Cancelled'); load();
  };

  const TransportRow = (tr) => [
    <div>
      <div style={{fontWeight:700,color:'#c8a96e',fontSize:12}}>TR-{tr.id}</div>
      <div style={{fontSize:10,color:'var(--textFaint)'}}>{new Date(tr.created_at).toLocaleDateString('en-KE')}</div>
    </div>,
    <div>
      <div style={{fontWeight:600}}>{tr.requester_name}</div>
      <div style={{fontSize:11,color:'var(--textFaint)'}}>{tr.requester_phone}</div>
    </div>,
    <div>
      <div style={{fontSize:12}}>{tr.pickup_location}</div>
      <div style={{fontSize:11,color:'var(--textFaint)'}}>→ {tr.dropoff_location}</div>
    </div>,
    new Date(tr.pickup_datetime).toLocaleString('en-KE',{dateStyle:'short',timeStyle:'short'}),
    <div>
      <div style={{color:'#2ecc71',fontWeight:700}}>{fmtKES(tr.fee)}</div>
      <div style={{fontSize:10,color:'#e74c3c'}}>-{fmtKES(tr.expense_amount)}</div>
    </div>,
    <span style={{color:STATUS_COLOR[tr.status]||'#aaa',fontWeight:700,fontSize:11,textTransform:'uppercase'}}>{tr.status}</span>,
    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
      <button style={{...btnSm,background:'#1a2a3a'}} onClick={()=>setSelected(tr)}>View</button>
      <button style={{...btnSm,background:'#1a2a1a'}} onClick={()=>printTransportReceipt(tr)}>🖨</button>
      {tr.status==='confirmed' && <button style={{...btnSm,background:'#27ae60'}} onClick={()=>doComplete(tr)}>✓</button>}
      {tr.status!=='completed'&&tr.status!=='cancelled' && <button style={{...btnSm,background:'#3a1010'}} onClick={()=>doCancel(tr)}>✕</button>}
    </div>
  ];

  return (
    <div>
      <PageHeader icon="🚐" title="Transport Management" sub="Pickup zones & pricing · Guest transfers · Billing and expense tracking"/>

      {/* ── Sub-tabs ── */}
      <div style={{display:'flex',gap:2,marginBottom:16,borderBottom:'1px solid var(--border)',paddingBottom:0}}>
        {[['requests','🚐 Requests'],['zones','📍 Pickup Zones & Pricing']].map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)} style={{padding:'8px 18px',background:'none',border:'none',borderBottom:`2px solid ${subTab===id?'#c8a96e':'transparent'}`,color:subTab===id?'#c8a96e':'var(--textMuted)',fontWeight:subTab===id?700:400,fontSize:12,cursor:'pointer',fontFamily:'inherit',marginBottom:-1}}>
            {label}{id==='zones'&&` (${zones.length})`}
          </button>
        ))}
      </div>

      {summary && (
        <StatCards stats={[
          {label:'Total Revenue',  value:fmtKES(summary.total_revenue), color:'#2ecc71'},
          {label:'Total Expenses', value:fmtKES(summary.total_expense), color:'#e74c3c'},
          {label:'Net Profit',     value:fmtKES(summary.total_profit),  color:'#c8a96e'},
          {label:'Pending',        value:summary.pending,               color:'#e67e22'},
        ]}/>
      )}

      {/* ══ REQUESTS TAB ══════════════════════════════════════════════════════ */}
      {subTab === 'requests' && (<>

      {/* Filters + New button */}
      <Card style={{marginBottom:12}}>
        <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div style={{flex:'1 1 160px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>🔍 Search</div>
            <input style={{...inputStyle,margin:0}} placeholder="Name, phone, location…"
              value={filterSearch} onChange={e=>setFilterSearch(e.target.value)}/>
          </div>
          <div style={{flex:'1 1 120px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Status</div>
            <select style={{...inputStyle,margin:0}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option><option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div style={{flex:'1 1 120px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>From</div>
            <input type="date" style={{...inputStyle,margin:0}} value={filterDateFrom} onChange={e=>setFilterDateFrom(e.target.value)}/>
          </div>
          <div style={{flex:'1 1 120px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>To</div>
            <input type="date" style={{...inputStyle,margin:0}} value={filterDateTo} onChange={e=>setFilterDateTo(e.target.value)}/>
          </div>
          <button style={{...btnSm,background:'#2a2a2a',marginBottom:1}}
            onClick={()=>{setFilterSearch('');setFilterStatus('');setFilterDateFrom('');setFilterDateTo('');}}>Clear</button>
          <button style={{...btnPrimary,marginBottom:1,marginTop:0}} onClick={()=>setShowForm(true)}>+ New Request</button>
        </div>
      </Card>

      <Card title={`Transport Requests (${filtered.length})`}>
        {loading ? <Loading/> : filtered.length===0 ? <Empty text="No transport requests"/> :
          <Table
            headers={['ID','Guest','Route','Pickup Time','Revenue/Expense','Status','Actions']}
            rows={filtered.map(TransportRow)}
          />}
      </Card>

      {/* New Request Form Modal */}
      {showForm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#111',border:'1px solid #222',borderRadius:10,padding:24,maxWidth:560,width:'100%',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{fontSize:16,fontWeight:800,color:'var(--textPrimary)',marginBottom:18}}>🚐 New Transport Request</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <Field label="Guest / Requester Name *">
                <input style={inputStyle} value={form.requester_name} onChange={e=>setForm(f=>({...f,requester_name:e.target.value}))} placeholder="Full name"/>
              </Field>
              <Field label="Phone *">
                <input style={inputStyle} value={form.requester_phone} onChange={e=>setForm(f=>({...f,requester_phone:e.target.value}))} placeholder="+254…"/>
              </Field>
              <Field label="Pickup Location">
                <select style={inputStyle} value={form.pickup_location} onChange={e=>handleZonePick(e.target.value)}>
                  <option value="">— Select pickup zone —</option>
                  {zones.filter(z=>z.is_active).map(z=><option key={z.id} value={z.name}>{z.name}</option>)}
                  <option value="__custom__">Other / Custom location…</option>
                </select>
                {form.pickup_location==='__custom__' && (
                  <input style={{...inputStyle,marginTop:6}} placeholder="Enter pickup address…" onChange={e=>setForm(f=>({...f,pickup_location:e.target.value}))}/>
                )}
              </Field>
              <Field label="Drop-off Location">
                <input style={inputStyle} value={form.dropoff_location} onChange={e=>setForm(f=>({...f,dropoff_location:e.target.value}))} placeholder="Hotel / Address…"/>
              </Field>
              <Field label="Pickup Date & Time *">
                <input type="datetime-local" style={inputStyle} value={form.pickup_datetime} onChange={e=>setForm(f=>({...f,pickup_datetime:e.target.value}))}/>
              </Field>
              <Field label="Passengers">
                <input type="number" style={inputStyle} min="1" max="50" value={form.passengers} onChange={e=>setForm(f=>({...f,passengers:parseInt(e.target.value)||1}))}/>
              </Field>
              <Field label="Vehicle Type">
                <select style={inputStyle} value={form.vehicle_type} onChange={e=>handleVehiclePick(e.target.value)}>
                  <option value="saloon">Saloon Car — KES 2,500</option>
                  <option value="suv">SUV / 4x4 — KES 4,000</option>
                  <option value="minivan">Mini Van — KES 5,000</option>
                  <option value="shuttle">Shuttle Bus — KES 8,000</option>
                </select>
              </Field>
              <Field label="Fee Charged to Guest (KES)">
                <input type="number" style={inputStyle} value={form.fee} onChange={e=>setForm(f=>({...f,fee:parseFloat(e.target.value)||0}))}/>
              </Field>
              <Field label="Expense / Fuel Cost (KES)">
                <input type="number" style={inputStyle} value={form.expense_amount} onChange={e=>setForm(f=>({...f,expense_amount:parseFloat(e.target.value)||0}))}/>
              </Field>
              <Field label="Payment Mode">
                <select style={inputStyle} value={form.payment_mode} onChange={e=>setForm(f=>({...f,payment_mode:e.target.value}))}>
                  <option value="cash">Cash</option><option value="mpesa">M-Pesa</option>
                  <option value="card">Card</option><option value="room_charge">Room Charge</option>
                </select>
              </Field>
              {form.payment_mode!=='cash' && (
                <Field label="Transaction / Ref Code">
                  <input style={inputStyle} value={form.transaction_code} onChange={e=>setForm(f=>({...f,transaction_code:e.target.value}))} placeholder="M-Pesa code…"/>
                </Field>
              )}
              <Field label="Link to Guest (optional)" style={{gridColumn:'1/-1'}}>
                <select style={inputStyle} value={form.guest} onChange={e=>setForm(f=>({...f,guest:e.target.value}))}>
                  <option value="">— No guest link —</option>
                  {guests.filter(g=>g.status==='checked-in').map(g=>(
                    <option key={g.id} value={g.id}>Room {g.room_number||'?'} — {g.name}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Notes">
              <textarea style={{...inputStyle,minHeight:60,resize:'vertical'}} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Flight details, special instructions…"/>
            </Field>
            <div style={{background:'#0a1a0a',border:'1px solid #2ecc7133',borderRadius:4,padding:'8px 12px',marginTop:4,fontSize:12}}>
              <span style={{color:'var(--textMuted)'}}>Net profit on this trip: </span>
              <strong style={{color:'#2ecc71'}}>{fmtKES(form.fee - form.expense_amount)}</strong>
              <span style={{color:'var(--textFaint)',fontSize:11,marginLeft:8}}>(Revenue auto-posted to Payments · Expense auto-posted to Expenses)</span>
            </div>
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button style={{...btnPrimary,flex:2}} onClick={doCreate}>✓ Create & Bill</button>
              <button style={{...btnSm,flex:1}} onClick={()=>{setShowForm(false);setForm(initForm);}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#111',border:'1px solid #222',borderRadius:10,padding:24,maxWidth:480,width:'100%'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div style={{fontSize:16,fontWeight:800,color:'#c8a96e'}}>TR-{selected.id}</div>
              <button onClick={()=>setSelected(null)} style={{background:'none',border:'none',color:'var(--textFaint)',fontSize:20,cursor:'pointer'}}>✕</button>
            </div>
            {[
              ['Guest', selected.requester_name],['Phone', selected.requester_phone],
              ['From', selected.pickup_location],['To', selected.dropoff_location],
              ['Pickup', new Date(selected.pickup_datetime).toLocaleString('en-KE')],
              ['Vehicle', selected.vehicle_type],['Passengers', selected.passengers],
              ['Fee', fmtKES(selected.fee)],['Expense', fmtKES(selected.expense_amount)],
              ['Profit', fmtKES(selected.profit||0)],['Status', selected.status],
              ['Payment', (selected.payment_mode||'').toUpperCase()],
              ['Billed By', selected.billed_by_name||'—'],
            ].map(([l,v])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid #111',fontSize:12}}>
                <span style={{color:'var(--textFaint)'}}>{l}</span>
                <span style={{color:'var(--textSecond)',fontWeight:500}}>{v}</span>
              </div>
            ))}
            {selected.notes && <div style={{marginTop:10,fontSize:12,color:'var(--textMuted)',background:'var(--bgInput)',padding:8,borderRadius:4}}>{selected.notes}</div>}
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button style={{...btnSm,background:'#1a2a1a'}} onClick={()=>printTransportReceipt(selected)}>🖨 Print Receipt</button>
              {selected.status==='confirmed' && <button style={{...btnPrimary,background:'#27ae60'}} onClick={()=>{doComplete(selected);setSelected(null);}}>✓ Mark Completed</button>}
            </div>
          </div>
        </div>
      )}

      </>)} {/* end subTab === 'requests' */}

      {/* ══ ZONES TAB ════════════════════════════════════════════════════════ */}
      {subTab === 'zones' && (<>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <div style={{fontSize:12,color:'var(--textMuted)'}}>
            Configure pickup &amp; drop-off zones with per-vehicle pricing. These appear in the guest booking form and admin transport requests.
          </div>
          <button style={{...btnPrimary,marginTop:0,whiteSpace:'nowrap'}} onClick={()=>openZoneModal()}>+ Add Zone</button>
        </div>

        {zonesLoading ? <Loading/> : zones.length === 0 ? (
          <div style={{textAlign:'center',padding:'60px 20px',color:'var(--textFaint)'}}>
            <div style={{fontSize:36,marginBottom:12}}>📍</div>
            <div style={{fontSize:14,marginBottom:6,color:'var(--textMuted)'}}>No pickup zones configured</div>
            <div style={{fontSize:12}}>Add zones like "JKIA Airport", "Nakuru Town", "Naivasha" with per-vehicle prices</div>
          </div>
        ) : (
          <div style={{display:'grid',gap:10}}>
            {zones.map(z => (
              <div key={z.id} style={{background:'var(--bgCard)',border:'1px solid var(--border)',borderRadius:8,padding:'14px 16px',borderLeft:`3px solid ${z.is_active?'#c8a96e':'#555'}`}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                      <span style={{fontSize:14,fontWeight:700,color:'var(--textPrimary)'}}>📍 {z.name}</span>
                      {!z.is_active && <span style={{fontSize:9,background:'#333',color:'#888',borderRadius:3,padding:'1px 6px',textTransform:'uppercase'}}>Inactive</span>}
                    </div>
                    {z.description && <div style={{fontSize:11,color:'var(--textFaint)',marginBottom:8}}>{z.description}</div>}
                    <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                      {[['🚗 Saloon',z.fee_saloon],['🚙 SUV',z.fee_suv],['🚐 Minivan',z.fee_minivan],['🚌 Shuttle',z.fee_shuttle]].map(([label,fee])=>(
                        parseFloat(fee)>0 ? (
                          <span key={label} style={{fontSize:11,background:'rgba(200,169,110,0.1)',border:'1px solid rgba(200,169,110,0.25)',borderRadius:4,padding:'3px 8px',color:'#c8a96e'}}>
                            {label}: <strong>{fmtKES(fee)}</strong>
                          </span>
                        ) : (
                          <span key={label} style={{fontSize:11,background:'var(--bgInput)',border:'1px solid var(--border)',borderRadius:4,padding:'3px 8px',color:'var(--textFaint)'}}>
                            {label}: N/A
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:6,flexShrink:0}}>
                    <button style={{...btnSm}} onClick={()=>openZoneModal(z)}>✎ Edit</button>
                    <button style={{...btnSm,background:'#1a0808',border:'1px solid #3a1515',color:'#e74c3c'}} onClick={()=>deleteZone(z)}>✕</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>)} {/* end subTab === 'zones' */}

      {/* Zone Create/Edit Modal */}
      {zoneModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#0d0d0d',border:'1px solid #2a2a2a',borderRadius:10,padding:24,maxWidth:520,width:'100%',maxHeight:'92vh',overflowY:'auto'}}>
            <div style={{fontSize:15,fontWeight:800,color:'#c8a96e',marginBottom:4}}>
              {editZone ? '✎ Edit Transport Zone' : '📍 New Transport Zone'}
            </div>
            <div style={{fontSize:11,color:'var(--textFaint)',marginBottom:18}}>
              Set per-vehicle pricing for this pickup location. Leave a vehicle at 0 to mark it as unavailable for this zone.
            </div>

            <Field label="Zone Name *">
              <input style={inputStyle} value={zoneForm.name} onChange={e=>setZoneForm(f=>({...f,name:e.target.value}))} placeholder="e.g. JKIA Airport, Nakuru Town Centre, Naivasha"/>
            </Field>
            <Field label="Description (optional — shown to guest)">
              <input style={inputStyle} value={zoneForm.description} onChange={e=>setZoneForm(f=>({...f,description:e.target.value}))} placeholder="e.g. Jomo Kenyatta International Airport, any terminal"/>
            </Field>

            <div style={{fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginBottom:8,marginTop:4}}>Per-Vehicle Fees (KES) — set to 0 if not available</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
              <Field label="🚗 Saloon / Sedan">
                <input type="number" style={inputStyle} min="0" value={zoneForm.fee_saloon} onChange={e=>setZoneForm(f=>({...f,fee_saloon:e.target.value}))} placeholder="e.g. 2500"/>
              </Field>
              <Field label="🚙 SUV / 4x4">
                <input type="number" style={inputStyle} min="0" value={zoneForm.fee_suv} onChange={e=>setZoneForm(f=>({...f,fee_suv:e.target.value}))} placeholder="e.g. 4000"/>
              </Field>
              <Field label="🚐 Mini Van">
                <input type="number" style={inputStyle} min="0" value={zoneForm.fee_minivan} onChange={e=>setZoneForm(f=>({...f,fee_minivan:e.target.value}))} placeholder="e.g. 5000"/>
              </Field>
              <Field label="🚌 Shuttle Bus">
                <input type="number" style={inputStyle} min="0" value={zoneForm.fee_shuttle} onChange={e=>setZoneForm(f=>({...f,fee_shuttle:e.target.value}))} placeholder="e.g. 8000"/>
              </Field>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
              <Field label="Display Order (lower = first)">
                <input type="number" style={inputStyle} min="0" value={zoneForm.sort_order} onChange={e=>setZoneForm(f=>({...f,sort_order:e.target.value}))}/>
              </Field>
              <Field label="Status">
                <select style={inputStyle} value={zoneForm.is_active} onChange={e=>setZoneForm(f=>({...f,is_active:e.target.value==='true'}))}>
                  <option value="true">✅ Active — visible to guests</option>
                  <option value="false">⏸ Inactive — hidden</option>
                </select>
              </Field>
            </div>

            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>setZoneModal(false)} style={{flex:1,padding:9,background:'transparent',border:'1px solid #2a2a2a',borderRadius:4,color:'#888',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
              <button onClick={saveZone} disabled={savingZone} style={{flex:2,padding:9,background:'#c8a96e',border:'none',borderRadius:4,color:'#000',fontWeight:700,cursor:'pointer',fontFamily:'inherit',opacity:savingZone?0.6:1}}>
                {savingZone ? 'Saving…' : editZone ? '💾 Update Zone' : '✓ Create Zone'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ─── ADMIN: ATTENDANCE ──────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
function AdminAttendance({ notify }) {
  const STATUSES    = ['present','absent','half_day','late','leave'];
  const STATUS_COLOR = { present:'#2ecc71', absent:'#e74c3c', half_day:'#e67e22', leave:'#3498db', late:'#f39c12' };
  const STATUS_LABEL = { present:'Present', absent:'Absent', half_day:'Half Day', leave:'On Leave', late:'Late' };

  const [tab, setTab]             = useState('bulk');
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);

  // Bulk tab state
  const [bulkDate, setBulkDate]   = useState(new Date().toISOString().slice(0,10));
  const [bulkDept, setBulkDept]   = useState('');
  const [staff, setStaff]         = useState([]);
  const [bulkMap, setBulkMap]     = useState({}); // { empId: { status, check_in_time, check_out_time, notes, existingId } }
  const [dirtySet, setDirtySet]   = useState(new Set());
  const [detailEmp, setDetailEmp] = useState(null);

  // History tab state
  const [filterDate, setFilterDate]     = useState(new Date().toISOString().slice(0,10));
  const [filterEmp, setFilterEmp]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDept2, setFilterDept2]   = useState('');
  const [records, setRecords]           = useState([]);
  const [editModal, setEditModal]       = useState(null);
  const [editForm, setEditForm]         = useState({});

  // Monthly tab state
  const [month, setMonth]               = useState(new Date().toISOString().slice(0,7));
  const [monthlyReport, setMonthlyReport] = useState(null);

  // ── Load staff + existing records for bulk grid ──────────────────────────────
  const loadBulk = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date: bulkDate });
      if (bulkDept) params.append('department', bulkDept);
      const [sRes, aRes] = await Promise.all([
        api.getUsers(),
        api.getAttendance('?' + params.toString()),
      ]);
      const allStaff = (sRes.data.results || sRes.data || [])
        .filter(s => s.is_active && !s.is_terminated)
        .filter(s => !bulkDept || s.role === bulkDept)
        .sort((a,b) => a.name.localeCompare(b.name));
      setStaff(allStaff);

      const existing = aRes.data.results || aRes.data || [];
      const map = {};
      allStaff.forEach(s => {
        const rec = existing.find(r => r.employee === s.id || r.employee_id === s.id);
        map[s.id] = {
          status:         rec?.status         || '',
          check_in_time:  rec?.check_in_time  || '',
          check_out_time: rec?.check_out_time || '',
          notes:          rec?.notes          || '',
          existingId:     rec?.id             || null,
        };
      });
      setBulkMap(map);
      setDirtySet(new Set());
    } catch(e) { notify('Failed to load staff / attendance','error'); }
    finally { setLoading(false); }
  }, [bulkDate, bulkDept, notify]);

  useEffect(() => { if (tab === 'bulk') loadBulk(); }, [tab, loadBulk]);

  // ── Bulk helpers ──────────────────────────────────────────────────────────────
  const setStatus = (empId, status) => {
    setBulkMap(m => ({ ...m, [empId]: { ...m[empId], status } }));
    setDirtySet(d => new Set([...d, empId]));
  };

  const updateField = (empId, field, val) => {
    setBulkMap(m => ({ ...m, [empId]: { ...m[empId], [field]: val } }));
    setDirtySet(d => new Set([...d, empId]));
  };

  const markAll = (status) => {
    setBulkMap(m => {
      const next = { ...m };
      staff.forEach(s => { next[s.id] = { ...next[s.id], status }; });
      return next;
    });
    setDirtySet(new Set(staff.map(s => s.id)));
  };

  const saveBulk = async () => {
    const toSave = staff.filter(s => dirtySet.has(s.id) && bulkMap[s.id]?.status);
    if (!toSave.length) { notify('No changes to save','info'); return; }
    setSaving(true);
    let ok = 0, fail = 0;
    for (const s of toSave) {
      const entry = bulkMap[s.id];
      try {
        await api.createAttendance({
          employee: s.id,
          date: bulkDate,
          status: entry.status,
          check_in_time:  entry.check_in_time  || null,
          check_out_time: entry.check_out_time || null,
          notes: entry.notes || '',
        });
        ok++;
      } catch(e) { fail++; }
    }
    setSaving(false);
    setDirtySet(new Set());
    if (fail === 0) notify(`✅ Saved ${ok} attendance record${ok>1?'s':''}`, 'success');
    else notify(`Saved ${ok}, failed ${fail}`, 'warning');
    loadBulk();
  };

  // ── Summary counts ────────────────────────────────────────────────────────────
  const summary = STATUSES.reduce((acc, s) => {
    acc[s] = staff.filter(emp => bulkMap[emp.id]?.status === s).length;
    return acc;
  }, {});
  const unmarked = staff.filter(emp => !bulkMap[emp.id]?.status).length;
  const dirty    = dirtySet.size;

  // ── History tab ───────────────────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('date', filterDate);
      if (filterEmp)     params.append('employee', filterEmp);
      if (filterStatus)  params.append('status', filterStatus);
      if (filterDept2)   params.append('department', filterDept2);
      const [r, s] = await Promise.all([
        api.getAttendance('?' + params.toString()),
        api.getUsers(),
      ]);
      setRecords(r.data.results || r.data);
      setStaff(s.data.results || s.data || []);
    } catch(e) { notify('Failed to load','error'); }
    finally { setLoading(false); }
  }, [filterDate, filterEmp, filterStatus, filterDept2, notify]);

  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  const saveEdit = async () => {
    try {
      if (editModal.id) await api.updateAttendance(editModal.id, editForm);
      else              await api.createAttendance(editForm);
      notify('Saved'); setEditModal(null); loadHistory();
    } catch(e) { notify(e.response?.data?.error||'Error','error'); }
  };

  // ── Monthly tab ───────────────────────────────────────────────────────────────
  const loadMonthly = useCallback(async () => {
    try {
      const r = await api.getMonthlyAttendance(month);
      setMonthlyReport(r.data);
    } catch(e) { notify('Failed to load monthly report','error'); }
  }, [month, notify]);

  useEffect(() => { if (tab === 'monthly') loadMonthly(); }, [tab, loadMonthly]);

  const DEPT_OPTIONS = ['reception','waiter','chef','kitchen','housekeeping','gate','cashier','hr','manager','accountant','admin','storekeeper','marketing','swimming_attendant'];

  const tabs = [
    { id:'bulk',    label:'Bulk Entry',     icon:'📋' },
    { id:'history', label:'Records',        icon:'📅' },
    { id:'monthly', label:'Monthly Report', icon:'📊' },
  ];

  return (
    <div>
      <PageHeader icon="🕐" title="Employee Attendance" sub="Bulk-mark daily attendance, review records, and view monthly summaries"/>
      <TabBar tabs={tabs} active={tab} onChange={setTab}/>

      {/* ── BULK ENTRY TAB ── */}
      {tab==='bulk' && (
        <>
          {/* Controls */}
          <Card style={{marginBottom:12}}>
            <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
              <div style={{flex:'1 1 140px'}}>
                <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Date</div>
                <input type="date" style={{...inputStyle,margin:0}} value={bulkDate} onChange={e=>setBulkDate(e.target.value)}/>
              </div>
              <div style={{flex:'1 1 150px'}}>
                <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Department</div>
                <select style={{...inputStyle,margin:0}} value={bulkDept} onChange={e=>setBulkDept(e.target.value)}>
                  <option value="">All Departments</option>
                  {DEPT_OPTIONS.map(d=><option key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1).replace('_',' ')}</option>)}
                </select>
              </div>
              {/* Quick-mark all buttons */}
              <div style={{flex:'1 1 auto',display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                <div style={{fontSize:10,color:'var(--textFaint)',width:'100%',marginBottom:2}}>Mark all as:</div>
                {STATUSES.map(s=>(
                  <button key={s} onClick={()=>markAll(s)}
                    style={{padding:'4px 10px',borderRadius:12,border:`1px solid ${STATUS_COLOR[s]}55`,background:`${STATUS_COLOR[s]}18`,color:STATUS_COLOR[s],fontSize:11,fontWeight:700,cursor:'pointer'}}>
                    {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {/* Live summary bar */}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12,alignItems:'center'}}>
            {STATUSES.map(s => summary[s] > 0 && (
              <div key={s} style={{padding:'5px 14px',background:`${STATUS_COLOR[s]}18`,border:`1px solid ${STATUS_COLOR[s]}44`,borderRadius:20,fontSize:12,color:STATUS_COLOR[s],fontWeight:700}}>
                {summary[s]} {STATUS_LABEL[s]}
              </div>
            ))}
            {unmarked > 0 && (
              <div style={{padding:'5px 14px',background:'#ffffff0a',border:'1px solid #444',borderRadius:20,fontSize:12,color:'#888'}}>
                {unmarked} unmarked
              </div>
            )}
            <div style={{flex:1}}/>
            {dirty > 0 && (
              <button onClick={saveBulk} disabled={saving}
                style={{...btnPrimary,minWidth:140,opacity:saving?0.7:1}}>
                {saving ? 'Saving…' : `💾 Save ${dirty} change${dirty>1?'s':''}`}
              </button>
            )}
          </div>

          {/* Bulk grid */}
          {loading ? <Loading/> : staff.length === 0 ? <Empty text="No staff found"/> : (
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {staff.map(emp => {
                const entry  = bulkMap[emp.id] || {};
                const isDirty = dirtySet.has(emp.id);
                return (
                  <div key={emp.id} style={{
                    background: isDirty ? 'var(--bgCard)' : '#0d0d0d',
                    border: `1px solid ${isDirty ? '#c8a96e55' : '#1a1a1a'}`,
                    borderLeft: `3px solid ${entry.status ? STATUS_COLOR[entry.status] : '#333'}`,
                    borderRadius:8, padding:'10px 14px',
                    display:'flex', flexWrap:'wrap', gap:10, alignItems:'center',
                  }}>
                    {/* Employee info */}
                    <div style={{flex:'0 0 180px',minWidth:140}}>
                      <div style={{fontWeight:700,color:'var(--textPrimary)',fontSize:13}}>{emp.name}</div>
                      <div style={{fontSize:11,color:'var(--textMuted)',textTransform:'capitalize'}}>{emp.role?.replace('_',' ')}</div>
                    </div>

                    {/* Status pills */}
                    <div style={{display:'flex',gap:5,flexWrap:'wrap',flex:'1 1 300px'}}>
                      {STATUSES.map(s=>(
                        <button key={s} onClick={()=>setStatus(emp.id, s)}
                          style={{
                            padding:'4px 12px',borderRadius:14,fontSize:11,fontWeight:700,cursor:'pointer',
                            border:`1px solid ${entry.status===s ? STATUS_COLOR[s] : STATUS_COLOR[s]+'44'}`,
                            background: entry.status===s ? STATUS_COLOR[s]+'33' : 'transparent',
                            color: entry.status===s ? STATUS_COLOR[s] : '#555',
                            transition:'all 0.15s',
                          }}>
                          {STATUS_LABEL[s]}
                        </button>
                      ))}
                    </div>

                    {/* Time fields — only shown when present/late/half_day */}
                    {['present','late','half_day'].includes(entry.status) && (
                      <div style={{display:'flex',gap:8,flex:'0 0 auto',alignItems:'center'}}>
                        <div>
                          <div style={{fontSize:9,color:'var(--textFaint)',marginBottom:2}}>IN</div>
                          <input type="time" style={{...inputStyle,margin:0,width:100,padding:'4px 8px',fontSize:12}}
                            value={entry.check_in_time} onChange={e=>updateField(emp.id,'check_in_time',e.target.value)}/>
                        </div>
                        <div>
                          <div style={{fontSize:9,color:'var(--textFaint)',marginBottom:2}}>OUT</div>
                          <input type="time" style={{...inputStyle,margin:0,width:100,padding:'4px 8px',fontSize:12}}
                            value={entry.check_out_time} onChange={e=>updateField(emp.id,'check_out_time',e.target.value)}/>
                        </div>
                      </div>
                    )}

                    {/* Notes icon + expand */}
                    <button onClick={()=>setDetailEmp(detailEmp===emp.id?null:emp.id)}
                      style={{background:'transparent',border:'1px solid #333',borderRadius:6,padding:'4px 8px',color:'#666',fontSize:11,cursor:'pointer'}}>
                      📝 {entry.notes ? 'Note ✓' : 'Note'}
                    </button>

                    {detailEmp===emp.id && (
                      <div style={{width:'100%',marginTop:4}}>
                        <textarea style={{...inputStyle,minHeight:48,fontSize:12,resize:'vertical',margin:0,width:'100%'}}
                          placeholder="Optional note for this record…"
                          value={entry.notes}
                          onChange={e=>updateField(emp.id,'notes',e.target.value)}/>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Sticky save bar at bottom */}
          {dirty > 0 && (
            <div style={{position:'sticky',bottom:16,marginTop:16,display:'flex',justifyContent:'flex-end'}}>
              <button onClick={saveBulk} disabled={saving}
                style={{...btnPrimary,padding:'12px 32px',fontSize:14,boxShadow:'0 4px 20px rgba(200,169,110,0.3)',opacity:saving?0.7:1}}>
                {saving ? 'Saving…' : `💾 Save ${dirty} change${dirty>1?'s':''}`}
              </button>
            </div>
          )}
        </>
      )}

      {/* ── RECORDS / HISTORY TAB ── */}
      {tab==='history' && (
        <>
          <Card style={{marginBottom:12}}>
            <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
              <div style={{flex:'1 1 130px'}}>
                <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Date</div>
                <input type="date" style={{...inputStyle,margin:0}} value={filterDate} onChange={e=>setFilterDate(e.target.value)}/>
              </div>
              <div style={{flex:'1 1 150px'}}>
                <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Employee</div>
                <select style={{...inputStyle,margin:0}} value={filterEmp} onChange={e=>setFilterEmp(e.target.value)}>
                  <option value="">All Employees</option>
                  {staff.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div style={{flex:'1 1 120px'}}>
                <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Status</div>
                <select style={{...inputStyle,margin:0}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
                  <option value="">All</option>
                  {STATUSES.map(s=><option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </div>
              <div style={{flex:'1 1 130px'}}>
                <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Department</div>
                <select style={{...inputStyle,margin:0}} value={filterDept2} onChange={e=>setFilterDept2(e.target.value)}>
                  <option value="">All Depts</option>
                  {DEPT_OPTIONS.map(d=><option key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1).replace('_',' ')}</option>)}
                </select>
              </div>
              <button style={{...btnSm,background:'#2a2a2a'}} onClick={()=>{setFilterEmp('');setFilterStatus('');setFilterDept2('');}}>Clear</button>
              <button style={{...btnPrimary,marginTop:0}} onClick={()=>{ setEditModal({id:null}); setEditForm({employee:'',date:filterDate,status:'present',check_in_time:'',check_out_time:'',notes:''}); }}>+ Add</button>
            </div>
          </Card>

          {records.length > 0 && (
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
              {STATUSES.map(s=>{
                const cnt=records.filter(r=>r.status===s).length;
                return cnt>0 ? <div key={s} style={{padding:'5px 14px',background:`${STATUS_COLOR[s]}18`,border:`1px solid ${STATUS_COLOR[s]}44`,borderRadius:20,fontSize:12,color:STATUS_COLOR[s],fontWeight:700}}>{cnt} {STATUS_LABEL[s]}</div> : null;
              })}
            </div>
          )}

          <Card title={`Records — ${filterDate} (${records.length})`}>
            {loading ? <Loading/> : records.length===0 ? <Empty text="No records for this date"/> :
              <Table
                headers={['Employee','Clock In','Clock Out','Hours','Status','Source','Edit']}
                rows={records.map(r=>[
                  <div>
                    <div style={{fontWeight:600}}>{r.employee_name}</div>
                    <div style={{fontSize:11,color:'var(--textFaint)',textTransform:'capitalize'}}>{r.employee_role}</div>
                  </div>,
                  r.check_in_time  || '—',
                  r.check_out_time || '—',
                  r.hours_worked   ? `${r.hours_worked}h` : '—',
                  <span style={{color:STATUS_COLOR[r.status]||'#aaa',fontWeight:700,fontSize:11,textTransform:'uppercase'}}>{r.status?.replace('_',' ')}</span>,
                  r.recorded_by_self
                    ? <span style={{color:'#3498db',fontSize:11}}>Self</span>
                    : <span style={{color:'#e67e22',fontSize:11}}>Admin</span>,
                  <button style={{...btnSm,background:'#1a2a3a'}} onClick={()=>{ setEditModal(r); setEditForm({employee:r.employee,date:r.date,status:r.status||'present',check_in_time:r.check_in_time||'',check_out_time:r.check_out_time||'',notes:r.notes||''}); }}>Edit</button>
                ])}
              />}
          </Card>
        </>
      )}

      {/* ── MONTHLY TAB ── */}
      {tab==='monthly' && (
        <>
          <Card style={{marginBottom:12}}>
            <div style={{display:'flex',gap:10,alignItems:'flex-end',flexWrap:'wrap'}}>
              <div>
                <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Month</div>
                <input type="month" style={{...inputStyle,margin:0,width:160}} value={month} onChange={e=>setMonth(e.target.value)}/>
              </div>
              <button style={{...btnPrimary,marginTop:0}} onClick={loadMonthly}>Load Report</button>
            </div>
          </Card>

          {monthlyReport && (
            <Card title={`Monthly Report — ${monthlyReport.month}`}>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'var(--bgHover)'}}>
                      {['Employee','Role','Present','Absent','Half Day','Late','On Leave','Total'].map(h=>(
                        <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'var(--accent)',fontWeight:600,fontSize:11,whiteSpace:'nowrap',borderBottom:'1px solid var(--border)'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyReport.report?.map((row,i)=>{
                      const attendance_pct = row.total_records > 0
                        ? Math.round((row.present + row.half_day * 0.5) / row.total_records * 100)
                        : null;
                      return (
                        <tr key={row.employee_id} style={{borderBottom:'1px solid #0d0d0d',background:i%2===0?'transparent':'var(--bgHover)'}}>
                          <td style={{padding:'7px 10px',fontWeight:600}}>
                            {row.employee_name}
                            {attendance_pct !== null && (
                              <div style={{fontSize:10,color:attendance_pct>=80?'#2ecc71':attendance_pct>=60?'#f39c12':'#e74c3c',marginTop:1}}>
                                {attendance_pct}% attendance
                              </div>
                            )}
                          </td>
                          <td style={{padding:'7px 10px',color:'var(--textMuted)',textTransform:'capitalize',fontSize:11}}>{row.role?.replace('_',' ')}</td>
                          <td style={{padding:'7px 10px',color:'#2ecc71',fontWeight:700}}>{row.present}</td>
                          <td style={{padding:'7px 10px',color:'#e74c3c',fontWeight:700}}>{row.absent}</td>
                          <td style={{padding:'7px 10px',color:'#e67e22'}}>{row.half_day}</td>
                          <td style={{padding:'7px 10px',color:'#f39c12'}}>{row.late}</td>
                          <td style={{padding:'7px 10px',color:'#3498db'}}>{row.on_leave}</td>
                          <td style={{padding:'7px 10px',color:'var(--textSecond)',fontWeight:600}}>{row.total_records}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ── EDIT / CREATE MODAL ── */}
      {editModal !== null && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#111',border:'1px solid #222',borderRadius:10,padding:24,maxWidth:440,width:'100%'}}>
            <div style={{fontSize:15,fontWeight:800,color:'var(--textPrimary)',marginBottom:18}}>
              {editModal.id ? 'Edit Record' : 'Add Attendance Record'}
            </div>
            {!editModal.id && (
              <Field label="Employee *">
                <select style={inputStyle} value={editForm.employee} onChange={e=>setEditForm(f=>({...f,employee:e.target.value}))}>
                  <option value="">— Select Employee —</option>
                  {staff.map(s=><option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                </select>
              </Field>
            )}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <Field label="Date">
                <input type="date" style={inputStyle} value={editForm.date} onChange={e=>setEditForm(f=>({...f,date:e.target.value}))}/>
              </Field>
              <Field label="Status">
                <select style={inputStyle} value={editForm.status} onChange={e=>setEditForm(f=>({...f,status:e.target.value}))}>
                  {STATUSES.map(s=><option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </Field>
              <Field label="Clock In">
                <input type="time" style={inputStyle} value={editForm.check_in_time} onChange={e=>setEditForm(f=>({...f,check_in_time:e.target.value}))}/>
              </Field>
              <Field label="Clock Out">
                <input type="time" style={inputStyle} value={editForm.check_out_time} onChange={e=>setEditForm(f=>({...f,check_out_time:e.target.value}))}/>
              </Field>
            </div>
            <Field label="Notes">
              <textarea style={{...inputStyle,minHeight:50,resize:'vertical'}} value={editForm.notes} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} placeholder="Optional notes…"/>
            </Field>
            <div style={{display:'flex',gap:8,marginTop:14}}>
              <button style={{...btnPrimary,flex:2}} onClick={saveEdit}>✓ Save</button>
              <button style={{...btnSm,flex:1}} onClick={()=>setEditModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Bulk Import ──────────────────────────────────────────────────────────────
function BulkImport({ notify }) {
  const [entity, setEntity]     = useState('rooms');
  const [rawText, setRawText]   = useState('');
  const [rows, setRows]         = useState([]);   // parsed preview rows
  const [errors, setErrors]     = useState([]);   // per-row errors after validation
  const [results, setResults]   = useState([]);   // after import: {row, ok, msg}
  const [importing, setImporting] = useState(false);
  const [imported, setImported]   = useState(false);

  // ── Templates ────────────────────────────────────────────────────────────
  const TEMPLATES = {
    rooms: {
      label: 'Rooms',
      icon: '🏨',
      color: '#3b82f6',
      headers: ['number','type','price','floor'],
      hint: 'type: Standard | Deluxe | Suite | Presidential',
      example: [
        ['101','Standard','5500','1'],
        ['102','Deluxe','8000','1'],
        ['201','Suite','15000','2'],
      ],
    },
    food: {
      label: 'Food Menu',
      icon: '🍽️',
      color: '#e67e22',
      headers: ['name','category','price','cost_price','description'],
      hint: 'cost_price and description are optional',
      example: [
        ['Grilled Tilapia','Grills','850','320','Served with ugali and kachumbari'],
        ['Caesar Salad','Salads','650','',''],
        ['Chicken Burger','Burgers','700','280',''],
      ],
    },
    bar: {
      label: 'Bar Menu',
      icon: '🍺',
      color: '#8B2635',
      headers: ['name','category','price','cost_price','description'],
      hint: 'cost_price and description are optional',
      example: [
        ['Tusker Lager','Beers','250','120','500ml bottle'],
        ['Mojito','Cocktails','650','',''],
        ['Mango Juice','Juices','300','','Fresh squeezed'],
      ],
    },
    stock: {
      label: 'Store Items',
      icon: '📦',
      color: '#8b5cf6',
      headers: ['name','category','unit','current_stock','reorder_level','unit_cost','supplier'],
      hint: 'category: food|beverage|cleaning|linen|stationery|maintenance|amenities|chemicals|uniforms|other  unit: kg|g|l|ml|piece|crate|bunch|tin|packet|dozen|box|roll|bottle|bag|carton',
      example: [
        ['Maize Flour 2kg','food','bag','50','10','180','Unga Mills'],
        ['Toilet Rolls','cleaning','carton','30','5','850','Bidco'],
        ['Cooking Oil 20L','food','carton','20','4','3200','Bidco'],
      ],
    },
  };

  const tpl = TEMPLATES[entity];

  // ── Download CSV template ─────────────────────────────────────────────────
  const downloadTemplate = () => {
    const t = TEMPLATES[entity];
    const lines = [t.headers.join(','), ...t.example.map(r => r.map(v => `"${v}"`).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${entity}_bulk_template.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  // ── Parse pasted / typed CSV text ────────────────────────────────────────
  const parseCSV = (text) => {
    return text.trim().split('\n').map(line => {
      // handle quoted fields with commas
      const cols = [];
      let cur = '', inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQ = !inQ; }
        else if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
        else { cur += ch; }
      }
      cols.push(cur.trim());
      return cols;
    }).filter(r => r.some(c => c !== ''));
  };

  const handleTextChange = (text) => {
    setRawText(text);
    setErrors([]);
    setResults([]);
    setImported(false);
    if (!text.trim()) { setRows([]); return; }
    const parsed = parseCSV(text);
    // strip header row if user pasted with headers
    const firstRow = parsed[0];
    const isHeader = firstRow.some(c => tpl.headers.includes(c.toLowerCase().replace(/ /g,'_')));
    setRows(isHeader ? parsed.slice(1) : parsed);
  };

  // ── Handle CSV file upload ────────────────────────────────────────────────
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => handleTextChange(ev.target.result);
    reader.readAsText(file);
    e.target.value = '';
  };

  // ── Validate rows client-side ─────────────────────────────────────────────
  const validate = () => {
    const errs = rows.map((row, i) => {
      const rowErrs = [];
      if (entity === 'rooms') {
        const [number, type, price, floor] = row;
        if (!number) rowErrs.push('Room number required');
        if (!['Standard','Deluxe','Suite','Presidential'].includes(type)) rowErrs.push(`Invalid type "${type}"`);
        if (!price || isNaN(Number(price)) || Number(price) <= 0) rowErrs.push('Price must be a positive number');
        if (floor && isNaN(Number(floor))) rowErrs.push('Floor must be a number');
      } else if (entity === 'food' || entity === 'bar') {
        const [name, category, price, cost_price] = row;
        if (!name) rowErrs.push('Name required');
        if (!category) rowErrs.push('Category required');
        if (!price || isNaN(Number(price)) || Number(price) <= 0) rowErrs.push('Price must be a positive number');
        if (cost_price && isNaN(Number(cost_price))) rowErrs.push('Cost price must be a number');
      } else if (entity === 'stock') {
        const VALID_CATS = ['food','beverage','cleaning','linen','stationery','maintenance','amenities','chemicals','uniforms','other'];
        const VALID_UNITS = ['kg','g','l','ml','piece','crate','bunch','tin','packet','dozen','box','roll','bottle','bag','carton'];
        const [name, category, unit, current_stock, reorder_level, unit_cost] = row;
        if (!name) rowErrs.push('Name required');
        if (!VALID_CATS.includes(category)) rowErrs.push(`Invalid category "${category}"`);
        if (!VALID_UNITS.includes(unit)) rowErrs.push(`Invalid unit "${unit}"`);
        if (current_stock && isNaN(Number(current_stock))) rowErrs.push('Current stock must be a number');
        if (reorder_level && isNaN(Number(reorder_level))) rowErrs.push('Reorder level must be a number');
        if (unit_cost && isNaN(Number(unit_cost))) rowErrs.push('Unit cost must be a number');
      }
      return rowErrs;
    });
    setErrors(errs);
    return errs.every(e => e.length === 0);
  };

  // ── Build payload per entity ──────────────────────────────────────────────
  const buildPayload = (row) => {
    if (entity === 'rooms') {
      const [number, type, price, floor] = row;
      return { number, type: type||'Standard', price: Number(price), floor: Number(floor||1) };
    }
    if (entity === 'food' || entity === 'bar') {
      const [name, category, price, cost_price, description] = row;
      return {
        name, category, price: Number(price),
        menu_type: entity,
        cost_price: cost_price ? Number(cost_price) : null,
        description: description || '',
        is_active: true,
      };
    }
    if (entity === 'stock') {
      const [name, category, unit, current_stock, reorder_level, unit_cost, supplier] = row;
      return {
        name, category, unit,
        current_stock: Number(current_stock||0),
        reorder_level: Number(reorder_level||0),
        unit_cost: Number(unit_cost||0),
        supplier: supplier||'',
        is_active: true,
      };
    }
  };

  // ── Call API ──────────────────────────────────────────────────────────────
  const callAPI = (payload) => {
    if (entity === 'rooms')       return api.createRoom(payload);
    if (entity === 'food')        return api.createMenuItem(payload);
    if (entity === 'bar')         return api.createMenuItem(payload);
    if (entity === 'stock')       return api.createStockItem(payload);
  };

  // ── Run import row by row ─────────────────────────────────────────────────
  const doImport = async () => {
    if (!validate()) { notify('Fix validation errors before importing', 'error'); return; }
    if (!rows.length) { notify('No rows to import', 'error'); return; }
    setImporting(true);
    setResults([]);
    const res = [];
    for (let i = 0; i < rows.length; i++) {
      const payload = buildPayload(rows[i]);
      try {
        await callAPI(payload);
        res.push({ row: i + 1, ok: true, msg: `Row ${i+1} imported ✓` });
      } catch(e) {
        const data = e.response?.data;
        let msg = '';
        if (data && typeof data === 'object') {
          msg = Object.entries(data).map(([k,v]) => `${k}: ${Array.isArray(v)?v[0]:v}`).join('; ');
        } else { msg = data || e.message || 'Unknown error'; }
        res.push({ row: i + 1, ok: false, msg: `Row ${i+1} failed: ${msg}` });
      }
    }
    setResults(res);
    setImporting(false);
    setImported(true);
    const okCount = res.filter(r => r.ok).length;
    const failCount = res.filter(r => !r.ok).length;
    if (failCount === 0) notify(`✅ All ${okCount} ${tpl.label} imported successfully!`);
    else if (okCount > 0) notify(`⚠️ ${okCount} imported, ${failCount} failed — see results below`, 'error');
    else notify(`❌ All ${failCount} rows failed — check errors below`, 'error');
  };

  const reset = () => {
    setRawText(''); setRows([]); setErrors([]); setResults([]); setImported(false);
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const cardSt = { background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:8, padding:20, marginBottom:16 };
  const colHd  = { fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', fontWeight:700, padding:'6px 8px', background:'#111', borderBottom:'1px solid #222' };
  const cellSt = { padding:'6px 8px', fontSize:12, color:'var(--textSecond)', borderBottom:'1px solid #1a1a1a', whiteSpace:'nowrap', overflow:'hidden', maxWidth:160, textOverflow:'ellipsis' };

  const hasErrors = errors.some(e => e.length > 0);

  return (
    <div>
      <PageHeader icon="📥" title="Bulk Import" sub="Add Rooms, Menu Items, or Store Items in bulk via CSV" />

      {/* Entity selector */}
      <div style={cardSt}>
        <div style={{ fontSize:13, fontWeight:700, color:'var(--textPrimary)', marginBottom:12 }}>1. Choose what to import</div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {Object.entries(TEMPLATES).map(([key, t]) => (
            <button key={key} onClick={() => { setEntity(key); reset(); }} style={{
              padding:'8px 18px', borderRadius:6, fontFamily:'inherit', cursor:'pointer',
              fontSize:13, fontWeight: entity===key ? 700 : 400,
              border:`1px solid ${entity===key ? t.color : 'var(--border)'}`,
              background: entity===key ? t.color+'18' : 'transparent',
              color: entity===key ? t.color : 'var(--textMuted)',
              transition:'all .12s',
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Template download + format hint */}
      <div style={cardSt}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--textPrimary)', marginBottom:6 }}>2. Download template & fill it in</div>
            <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom:10 }}>
              Required columns: <span style={{ color:tpl.color, fontWeight:600 }}>{tpl.headers.join(', ')}</span>
            </div>
            <div style={{ fontSize:11, color:'var(--textFaint)', background:'#111', padding:'6px 10px', borderRadius:4, display:'inline-block' }}>
              💡 {tpl.hint}
            </div>
          </div>
          <button onClick={downloadTemplate} style={{ ...btnSm, background:tpl.color, color:'#fff', fontWeight:700, padding:'9px 18px', whiteSpace:'nowrap' }}>
            ⬇ Download CSV Template
          </button>
        </div>
      </div>

      {/* CSV input */}
      <div style={cardSt}>
        <div style={{ fontSize:13, fontWeight:700, color:'var(--textPrimary)', marginBottom:12 }}>3. Paste CSV data or upload your filled template</div>
        <div style={{ display:'flex', gap:10, marginBottom:10, flexWrap:'wrap', alignItems:'center' }}>
          <label style={{ ...btnSm, cursor:'pointer', background:'var(--bgInput)', color:'var(--textMuted)', border:'1px dashed var(--borderMid)' }}>
            📂 Upload CSV file
            <input type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display:'none' }}/>
          </label>
          <span style={{ fontSize:12, color:'var(--textFaint)' }}>or paste directly below</span>
          {rows.length > 0 && (
            <span style={{ fontSize:12, color:tpl.color, fontWeight:600 }}>{rows.length} row{rows.length!==1?'s':''} detected</span>
          )}
        </div>
        <textarea
          style={{ ...inputStyle, width:'100%', minHeight:140, resize:'vertical', fontFamily:'monospace', fontSize:12 }}
          placeholder={`Paste CSV here (without header row), e.g.:\n${tpl.example.map(r=>r.join(',')).join('\n')}`}
          value={rawText}
          onChange={e => handleTextChange(e.target.value)}
        />
      </div>

      {/* Preview table */}
      {rows.length > 0 && !imported && (
        <div style={cardSt}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--textPrimary)', marginBottom:12 }}>
            4. Preview — {rows.length} row{rows.length!==1?'s':''} {hasErrors ? <span style={{ color:'#e74c3c' }}>({errors.filter(e=>e.length).length} with errors)</span> : <span style={{ color:'#2ecc71' }}>✓ All valid</span>}
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr>
                  <th style={{ ...colHd, width:40 }}>#</th>
                  {tpl.headers.map(h => <th key={h} style={colHd}>{h}</th>)}
                  <th style={colHd}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const rowErr = errors[i] || [];
                  const hasErr = rowErr.length > 0;
                  return (
                    <tr key={i} style={{ background: hasErr ? '#2a0a0a' : 'transparent' }}>
                      <td style={{ ...cellSt, color:'var(--textFaint)', textAlign:'center' }}>{i+1}</td>
                      {tpl.headers.map((h, j) => (
                        <td key={j} style={{ ...cellSt, color: row[j] ? 'var(--textSecond)' : '#555' }}>
                          {row[j] || <span style={{ color:'#444', fontStyle:'italic' }}>empty</span>}
                        </td>
                      ))}
                      <td style={{ ...cellSt, color: hasErr ? '#e74c3c' : '#2ecc71', minWidth:160 }}>
                        {hasErr ? rowErr.join('; ') : '✓ OK'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display:'flex', gap:10, marginTop:16 }}>
            <button
              onClick={doImport}
              disabled={importing || hasErrors}
              style={{ ...btnPrimary, flex:1, background: hasErrors ? '#333' : tpl.color, color: hasErrors ? '#666' : '#fff', fontWeight:700, fontSize:14 }}
            >
              {importing ? `⏳ Importing… (${results.length}/${rows.length})` : `🚀 Import ${rows.length} ${tpl.label}`}
            </button>
            <button onClick={reset} style={{ ...btnSm, padding:'11px 20px' }}>✕ Clear</button>
          </div>
          {hasErrors && (
            <div style={{ marginTop:10, fontSize:12, color:'#e74c3c', background:'#1a0808', padding:'8px 12px', borderRadius:4 }}>
              ⚠️ Fix the highlighted rows before importing. Common fixes: check spelling of category/type/unit, ensure numbers have no currency symbols, remove extra commas.
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {imported && results.length > 0 && (
        <div style={cardSt}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--textPrimary)', marginBottom:12 }}>
            Import Results — {results.filter(r=>r.ok).length}/{results.length} succeeded
          </div>
          <div style={{ maxHeight:320, overflowY:'auto' }}>
            {results.map((r, i) => (
              <div key={i} style={{
                display:'flex', alignItems:'center', gap:10,
                padding:'7px 10px', borderRadius:4, marginBottom:4,
                background: r.ok ? '#0a1f0a' : '#1a0808',
                border:`1px solid ${r.ok ? '#1e3a1e' : '#3a0808'}`,
              }}>
                <span style={{ fontSize:14 }}>{r.ok ? '✅' : '❌'}</span>
                <span style={{ fontSize:12, color: r.ok ? '#2ecc71' : '#e74c3c' }}>{r.msg}</span>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:10, marginTop:14 }}>
            <button onClick={reset} style={{ ...btnSm, background:'var(--bgInput)', color:'var(--textMuted)', padding:'9px 20px' }}>
              ↩ Import More
            </button>
            {results.some(r => !r.ok) && (
              <div style={{ fontSize:12, color:'var(--textMuted)', alignSelf:'center' }}>
                Failed rows were not saved — fix and re-import them.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Kitchen Prep Report ───────────────────────────────────────────────────────
function KitchenPrepReport({ notify }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [period, setPeriod]     = useState('today');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [station, setStation]   = useState('');
  const [sortBy, setSortBy]     = useState('prep_secs');
  const [sortDir, setSortDir]   = useState('desc');

  const fmtDur = (secs) => {
    if (!secs && secs !== 0) return '—';
    if (secs < 60) return `${secs}s`;
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  };

  const urgBg = (secs) => {
    if (secs > 1200) return { color:'#e74c3c', bg:'#e74c3c18' }; // >20m
    if (secs > 600)  return { color:'#f39c12', bg:'#f39c1218' }; // >10m
    return { color:'#2ecc71', bg:'#2ecc7118' };
  };

  const load = async () => {
    setLoading(true);
    try {
      let params = `?period=${period}`;
      if (period === 'custom' && dateFrom && dateTo) params += `&date_from=${dateFrom}&date_to=${dateTo}`;
      if (station) params += `&station=${encodeURIComponent(station)}`;
      const r = await api.getKitchenPrepReport(params);
      setData(r.data);
    } catch (e) {
      notify(e.response?.data?.error || 'Failed to load report', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const sorted = data ? [...data.orders].sort((a, b) => {
    const v = sortDir === 'asc' ? 1 : -1;
    return (a[sortBy] - b[sortBy]) * v;
  }) : [];

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const thStyle = (col) => ({
    padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
    color: sortBy === col ? 'var(--primary)' : 'var(--textMuted)',
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    borderBottom: '2px solid var(--border)',
    background: 'var(--bgHover)',
  });
  const tdStyle = { padding: '9px 12px', fontSize: 13, borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };

  const s = data?.summary || {};

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>
          ⏱️ Kitchen Prep Time Report
        </div>
        <div style={{ fontSize: 13, color: 'var(--textMuted)' }}>
          Track how long each order takes from placement to ready — identify bottlenecks by station.
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: 'var(--bgCard)', border: '1px solid var(--border)',
        borderRadius: 10, padding: '16px 20px', marginBottom: 20,
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end',
      }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--textMuted)', marginBottom: 5, fontWeight: 600 }}>PERIOD</div>
          <select value={period} onChange={e => setPeriod(e.target.value)} style={{
            background: 'var(--bgInput)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '7px 12px', color: 'var(--text)',
            fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
          }}>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {period === 'custom' && <>
          <div>
            <div style={{ fontSize: 11, color: 'var(--textMuted)', marginBottom: 5, fontWeight: 600 }}>FROM</div>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{
              background: 'var(--bgInput)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '7px 12px', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
            }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--textMuted)', marginBottom: 5, fontWeight: 600 }}>TO</div>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{
              background: 'var(--bgInput)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '7px 12px', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
            }} />
          </div>
        </>}

        <div>
          <div style={{ fontSize: 11, color: 'var(--textMuted)', marginBottom: 5, fontWeight: 600 }}>STATION</div>
          <select value={station} onChange={e => setStation(e.target.value)} style={{
            background: 'var(--bgInput)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '7px 12px', color: 'var(--text)',
            fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', minWidth: 160,
          }}>
            <option value="">All Stations</option>
            {(data?.all_stations || []).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <button
          onClick={load}
          disabled={loading}
          style={{
            padding: '8px 22px', background: 'var(--primary)', color: '#fff',
            border: 'none', borderRadius: 6, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: 13, fontFamily: 'inherit', opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? '…' : '🔍 Run Report'}
        </button>
      </div>

      {data && <>
        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 20 }}>
          {[
            { label: 'Total Orders',     value: s.total_orders,                         icon: '📋', color: '#3498db' },
            { label: 'Orders Timed',     value: s.timed_orders,                         icon: '⏱️', color: '#9b59b6' },
            { label: 'Avg Wait Time',    value: fmtDur(s.avg_wait_secs),                icon: '⏳', color: '#e67e22' },
            { label: 'Avg Prep Time',    value: fmtDur(s.avg_prep_secs),                icon: '🍳', color: '#f39c12' },
            { label: 'Avg Total Time',   value: fmtDur(s.avg_total_secs),               icon: '🏁', color: '#2ecc71' },
          ].map(card => (
            <div key={card.label} style={{
              background: 'var(--bgCard)', border: `1px solid ${card.color}33`,
              borderTop: `3px solid ${card.color}`, borderRadius: 10, padding: '14px 16px',
            }}>
              <div style={{ fontSize: 11, color: 'var(--textMuted)', fontWeight: 600, marginBottom: 6 }}>
                {card.icon} {card.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: card.color }}>
                {card.value ?? '—'}
              </div>
            </div>
          ))}
        </div>

        {/* Station breakdown */}
        {data.station_stats?.length > 0 && (
          <div style={{
            background: 'var(--bgCard)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '16px 20px', marginBottom: 20,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
              📍 By Kitchen Station
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {data.station_stats.map(st => {
                const ug = urgBg(st.avg_prep_secs);
                return (
                  <div key={st.station} style={{
                    background: 'var(--bgHover)', border: `1px solid ${ug.color}33`,
                    borderLeft: `4px solid ${ug.color}`, borderRadius: 8, padding: '12px 14px',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                      {st.station}
                    </div>
                    <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--textMuted)' }}>
                      <span>{st.order_count} orders</span>
                      <span style={{ color: ug.color, fontWeight: 700 }}>
                        avg {fmtDur(st.avg_prep_secs)}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--textMuted)', marginTop: 2 }}>
                      wait: {fmtDur(st.avg_wait_secs)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Order-by-order table */}
        <div style={{
          background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              Order Detail — {sorted.length} orders
            </div>
            <div style={{ fontSize: 11, color: 'var(--textMuted)' }}>
              Click column headers to sort
            </div>
          </div>

          {sorted.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--textMuted)', fontSize: 13 }}>
              No timed orders for this period. Orders are timed once kitchen starts preparing them.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {[
                      ['#', 'id'],
                      ['Table', 'table'],
                      ['Waiter', 'waiter'],
                      ['Station', 'kitchen_station'],
                      ['Items', 'items_count'],
                      ['Wait Time', 'wait_secs'],
                      ['Prep Time', 'prep_secs'],
                      ['Total Time', 'total_secs'],
                      ['Date', 'created_at'],
                    ].map(([label, col]) => (
                      <th key={col} style={thStyle(col)} onClick={() => toggleSort(col)}>
                        {label} {sortBy === col ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((o, i) => {
                    const ug = urgBg(o.prep_secs);
                    return (
                      <tr key={o.id} style={{ background: i % 2 === 0 ? 'transparent' : 'var(--bgHover)' }}>
                        <td style={tdStyle}><span style={{ color: 'var(--textMuted)', fontSize: 11 }}>#{o.id}</span></td>
                        <td style={tdStyle}><strong>{o.table}</strong></td>
                        <td style={tdStyle}>{o.waiter}</td>
                        <td style={tdStyle}>
                          <span style={{
                            fontSize: 11, color: '#e67e22', background: '#e67e2215',
                            border: '1px solid #e67e2233', borderRadius: 4, padding: '2px 7px',
                          }}>{o.kitchen_station}</span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{o.items_count}</td>
                        <td style={tdStyle}>
                          <span style={{ color: 'var(--textMuted)', fontSize: 12 }}>{fmtDur(o.wait_secs)}</span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            fontWeight: 700, color: ug.color,
                            background: ug.bg, borderRadius: 5,
                            padding: '2px 8px', fontSize: 12,
                          }}>{fmtDur(o.prep_secs)}</span>
                        </td>
                        <td style={tdStyle}>
                          <strong style={{ color: ug.color }}>{fmtDur(o.total_secs)}</strong>
                        </td>
                        <td style={{ ...tdStyle, fontSize: 11, color: 'var(--textMuted)' }}>
                          {new Date(o.created_at).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>}

      {!data && !loading && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--textMuted)' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>⏱️</div>
          <div style={{ fontSize: 14 }}>Click "Run Report" to load prep time data</div>
        </div>
      )}
    </div>
  );
}